// src/App.js - Kubi AI Recruitment Platform - FULLY FUNCTIONAL
// Real data from ATS/Database - Demo mode ONLY for Stripe billing
import React, { useState, useEffect, createContext, useContext, useRef, useCallback, useMemo } from 'react';
import { ThemeContext, useTheme as useThemeFromContext } from './ThemeContext';
import { Routes, Route, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Search, ChevronDown, ChevronRight, ChevronLeft, X, MapPin, Briefcase, Mail, Phone, Linkedin,
  Moon, Sun, Sparkles, Loader2, CreditCard, Check, Plus, RefreshCw, LogOut,
  Users, Settings, BarChart3, Building2, UserCheck, TrendingUp, FolderOpen, Menu,
  AlertCircle, CheckCircle, Trash2, Download, DollarSign, ExternalLink, ListPlus,
  ChevronUp, Database, Zap, Target, Filter, FileText, Eye, EyeOff, Clock, Star,
  Edit3, Globe, Package, Crown, Receipt, Calendar, PieChart, ArrowUpDown,
  SlidersHorizontal, AlertTriangle, XCircle, Send, Shield, UserPlus, Info,
  ArrowUp, ArrowDown, Trophy, RotateCcw, Brain, Wand2, Lightbulb, Hash, Activity,
  Paperclip, User, FileSearch, Link2, ArrowRight, TreePine, BookOpen, ShieldCheck, Smartphone
} from 'lucide-react';
import { LoginPage, RegisterPage, VerifyEmailPage, ForgotPasswordPage, ResetPasswordPage, AcceptInvitePage } from './Auth';
import EnhancedSearch from './EnhancedSearch';
import AIAssistantDashboard from './AIAssistantDashboard';
import AISettingsPanel from './AISettingsPanel';
import TrackerPage from './TrackerPage';
import FeeRecoveryPage from './FeeRecoveryPage';
import YardPageExternal from './YardPage';
import { Yard3D, useYardStore } from './yard';
import KubiChatbot, { KubiChatButton } from './KubiChatbot';
import AdminDashboard from './AdminDashboard';
import CooperSearch from './SpirkoSearch';
import OnboardingTour from './OnboardingTour';
import MyEnrichedContacts from './MyEnrichedContacts';
import './App.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://kubi-backend-production.up.railway.app';
const API_URL = API_BASE.endsWith('/api') ? API_BASE.slice(0, -4) : API_BASE;

// ==================== CONTEXTS ====================
// ThemeContext is defined in ./ThemeContext.js to avoid circular imports
const useTheme = useThemeFromContext;

const BillingContext = createContext({ credits: 0, setCredits: () => {}, plan: 'free', setPlan: () => {} });
const useBilling = () => useContext(BillingContext);

// Search context to pass position data from Yard to Search
const SearchContext = createContext({ searchFromPosition: null, setSearchFromPosition: () => {} });
const useSearchContext = () => useContext(SearchContext);

// ==================== MOBILE HOOK ====================
const useIsMobile = (breakpoint = 1024) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
};

// ==================== TOAST SYSTEM ====================
const ToastContext = createContext(null);
const useToast = () => useContext(ToastContext);

const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type = 'info', duration = 3500) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev.slice(-4), { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);
  const toast = useMemo(() => ({
    success: (m) => add(m, 'success'),
    error:   (m) => add(m, 'error'),
    info:    (m) => add(m, 'info'),
    warning: (m) => add(m, 'warning'),
  }), [add]);
  const typeStyles = {
    success: { bg: '#10b981', icon: '✓' },
    error:   { bg: '#ef4444', icon: '✕' },
    warning: { bg: '#f59e0b', icon: '⚠' },
    info:    { bg: '#1E88E5', icon: 'ℹ' },
  };
  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
        {toasts.map(t => {
          const s = typeStyles[t.type] || typeStyles.info;
          return (
            <div key={t.id} className="kubi-slide-right" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, maxWidth: 320, background: s.bg, color: '#fff', fontSize: 14, fontWeight: 500, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', pointerEvents: 'all' }}>
              <span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>{s.icon}</span>
              <span>{t.msg}</span>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

const getToken = () => localStorage.getItem('token');
const getUser = () => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } };

// Decode a JWT payload without verifying the signature (display only)
const decodeJwt = (token) => {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return null; }
};

// Check and install an impersonation token from the URL query param.
// Called once on app load — returns true if an impersonation session was started.
const installImpersonationToken = () => {
  const params = new URLSearchParams(window.location.search);
  const impToken = params.get('impersonation_token');
  if (!impToken) return false;
  const payload = decodeJwt(impToken);
  if (!payload?.impersonatedBy || !payload?.id) return false;
  // Store as the active auth token
  localStorage.setItem('token', impToken);
  // Store metadata so the banner can display it and clean up on end
  sessionStorage.setItem('knine_impersonation', JSON.stringify({
    impersonationId: payload.impersonationId,
    impersonatedBy: payload.impersonatedBy,
    email: payload.email,
    organizationId: payload.organizationId,
    expiresAt: payload.exp ? payload.exp * 1000 : null
  }));
  // Remove the token from the URL without reloading
  const clean = new URL(window.location.href);
  clean.searchParams.delete('impersonation_token');
  window.history.replaceState({}, '', clean.toString());
  return true;
};

// Run once immediately on module load
installImpersonationToken();

const ProtectedRoute = ({ children }) => (getToken() ? children : <Navigate to="/login" replace />);

const apiCall = async (endpoint, options = {}) => {
  const token = getToken();
  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers }
  });
  const contentType = res.headers.get('content-type') || '';
  let data = null;
  try {
    if (contentType.includes('application/json')) data = await res.json();
    else { const raw = await res.text(); if (raw.trim().startsWith('{')) data = JSON.parse(raw); else data = { message: raw }; }
  } catch { data = null; }
  if (!res.ok) throw new Error(data?.error || data?.message || `Request failed (${res.status})`);
  return data ?? {};
};

const formatCurrency = (val) => val ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val) : '$0';
const formatNumber = (val) => val ? new Intl.NumberFormat('en-US').format(val) : '0';

// ==================== CONSTANTS ====================
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }, { value: 'DC', label: 'Washington DC' },
  { value: 'REMOTE', label: 'Remote' }
];

const MAJOR_CITIES = {
  'CA': ['Los Angeles', 'San Francisco', 'San Diego', 'San Jose', 'Sacramento', 'Oakland', 'Irvine'],
  'NY': ['New York City', 'Buffalo', 'Rochester', 'Albany', 'Syracuse'],
  'TX': ['Houston', 'Dallas', 'Austin', 'San Antonio', 'Fort Worth'],
  'FL': ['Miami', 'Orlando', 'Tampa', 'Jacksonville', 'Fort Lauderdale'],
  'IL': ['Chicago', 'Aurora', 'Naperville', 'Rockford'],
  'WA': ['Seattle', 'Tacoma', 'Spokane', 'Bellevue'],
  'MA': ['Boston', 'Cambridge', 'Worcester', 'Springfield'],
  'GA': ['Atlanta', 'Augusta', 'Savannah', 'Athens'],
  'CO': ['Denver', 'Colorado Springs', 'Aurora', 'Boulder'],
  'default': ['Major City', 'Downtown', 'Metro Area']
};

// Billing constants - matches services/billing/config.js
const CREDIT_PACKAGES = [
  { id: 'credits_500', name: '500 Credits', credits: 500, price: 250, perCredit: 0.50 },
  { id: 'credits_1000', name: '1,000 Credits', credits: 1000, price: 450, perCredit: 0.45, savings: '10%' },
  { id: 'credits_2500', name: '2,500 Credits', credits: 2500, price: 1000, perCredit: 0.40, savings: '20%', popular: true },
  { id: 'credits_5000', name: '5,000 Credits', credits: 5000, price: 1750, perCredit: 0.35, savings: '30%' },
  { id: 'credits_10000', name: '10,000 Credits', credits: 10000, price: 3000, perCredit: 0.30, savings: '40%' }
];

const SUBSCRIPTION_PLANS = [
  { id: 'free', name: 'Free Trial', price: 0, annualPrice: 0, credits: 50, validation: false,
    features: ['50 enrichment credits', 'Basic filters', 'kNine Network access', 'Email support'],
    cta: 'Current Plan', color: '#64748b' },
  { id: 'starter', name: 'Starter', price: 250, annualPrice: 3000, credits: 2000, validation: false,
    features: ['2,000 credits/year', '2 users included', 'ATS integration', 'kNine Network access', 'Email support'],
    cta: 'Upgrade', color: '#1E88E5' },
  { id: 'professional', name: 'Professional', price: 500, annualPrice: 6000, credits: 5000, popular: true, validation: true,
    features: ['5,000 credits/year', '5 users included', 'ATS integration', 'kNine Contact Validation', 'Priority support'],
    cta: 'Upgrade', color: '#10b981' },
  { id: 'business', name: 'Business', price: 833, annualPrice: 10000, credits: 10000, validation: true,
    features: ['10,000 credits/year', '10 users included', 'ATS integration', 'kNine Contact Validation', 'Advanced analytics', 'Dedicated support'],
    cta: 'Upgrade', color: '#1E88E5' },
  { id: 'enterprise', name: 'Enterprise', price: null, annualPrice: null, credits: 'Unlimited', validation: true,
    features: ['Unlimited credits', 'Unlimited users', 'Custom integrations', 'kNine Contact Validation', 'Dedicated CSM', 'SLA guarantee'],
    cta: 'Contact Sales', color: '#f59e0b' }
];

const STRIPE_TEST_CARDS = [
  { name: 'Visa (Success)', number: '4242 4242 4242 4242', exp: '12/34', cvc: '123' },
  { name: 'Mastercard', number: '5555 5555 5555 4444', exp: '12/34', cvc: '123' },
];

// Available integration types
const AVAILABLE_INTEGRATIONS = [
  { type: 'pcrecruiter', name: 'PCRecruiter', icon: '🔗', color: '#1E88E5', description: 'Connect your PCRecruiter ATS', fields: [
    { key: 'username', label: 'PCR Username', type: 'text', placeholder: 'Your PCRecruiter username', required: true },
    { key: 'password', label: 'PCR Password', type: 'password', placeholder: 'Your PCRecruiter password', required: true },
    { key: 'databaseId', label: 'Database ID', type: 'text', placeholder: 'Your Database ID', required: true },
  ]},
  { type: 'stardex', name: 'Stardex', icon: '⭐', color: '#1565C0', description: 'Connect your Stardex AI-Powered ATS', fields: [
    { key: 'apiKey', label: 'API Key', type: 'password', placeholder: 'Your Stardex API key (Bearer token)', required: true },
    { key: 'apiUrl', label: 'API URL (optional)', type: 'text', placeholder: 'https://api.stardex.ai' },
  ]},
  { type: 'bigbiller', name: 'TE Recruit', icon: '📋', color: '#1e40af', description: 'Connect your TE Recruit (Top Echelon) ATS', authType: 'oauth2', fields: [
    { key: 'accessToken', label: 'Access Token', type: 'password', placeholder: 'Your TE Recruit API access token', required: true },
    { key: 'apiUrl', label: 'API URL (optional)', type: 'text', placeholder: 'https://bb3api.topechelon.com' },
  ],
    oauthStartUrl: '/api/integrations/bigbiller/auth/start',
    oauthStatusUrl: '/api/integrations/bigbiller/auth/status',
  },
  { type: 'bullhorn', name: 'Bullhorn', icon: '🐂', color: '#ff6600', description: 'Connect your Bullhorn CRM', fields: [
    { key: 'clientId', label: 'Client ID', type: 'text' },
    { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    { key: 'username', label: 'Username', type: 'text' },
    { key: 'password', label: 'Password', type: 'password' },
  ]},
  { type: 'greenhouse', name: 'Greenhouse', icon: '🌿', color: '#3ab549', description: 'Connect your Greenhouse ATS', fields: [
    { key: 'apiKey', label: 'API Key', type: 'password' },
  ]},
  { type: 'lever', name: 'Lever', icon: '⚡', color: '#5c5ce0', description: 'Connect your Lever ATS', fields: [
    { key: 'apiKey', label: 'API Key', type: 'password' },
    { key: 'environment', label: 'Environment', type: 'select', options: [{ value: 'sandbox', label: 'Sandbox' }, { value: 'production', label: 'Production' }] },
  ]},
];

// ==================== UI COMPONENTS ====================
const ProfileAvatar = ({ person, size = 'md' }) => {
  const name = person?.full_name || person?.name || person?.FirstName || person?.Name || '';
  const initials = name ? (name.split(' ').length >= 2 ? `${name.split(' ')[0][0]}${name.split(' ').pop()[0]}` : name.substring(0, 2)).toUpperCase() : '?';
  const colors = ['#1E88E5', '#057642', '#10b981', '#0077b5', '#00a0dc', '#1E88E5'];
  const color = colors[name.charCodeAt(0) % colors.length] || colors[0];
  const sizes = { sm: 32, md: 40, lg: 52, xl: 64 };
  return (
    <div style={{ width: sizes[size], height: sizes[size], borderRadius: '50%', background: `linear-gradient(135deg, ${color}, ${colors[(name.charCodeAt(0) + 1) % colors.length]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: sizes[size] * 0.38, flexShrink: 0 }}>
      {initials}
    </div>
  );
};

const Badge = ({ children, variant = 'default', size = 'sm' }) => {
  const { darkMode } = useTheme();
  const variants = {
    default: { bg: darkMode ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: darkMode ? '#a0a0b0' : '#374151' },
    primary: { bg: 'rgba(30, 136, 229, 0.15)', color: '#1E88E5' },
    success: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981' },
    warning: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' },
    danger: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' },
    purple: { bg: 'rgba(30, 136, 229, 0.15)', color: '#1E88E5' },
    ats: { bg: 'rgba(30, 136, 229, 0.15)', color: '#1E88E5' },
    knine: { bg: 'rgba(5, 118, 66, 0.15)', color: '#057642' }
  };
  const v = variants[variant] || variants.default;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: size === 'sm' ? '2px 8px' : '4px 12px', borderRadius: 20, fontSize: size === 'sm' ? 11 : 13, fontWeight: 600, background: v.bg, color: v.color }}>{children}</span>;
};

const Button = ({ children, variant = 'primary', size = 'md', icon: Icon, loading, disabled, onClick, style = {} }) => {
  const { darkMode } = useTheme();
  const variants = {
    primary: { bg: 'linear-gradient(135deg, #1E88E5, #1565C0)', color: '#fff', border: 'none' },
    secondary: { bg: darkMode ? '#1e293b' : '#fff', color: darkMode ? '#94a3b8' : '#374151', border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` },
    ghost: { bg: 'transparent', color: darkMode ? '#64748b' : '#6b7280', border: 'none' },
    danger: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.3)' },
    success: { bg: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none' },
    ai: { bg: 'linear-gradient(135deg, #1E88E5, #1565C0)', color: '#fff', border: 'none' }
  };
  const sizes = { sm: { px: 10, py: 6, font: 13 }, md: { px: 14, py: 9, font: 14 }, lg: { px: 18, py: 11, font: 15 } };
  const v = variants[variant] || variants.primary;
  const s = sizes[size] || sizes.md;
  return (
    <button onClick={onClick} disabled={disabled || loading} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: `${s.py}px ${s.px}px`, borderRadius: 8, fontSize: s.font, fontWeight: 600, background: v.bg, color: v.color, border: v.border, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1, transition: 'all 0.2s', ...style }}>
      {loading ? <Loader2 size={16} className="spin" /> : Icon && <Icon size={15} />}
      {children}
    </button>
  );
};

const Card = ({ children, style = {}, gradient, onClick }) => {
  const { darkMode } = useTheme();
  return <div onClick={onClick} style={{ background: gradient || (darkMode ? '#1e293b' : '#fff'), borderRadius: 12, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, overflow: 'hidden', cursor: onClick ? 'pointer' : undefined, ...style }}>{children}</div>;
};

const Toggle = ({ checked, onChange }) => {
  const { darkMode } = useTheme();
  return (
    <button onClick={() => onChange(!checked)} type="button" style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', background: checked ? '#1E88E5' : (darkMode ? '#334155' : '#e5e7eb'), position: 'relative', transition: 'background 0.2s' }}>
      <div style={{ width: 18, height: 18, borderRadius: 9, background: '#fff', position: 'absolute', top: 3, left: checked ? 23 : 3, transition: 'left 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
    </button>
  );
};

const LoadingSpinner = ({ text = 'Loading...' }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 12 }}>
    <Loader2 size={36} className="spin" style={{ color: '#1E88E5' }} />
    <p style={{ color: '#64748b', margin: 0 }}>{text}</p>
  </div>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, textAlign: 'center' }}>
    <div style={{ width: 64, height: 64, borderRadius: 16, background: 'rgba(30, 136, 229, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
      <Icon size={28} style={{ color: '#1E88E5' }} />
    </div>
    <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600 }}>{title}</h3>
    <p style={{ margin: '0 0 16px', color: '#64748b', maxWidth: 280, fontSize: 14 }}>{description}</p>
    {action}
  </div>
);

// ==================== SKELETON LOADERS ====================
const Skeleton = ({ width = '100%', height = 16, borderRadius = 8, style = {} }) => {
  const { darkMode } = useTheme();
  return (
    <div className="kubi-skeleton" style={{ width, height, borderRadius, background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)', ...style }} />
  );
};

const SearchResultSkeleton = () => {
  const { darkMode } = useTheme();
  return (
    <div style={{ padding: 20, background: darkMode ? '#1e293b' : '#fff', borderRadius: 12, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <Skeleton width={56} height={56} borderRadius={28} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Skeleton height={18} width="55%" />
        <Skeleton height={13} width="38%" />
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Skeleton height={24} width={80} borderRadius={12} />
          <Skeleton height={24} width={60} borderRadius={12} />
        </div>
      </div>
      <Skeleton width={72} height={32} borderRadius={16} />
    </div>
  );
};

const ModalOverlay = ({ children, onClose }) => (
  <div className="kubi-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
    <div className="kubi-modal-content" style={{ maxWidth: '100%', width: '100%', display: 'flex', justifyContent: 'center' }}>{children}</div>
  </div>
);

const RangeSlider = ({ label, min, max, value, onChange, unit = '', darkMode }) => {
  const percentage = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: darkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase' }}>{label}</label>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#1E88E5' }}>{value}{unit}</span>
      </div>
      <div style={{ position: 'relative', height: 6, background: darkMode ? '#334155' : '#e5e7eb', borderRadius: 3 }}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${percentage}%`, background: 'linear-gradient(90deg, #1E88E5, #10b981)', borderRadius: 3 }} />
        <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(parseInt(e.target.value))} style={{ position: 'absolute', top: -8, left: 0, width: '100%', height: 22, opacity: 0, cursor: 'pointer' }} />
        <div style={{ position: 'absolute', top: -7, left: `${percentage}%`, transform: 'translateX(-50%)', width: 20, height: 20, borderRadius: '50%', background: '#fff', border: '3px solid #1E88E5', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', pointerEvents: 'none' }} />
      </div>
    </div>
  );
};

const AnimatedCounter = ({ value, duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    let start = 0; const end = parseInt(value) || 0; const increment = end / (duration / 16);
    const timer = setInterval(() => { start += increment; if (start >= end) { setDisplayValue(end); clearInterval(timer); } else { setDisplayValue(Math.floor(start)); } }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);
  return <>{formatNumber(displayValue)}</>;
};

const StatCard = ({ icon: Icon, label, value, trend, color = '#1E88E5', gradient, darkMode }) => (
  <Card style={{ padding: 20 }} gradient={gradient}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: 12, color: gradient ? 'rgba(255,255,255,0.7)' : '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: 32, fontWeight: 800, color: gradient ? '#fff' : (darkMode ? '#f8fafc' : '#1f2937') }}><AnimatedCounter value={value} /></div>
        {trend !== undefined && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8 }}>
            {trend >= 0 ? <ArrowUp size={14} style={{ color: '#10b981' }} /> : <ArrowDown size={14} style={{ color: '#ef4444' }} />}
            <span style={{ fontSize: 13, fontWeight: 600, color: trend >= 0 ? '#10b981' : '#ef4444' }}>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: gradient ? 'rgba(255,255,255,0.15)' : `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={24} style={{ color: gradient ? '#fff' : color }} />
      </div>
    </div>
  </Card>
);

const MiniChart = ({ data, color = '#1E88E5', height = 40 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data); const min = Math.min(...data); const range = max - min || 1;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height }}>
      {data.map((val, i) => (<div key={i} style={{ flex: 1, height: `${((val - min) / range) * 100}%`, minHeight: 4, background: `linear-gradient(180deg, ${color}, ${color}40)`, borderRadius: 2 }} />))}
    </div>
  );
};

const ProgressRing = ({ progress, size = 80, strokeWidth = 8, color = '#1E88E5', label, hideLabel = false, children }) => {
  const radius = (size - strokeWidth) / 2; const circumference = radius * 2 * Math.PI; const strokeDashoffset = circumference - (progress / 100) * circumference;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} style={{ opacity: 0.1 }} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" />
      </svg>
      {!hideLabel && !children && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: size * 0.25, fontWeight: 800, color }}>{progress}%</span>
          {label && <span style={{ fontSize: 10, color: '#64748b' }}>{label}</span>}
        </div>
      )}
      {children && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {children}
        </div>
      )}
    </div>
  );
};

const CreditsBadge = ({ darkMode, isMobile }) => {
  const { credits } = useBilling();
  let color = '#10b981'; let bgColor = 'rgba(16, 185, 129, 0.1)';
  if (credits <= 10) { color = '#ef4444'; bgColor = 'rgba(239, 68, 68, 0.1)'; }
  else if (credits <= 50) { color = '#f59e0b'; bgColor = 'rgba(245, 158, 11, 0.1)'; }
  const displayValue = isMobile && credits > 999 ? `${(credits / 1000).toFixed(1)}K` : credits;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 8, padding: isMobile ? '6px 10px' : '8px 14px', borderRadius: 20, background: bgColor, border: `1px solid ${color}30` }}>
      <Zap size={isMobile ? 13 : 15} style={{ color }} />
      <span style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color }}>{displayValue}</span>
      {!isMobile && <span style={{ fontSize: 12, color: darkMode ? '#64748b' : '#6b7280' }}>credits</span>}
    </div>
  );
};

// ==================== SIDEBAR ====================
const Sidebar = ({ collapsed, setCollapsed, mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const { darkMode } = useTheme();
  const isMobile = useIsMobile();
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith('/settings'));

  const [aiAccess, setAiAccess] = useState(() => {
    try { const s = localStorage.getItem('kubi_ai_access'); return s ? JSON.parse(s) : { roxi: true, cooper: false }; } catch { return { roxi: true, cooper: false }; }
  });
  const [platformAccess, setPlatformAccess] = useState(() => {
    try { const s = localStorage.getItem('kubi_platform_access'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });

  useEffect(() => {
    const checkAccess = () => {
      try {
        const ai = localStorage.getItem('kubi_ai_access'); if (ai) setAiAccess(JSON.parse(ai));
        const pl = localStorage.getItem('kubi_platform_access'); if (pl) setPlatformAccess(JSON.parse(pl));
      } catch {}
    };
    window.addEventListener('storage', checkAccess);
    const interval = setInterval(checkAccess, 2000);
    return () => { window.removeEventListener('storage', checkAccess); clearInterval(interval); };
  }, []);

  // Close mobile drawer on route change
  useEffect(() => { if (mobileOpen && onMobileClose) onMobileClose(); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPlatformAccessible = (key) => { if (!platformAccess[key]) return true; return platformAccess[key].accessible !== false; };
  const getBrandingText = () => { if ((aiAccess.roxi || aiAccess.kubi) && (aiAccess.cooper || aiAccess.spirko)) return 'Roxi & Cooper AI'; if (aiAccess.cooper || aiAccess.spirko) return 'Cooper AI'; return 'Roxi AI'; };

  const mainNav = [
    ...(isPlatformAccessible('roxi') || isPlatformAccessible('kubi') ? [{ path: '/my-contacts', icon: BookOpen, label: 'My Contacts' }] : []),
    ...(isPlatformAccessible('roxi') || isPlatformAccessible('kubi') ? [{ path: '/roxi', icon: Search, label: 'Roxi Sourcing' }] : []),
    ...((aiAccess.cooper || aiAccess.spirko) ? [{ path: '/cooper', icon: Target, label: 'Cooper BD' }] : []),
    { path: '/analytics', icon: BarChart3, label: 'Analytics' },
    ...(isPlatformAccessible('the_yard') ? [{ path: '/yard', icon: TreePine, label: 'The Yard' }] : []),
    ...(isPlatformAccessible('tracker') ? [{ path: '/tracker', icon: UserCheck, label: 'Tracker' }] : []),
    ...(isPlatformAccessible('fee_recovery') ? [{ path: '/fee-recovery', icon: DollarSign, label: 'Fee Recovery' }] : []),
  ];
  const settingsNav = [
    { path: '/settings', icon: Settings, label: 'General', exact: true },
    { path: '/settings/team', icon: Users, label: 'Team' },
    { path: '/settings/integrations', icon: Building2, label: 'Integrations' },
    { path: '/settings/usage', icon: CreditCard, label: 'Usage & Billing' },
  ];

  const showLabel = !collapsed || isMobile;

  const NavItem = ({ item, nested = false }) => {
    const [showTip, setShowTip] = useState(false);
    const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
    return (
      <div style={{ position: 'relative' }}
           onMouseEnter={() => { if (!showLabel) setShowTip(true); }}
           onMouseLeave={() => setShowTip(false)}>
        <Link to={item.path} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: nested ? '9px 14px 9px 40px' : '11px 14px', borderRadius: 8, margin: '2px 6px', background: isActive ? 'rgba(30, 136, 229, 0.15)' : 'transparent', color: isActive ? '#1E88E5' : (darkMode ? '#94a3b8' : '#4b5563'), textDecoration: 'none', fontSize: 14, fontWeight: isActive ? 600 : 500, border: isActive ? '1px solid rgba(30, 136, 229, 0.25)' : '1px solid transparent', transition: 'all 0.15s ease', position: 'relative' }}>
          {isActive && <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: 20, background: '#1E88E5', borderRadius: '0 2px 2px 0' }} />}
          <item.icon size={18} />
          {showLabel && <span>{item.label}</span>}
        </Link>
        {showTip && (
          <div style={{ position: 'absolute', left: 'calc(100% + 8px)', top: '50%', transform: 'translateY(-50%)', background: darkMode ? '#1e293b' : '#1f2937', color: '#fff', padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', zIndex: 300, pointerEvents: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
            {item.label}
            <div style={{ position: 'absolute', right: '100%', top: '50%', transform: 'translateY(-50%)', border: '5px solid transparent', borderRightColor: darkMode ? '#1e293b' : '#1f2937' }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isMobile && mobileOpen && (
        <div onClick={onMobileClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)', zIndex: 1099 }} />
      )}
      <aside style={{ width: isMobile ? 260 : (collapsed ? 72 : 260), height: '100vh', position: 'fixed', left: 0, top: 0, background: darkMode ? '#0f172a' : '#fff', borderRight: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, display: 'flex', flexDirection: 'column', transition: isMobile ? 'transform 0.3s ease' : 'width 0.2s', zIndex: isMobile ? 1100 : 100, transform: isMobile && !mobileOpen ? 'translateX(-100%)' : 'translateX(0)' }}>
        <div style={{ padding: '16px 12px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #1E88E5, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 900, fontSize: 16, color: '#ffffff', boxShadow: '0 2px 10px rgba(30, 136, 229, 0.35)' }}>K9</div>
          {showLabel && <span style={{ fontSize: 18, fontWeight: 700, color: darkMode ? '#f8fafc' : '#111827' }}>k<span style={{ color: '#1E88E5' }}>Nine</span>.ai</span>}
          {isMobile ? (
            <button onClick={onMobileClose} style={{ marginLeft: 'auto', width: 32, height: 32, borderRadius: 8, background: darkMode ? '#1e293b' : '#f3f4f6', border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: darkMode ? '#94a3b8' : '#6b7280' }}>
              <X size={16} />
            </button>
          ) : (
            <button onClick={() => setCollapsed(!collapsed)} style={{ marginLeft: 'auto', width: 32, height: 32, borderRadius: 8, background: darkMode ? '#1e293b' : '#f3f4f6', border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: darkMode ? '#94a3b8' : '#6b7280' }}>
              <Menu size={16} />
            </button>
          )}
        </div>
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto' }}>
          {mainNav.map(item => <NavItem key={item.path} item={item} />)}
          <div style={{ margin: '12px 6px 0', position: 'relative' }}>
            <button onClick={() => setSettingsOpen(!settingsOpen)} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', borderRadius: 8, border: location.pathname.startsWith('/settings') ? '1px solid rgba(30, 136, 229, 0.25)' : '1px solid transparent', background: location.pathname.startsWith('/settings') ? 'rgba(30, 136, 229, 0.1)' : 'transparent', color: location.pathname.startsWith('/settings') ? '#1E88E5' : (darkMode ? '#94a3b8' : '#4b5563'), cursor: 'pointer', fontSize: 14, fontWeight: 500, textAlign: 'left' }}>
              <Settings size={18} />
              {showLabel && <><span style={{ flex: 1 }}>Settings</span><ChevronDown size={14} style={{ transform: settingsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} /></>}
            </button>
            {settingsOpen && showLabel && settingsNav.map(item => <NavItem key={item.path} item={item} nested />)}
          </div>
        </nav>
        {showLabel && (
          <div style={{ padding: 16, borderTop: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
            <div style={{ padding: 14, background: 'rgba(30, 136, 229, 0.08)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12, border: '1px solid rgba(30, 136, 229, 0.18)' }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: (aiAccess.cooper || aiAccess.spirko) && !(aiAccess.roxi || aiAccess.kubi) ? 'linear-gradient(135deg, #f59e0b, #ef4444)' : 'linear-gradient(135deg, #1E88E5, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12, color: '#fff', boxShadow: (aiAccess.cooper || aiAccess.spirko) && !(aiAccess.roxi || aiAccess.kubi) ? '0 2px 8px rgba(245,158,11,0.3)' : '0 2px 8px rgba(30,136,229,0.3)' }}>K9</div>
              <div>
                <div style={{ fontSize: 10, color: darkMode ? '#94a3b8' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Powered by</div>
                <div style={{ fontWeight: 700, fontSize: (aiAccess.roxi || aiAccess.kubi) && (aiAccess.cooper || aiAccess.spirko) ? 12 : 14, background: (aiAccess.cooper || aiAccess.spirko) && !(aiAccess.roxi || aiAccess.kubi) ? 'linear-gradient(135deg, #f59e0b, #ef4444)' : 'linear-gradient(135deg, #1E88E5, #42A5F5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{getBrandingText()}</div>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

// ==================== HEADER ====================
const Header = ({ user, onLogout, onMenuToggle }) => {
  const { darkMode } = useTheme();
  const { credits, plan } = useBilling();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowUserMenu(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header style={{ height: isMobile ? 60 : 64, background: darkMode ? '#1e293b' : '#fff', borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, display: 'flex', alignItems: 'center', padding: isMobile ? '0 12px' : '0 24px', gap: isMobile ? 8 : 14, flexShrink: 0 }}>
      {isMobile && (
        <button onClick={onMenuToggle} style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: darkMode ? '#94a3b8' : '#64748b', borderRadius: 8, flexShrink: 0 }}>
          <Menu size={22} />
        </button>
      )}
      {isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg, #1E88E5, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 12, color: '#fff', flexShrink: 0 }}>K9</div>
          <span style={{ fontWeight: 700, fontSize: 16, color: darkMode ? '#f8fafc' : '#111827', whiteSpace: 'nowrap' }}>kNine<span style={{ color: '#1E88E5' }}>.ai</span></span>
        </div>
      )}
      {!isMobile && <div style={{ flex: 1 }} />}
      <CreditsBadge darkMode={darkMode} isMobile={isMobile} />
      <div ref={menuRef} style={{ position: 'relative' }}>
        <div onClick={() => setShowUserMenu(!showUserMenu)} style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, padding: isMobile ? '5px 10px 5px 5px' : '6px 14px 6px 6px', borderRadius: 20, background: darkMode ? '#334155' : '#f3f4f6', cursor: 'pointer' }}>
          <ProfileAvatar person={user} size="sm" />
          {!isMobile && <span style={{ fontSize: 13, fontWeight: 500, color: darkMode ? '#f8fafc' : '#1f2937' }}>{user?.name || user?.email}</span>}
          <ChevronDown size={14} style={{ color: '#64748b' }} />
        </div>
        {showUserMenu && (
          <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: isMobile ? 240 : 280, background: darkMode ? '#1e293b' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, borderRadius: 12, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', zIndex: 200 }}>
            <div style={{ padding: 16, borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <ProfileAvatar person={user} size="md" />
                <div>
                  <div style={{ fontWeight: 600 }}>{user?.name || 'User'}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{user?.email}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <div style={{ flex: 1, padding: '8px 10px', borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc', textAlign: 'center' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#10b981' }}>{credits}</div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>Credits</div>
                </div>
                <div style={{ flex: 1, padding: '8px 10px', borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc', textAlign: 'center' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{plan}</div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>Plan</div>
                </div>
              </div>
            </div>
            <div style={{ padding: 8 }}>
              {[{ icon: CreditCard, label: 'Billing', path: '/settings/usage' }, { icon: Settings, label: 'Settings', path: '/settings' }].map(item => (
                <button key={item.path} onClick={() => { setShowUserMenu(false); navigate(item.path); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, cursor: 'pointer', textAlign: 'left' }}>
                  <item.icon size={16} style={{ color: '#64748b' }} /><span>{item.label}</span>
                </button>
              ))}
              <button onClick={() => { setShowUserMenu(false); window.open('/knine-quickstart.html', '_blank', 'noopener,noreferrer'); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, cursor: 'pointer', textAlign: 'left' }}>
                <ExternalLink size={16} style={{ color: '#64748b' }} /><span>Quick Start Guide</span>
              </button>
              <button onClick={() => { setShowUserMenu(false); window.open('/integrations.html', '_blank', 'noopener,noreferrer'); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, cursor: 'pointer', textAlign: 'left' }}>
                <ExternalLink size={16} style={{ color: '#64748b' }} /><span>Integration Setup</span>
              </button>
            </div>
            <div style={{ padding: 8, borderTop: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
              <button onClick={() => { setShowUserMenu(false); onLogout(); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: 'none', background: 'transparent', color: '#ef4444', fontSize: 14, cursor: 'pointer' }}>
                <LogOut size={16} /><span>Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// ==================== RESUME PANEL ====================
const ResumePanel = ({ candidate, onClose, darkMode }) => {
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [attachments, setAttachments] = useState([]);

  useEffect(() => { if (candidate) loadCandidateDetails(); }, [candidate]);

  const loadCandidateDetails = async () => {
    setLoading(true);
    try {
      const candidateId = candidate.CandidateId || candidate.id;
      const data = await apiCall(`/api/candidates/${candidateId}`);
      setResume(data.resume || data.Resume || data.ResumeText || null);
      try {
        const attData = await apiCall(`/api/candidates/${candidateId}/attachments`);
        setAttachments(Array.isArray(attData) ? attData : attData.attachments || []);
      } catch { setAttachments([]); }
    } catch (err) { console.error('Failed to load candidate:', err); }
    finally { setLoading(false); }
  };

  const getName = () => candidate?.full_name || candidate?.name || `${candidate?.FirstName || ''} ${candidate?.LastName || ''}`.trim() || 'Unknown';
  const getTitle = () => candidate?.title || candidate?.Title || candidate?.CurrentTitle || 'No title';
  const getCompany = () => candidate?.company || candidate?.Company || candidate?.CurrentCompany || '';
  const getLocation = () => candidate?.location || candidate?.Location || (candidate?.City ? `${candidate.City}, ${candidate.State || ''}` : '');
  const getEmail = () => candidate?.email || candidate?.Email || candidate?.EmailAddress || '';
  const getPhone = () => candidate?.phone || candidate?.Phone || candidate?.HomePhone || '';

  return (
    <div className="kubi-drawer" style={{ position: 'fixed', top: 0, right: 0, width: 600, height: '100vh', background: darkMode ? '#1e293b' : '#fff', borderLeft: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, boxShadow: '-10px 0 40px rgba(0,0,0,0.2)', zIndex: 500, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, display: 'flex', alignItems: 'center', gap: 16 }}>
        <ProfileAvatar person={{ name: getName() }} size="lg" />
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700 }}>{getName()}</h2>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>{getTitle()}{getCompany() && ` at ${getCompany()}`}</p>
          {getLocation() && <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={12} /> {getLocation()}</p>}
        </div>
        <button onClick={onClose} style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}><X size={18} /></button>
      </div>

      <div style={{ padding: '16px 24px', borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, display: 'flex', gap: 12 }}>
        {getEmail() && <a href={`mailto:${getEmail()}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc', color: '#1E88E5', fontSize: 13, textDecoration: 'none' }}><Mail size={14} /> {getEmail()}</a>}
        {getPhone() && <a href={`tel:${getPhone()}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc', color: '#1E88E5', fontSize: 13, textDecoration: 'none' }}><Phone size={14} /> {getPhone()}</a>}
      </div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
        {[{ id: 'profile', label: 'Profile', icon: User }, { id: 'resume', label: 'Resume', icon: FileText }, { id: 'attachments', label: `Files (${attachments.length})`, icon: Paperclip }].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ flex: 1, padding: '12px 16px', border: 'none', background: activeTab === tab.id ? (darkMode ? '#0f172a' : '#f8fafc') : 'transparent', color: activeTab === tab.id ? '#1E88E5' : '#64748b', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, borderBottom: activeTab === tab.id ? '2px solid #1E88E5' : '2px solid transparent', marginBottom: -1 }}>
            <tab.icon size={14} />{tab.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {loading ? <LoadingSpinner text="Loading candidate..." /> : (
          <>
            {activeTab === 'profile' && (
              <div>
                {(candidate?.Skills || candidate?.skills) && (
                  <div style={{ marginBottom: 24 }}>
                    <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Skills</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {(candidate.Skills || candidate.skills || '').split(/[,;]/).filter(Boolean).map((skill, i) => (<Badge key={i} variant="primary">{skill.trim()}</Badge>))}
                    </div>
                  </div>
                )}
                <div style={{ marginBottom: 24 }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Experience</h4>
                  <div style={{ padding: 16, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{getTitle()}</div>
                    <div style={{ fontSize: 14, color: '#64748b' }}>{getCompany()}</div>
                    {candidate?.YearsExperience && <div style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>{candidate.YearsExperience} years experience</div>}
                  </div>
                </div>
              </div>
            )}
            {activeTab === 'resume' && (
              <div>
                {resume ? (
                  <div style={{ padding: 20, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6, maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>{typeof resume === 'string' ? resume : JSON.stringify(resume, null, 2)}</div>
                ) : (<EmptyState icon={FileText} title="No resume on file" description="This candidate doesn't have a resume uploaded" />)}
              </div>
            )}
            {activeTab === 'attachments' && (
              <div>
                {attachments.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {attachments.map((att, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 16, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(30, 136, 229, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FileText size={20} style={{ color: '#1E88E5' }} /></div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{att.FileName || att.name || `File ${i + 1}`}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{att.FileType || 'Document'}</div>
                        </div>
                        {att.url && <a href={att.url} target="_blank" rel="noopener noreferrer" style={{ padding: '8px 12px', borderRadius: 6, background: '#1E88E5', color: '#fff', textDecoration: 'none', fontSize: 13 }}>View</a>}
                      </div>
                    ))}
                  </div>
                ) : (<EmptyState icon={Paperclip} title="No attachments" description="No files attached to this candidate" />)}
              </div>
            )}
          </>
        )}
      </div>

      <div style={{ padding: '16px 24px', borderTop: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, display: 'flex', gap: 12 }}>
        <Button variant="primary" icon={Send} style={{ flex: 1 }}>Submit to Position</Button>
        <Button variant="secondary" icon={ListPlus}>Add to List</Button>
        <Button variant="secondary" icon={ExternalLink}>Open in ATS</Button>
      </div>
    </div>
  );
};

// ==================== SEARCH PAGE - REAL API ====================
const SearchPage = () => {
  const { darkMode } = useTheme();
  const { credits, setCredits } = useBilling();
  const { searchFromPosition, setSearchFromPosition } = useSearchContext();
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Filters
  const [showFilters, setShowFilters] = useState(true);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [radius, setRadius] = useState(25);
  const [experienceMin, setExperienceMin] = useState(0);
  const [experienceMax, setExperienceMax] = useState(20);
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState('');
  const [sources, setSources] = useState({ ats: true, knine: false });

  // AI
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [showAiPanel, setShowAiPanel] = useState(true);
  const [searchSteps, setSearchSteps] = useState([]);

  // Handle position from Yard - build smart search criteria
  useEffect(() => {
    if (searchFromPosition) {
      const p = searchFromPosition;
      const title = p.title || p.JobTitle || '';
      setQuery(title);

      // Set location if available
      if (p.State) {
        const match = US_STATES.find(s => s.value === p.State || s.label.toLowerCase() === (p.State || '').toLowerCase());
        if (match) setSelectedState(match.value);
      }
      if (p.City) setSelectedCity(p.City);

      // Extract skills from description OR infer from title
      let foundSkills = [];
      if (p.description || p.Description) {
        const desc = p.description || p.Description;
        foundSkills = extractSkills(desc);
      }
      // If no skills from description, infer from title
      if (foundSkills.length === 0) {
        foundSkills = inferSkillsFromTitle(title);
      }
      if (foundSkills.length) setSkills(foundSkills.slice(0, 5));

      // Add search steps to show AI is working
      setSearchSteps([
        { text: `Analyzing position: ${title}`, done: true },
        { text: `Location: ${[p.City, p.State].filter(Boolean).join(', ') || 'Any'}`, done: true },
        { text: `Inferred skills: ${foundSkills.slice(0, 3).join(', ') || 'General'}`, done: true },
        { text: 'Searching for matching candidates...', done: false }
      ]);

      setTimeout(() => {
        handleSearch();
        setSearchSteps(prev => prev.map((s, i) => i === 3 ? { ...s, done: true } : s));
      }, 500);
      setSearchFromPosition(null);
    }
  }, [searchFromPosition]);

  const cityOptions = useMemo(() => selectedState && MAJOR_CITIES[selectedState] ? MAJOR_CITIES[selectedState].map(c => ({ value: c, label: c })) : [], [selectedState]);

  const extractSkills = (text) => {
    const common = ['JavaScript', 'Python', 'Java', 'React', 'Node.js', 'AWS', 'SQL', 'TypeScript', 'Angular', 'Vue', 'C++', 'C#', '.NET', 'Ruby', 'Go', 'Kubernetes', 'Docker', 'Machine Learning', 'Salesforce', 'SAP'];
    return common.filter(s => text.toLowerCase().includes(s.toLowerCase()));
  };

  // Infer skills from job title for smarter search
  const inferSkillsFromTitle = (title) => {
    if (!title) return [];
    const t = title.toLowerCase();
    const inferred = [];
    // Engineering roles
    if (t.includes('software') || t.includes('developer') || t.includes('engineer')) {
      if (t.includes('front') || t.includes('react') || t.includes('ui')) inferred.push('React', 'JavaScript', 'TypeScript');
      else if (t.includes('back') || t.includes('api')) inferred.push('Node.js', 'Python', 'SQL');
      else if (t.includes('full')) inferred.push('JavaScript', 'React', 'Node.js');
      else if (t.includes('data')) inferred.push('Python', 'SQL', 'Machine Learning');
      else if (t.includes('devops') || t.includes('sre') || t.includes('cloud')) inferred.push('AWS', 'Kubernetes', 'Docker');
      else if (t.includes('mobile') || t.includes('ios') || t.includes('android')) inferred.push('React Native', 'Swift', 'Kotlin');
      else inferred.push('JavaScript', 'Python');
    }
    // Sales roles
    if (t.includes('sales') || t.includes('account') || t.includes('business development')) {
      inferred.push('Salesforce', 'CRM', 'B2B Sales');
    }
    // Management
    if (t.includes('manager') || t.includes('director') || t.includes('lead')) {
      inferred.push('Leadership', 'Team Management');
    }
    // Marketing
    if (t.includes('marketing')) {
      inferred.push('Digital Marketing', 'SEO', 'Analytics');
    }
    return [...new Set(inferred)].slice(0, 5);
  };

  useEffect(() => {
    if (query.length > 3) {
      const timer = setTimeout(() => {
        const suggestions = [];
        const q = query.toLowerCase();
        if (q.includes('engineer') || q.includes('developer')) {
          suggestions.push({ type: 'skill', value: 'Python' }, { type: 'skill', value: 'JavaScript' }, { type: 'refinement', value: 'Add "senior"', query: `Senior ${query}` });
        } else if (q.includes('sales') || q.includes('account')) {
          suggestions.push({ type: 'skill', value: 'Salesforce' }, { type: 'refinement', value: 'Add industry', query: `${query} SaaS` });
        } else {
          suggestions.push({ type: 'tip', value: 'Add specific skills' }, { type: 'tip', value: 'Include location' });
        }
        setAiSuggestions(suggestions.slice(0, 4));
      }, 500);
      return () => clearTimeout(timer);
    } else setAiSuggestions([]);
  }, [query]);

  const addSkill = (s) => { if (s && !skills.includes(s)) { setSkills([...skills, s]); setSkillInput(''); } };
  const removeSkill = (s) => setSkills(skills.filter(x => x !== s));

  const handleSearch = async () => {
    if (!query.trim() && skills.length === 0) return;
    setLoading(true); setSearched(true); setError(null); setResults([]); setSearchSteps([]);

    const steps = [{ text: 'Analyzing search...' }, { text: 'Querying ATS...' }, { text: 'Ranking results...' }];
    for (let i = 0; i < steps.length; i++) { await new Promise(r => setTimeout(r, 200)); setSearchSteps(prev => [...prev, steps[i]]); }

    try {
      const params = { query: query.trim(), skills: skills.join(','), state: selectedState, city: selectedCity, radius, experienceMin, experienceMax };
      const response = await apiCall('/api/search', { method: 'POST', body: JSON.stringify(params) });
      const candidates = response.candidates || response.results || response.data || [];
      setResults(candidates); setTotalResults(response.total || candidates.length);
      if (candidates.length) { setCredits(prev => Math.max(0, prev - 1)); localStorage.setItem('kubi_search_count', (parseInt(localStorage.getItem('kubi_search_count') || '0') + 1).toString()); }
    } catch (err) {
      console.error('Search failed:', err);
      try {
        const alt = await apiCall(`/api/candidates/search?q=${encodeURIComponent(query)}&limit=25`);
        const candidates = alt.candidates || alt.results || (Array.isArray(alt) ? alt : []);
        setResults(candidates); setTotalResults(candidates.length);
        if (candidates.length) setCredits(prev => Math.max(0, prev - 1));
      } catch { setError(err.message || 'Search failed'); }
    }
    setLoading(false);
  };

  const applySuggestion = (s) => {
    if (s.type === 'skill') addSkill(s.value);
    else if (s.type === 'refinement' && s.query) setQuery(s.query);
  };

  const getName = (c) => c.full_name || c.name || `${c.FirstName || ''} ${c.LastName || ''}`.trim() || 'Unknown';
  const getTitle = (c) => c.title || c.Title || c.CurrentTitle || 'No title';
  const getCompany = (c) => c.company || c.Company || c.CurrentCompany || '';
  const getLocation = (c) => c.location || c.Location || (c.City ? `${c.City}, ${c.State || ''}` : '');
  const getSkills = (c) => c.skills && Array.isArray(c.skills) ? c.skills : (c.Skills ? c.Skills.split(/[,;]/).map(s => s.trim()).filter(Boolean) : []);
  const getExp = (c) => c.experience || c.YearsExperience || null;
  const getScore = (c) => c.matchScore || c.score || Math.floor(Math.random() * 20) + 80;
  const getSource = (c) => c.source || (c.CandidateId ? 'ats' : 'knine');
  const hasResume = (c) => c.hasResume || c.Resume || c.ResumeText;

  return (
    <div>
      {/* Hero Search */}
      <div style={{ background: `linear-gradient(135deg, ${darkMode ? '#0f172a' : '#f8fafc'}, ${darkMode ? '#1e293b' : '#e2e8f0'})`, borderRadius: 20, padding: 32, marginBottom: 24, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #1E88E5, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Brain size={24} style={{ color: '#fff' }} /></div>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, background: 'linear-gradient(135deg, #1E88E5, #10b981)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Roxi Search</h1>
              <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>AI-powered candidate discovery</p>
            </div>
          </div>

          <div style={{ position: 'relative', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', background: darkMode ? '#0f172a' : '#fff', borderRadius: 16, border: `2px solid ${darkMode ? '#334155' : '#e5e7eb'}`, padding: '4px 4px 4px 20px' }}>
              <Wand2 size={20} style={{ color: '#1E88E5', marginRight: 12 }} />
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} placeholder="Describe your ideal candidate..." style={{ flex: 1, padding: '16px 0', border: 'none', background: 'transparent', fontSize: 16, color: darkMode ? '#f8fafc' : '#1f2937', outline: 'none' }} />
              <Button variant="ai" size="lg" icon={Search} loading={loading} onClick={handleSearch} style={{ borderRadius: 12 }}>Search</Button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={() => setShowFilters(!showFilters)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 20, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: showFilters ? 'rgba(30, 136, 229, 0.1)' : 'transparent', color: showFilters ? '#1E88E5' : '#64748b', fontSize: 13, cursor: 'pointer' }}><SlidersHorizontal size={14} />Filters</button>
            <button onClick={() => setShowAiPanel(!showAiPanel)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 20, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: showAiPanel ? 'rgba(30, 136, 229, 0.1)' : 'transparent', color: showAiPanel ? '#1E88E5' : '#64748b', fontSize: 13, cursor: 'pointer' }}><Lightbulb size={14} />AI Suggestions</button>
            {selectedState && <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, background: 'rgba(30, 136, 229, 0.1)', color: '#1E88E5', fontSize: 12 }}><MapPin size={12} />{selectedCity ? `${selectedCity}, ` : ''}{selectedState}<button onClick={() => { setSelectedState(''); setSelectedCity(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}><X size={12} style={{ color: '#1E88E5' }} /></button></span>}
            {skills.map(s => <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', fontSize: 12 }}>{s}<button onClick={() => removeSkill(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}><X size={12} style={{ color: '#10b981' }} /></button></span>)}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: showFilters ? '280px 1fr' : '1fr', gap: 24 }}>
        {/* Filters */}
        {showFilters && (
          <Card style={{ padding: 20, position: 'sticky', top: 20, alignSelf: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Filters</h3>
              <button onClick={() => { setSelectedState(''); setSelectedCity(''); setRadius(25); setExperienceMin(0); setExperienceMax(20); setSkills([]); }} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6, border: 'none', background: 'transparent', color: '#64748b', fontSize: 12, cursor: 'pointer' }}><RotateCcw size={12} />Reset</button>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 12, textTransform: 'uppercase' }}>Data Sources</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 8 }}><input type="checkbox" checked={sources.ats} onChange={(e) => setSources({ ...sources, ats: e.target.checked })} style={{ width: 18, height: 18, accentColor: '#1E88E5' }} /><Database size={16} style={{ color: '#1E88E5' }} /><span style={{ fontSize: 14 }}>Your ATS</span><Badge variant="ats" size="sm">PCR</Badge></label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}><input type="checkbox" checked={sources.knine} onChange={(e) => setSources({ ...sources, knine: e.target.checked })} style={{ width: 18, height: 18, accentColor: '#10b981' }} /><Globe size={16} style={{ color: '#10b981' }} /><span style={{ fontSize: 14 }}>kNine Network</span><Badge variant="knine" size="sm">200M+</Badge></label>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Location</label>
              <select value={selectedState} onChange={(e) => { setSelectedState(e.target.value); setSelectedCity(''); }} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, marginBottom: 8 }}>
                <option value="">All States</option>
                {US_STATES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {selectedState && cityOptions.length > 0 && (
                <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14 }}>
                  <option value="">All Cities</option>
                  {cityOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              )}
            </div>

            {selectedCity && <RangeSlider label="Radius" min={5} max={100} value={radius} onChange={setRadius} unit=" mi" darkMode={darkMode} />}

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Experience (Years)</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input type="number" min={0} max={30} value={experienceMin} onChange={(e) => setExperienceMin(parseInt(e.target.value) || 0)} style={{ width: 70, padding: '8px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, textAlign: 'center' }} />
                <span>to</span>
                <input type="number" min={0} max={30} value={experienceMax} onChange={(e) => setExperienceMax(parseInt(e.target.value) || 0)} style={{ width: 70, padding: '8px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, textAlign: 'center' }} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Skills</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input type="text" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { addSkill(skillInput); e.preventDefault(); } }} placeholder="Add skill..." style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14 }} />
                <button onClick={() => addSkill(skillInput)} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#1E88E5', color: '#fff', cursor: 'pointer' }}><Plus size={16} /></button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {skills.map(s => <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 20, background: 'rgba(30, 136, 229, 0.1)', color: '#1E88E5', fontSize: 12 }}>{s}<button onClick={() => removeSkill(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}><X size={12} /></button></span>)}
              </div>
            </div>
          </Card>
        )}

        {/* Results */}
        <div>
          {showAiPanel && aiSuggestions.length > 0 && (
            <Card style={{ padding: 16, marginBottom: 16, background: darkMode ? 'rgba(30, 136, 229, 0.05)' : 'rgba(30, 136, 229, 0.03)', border: `1px solid rgba(30, 136, 229, 0.2)` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}><Sparkles size={16} style={{ color: '#1E88E5' }} /><span style={{ fontSize: 13, fontWeight: 600, color: '#1E88E5' }}>AI Suggestions</span></div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {aiSuggestions.map((s, i) => (
                  <button key={i} onClick={() => applySuggestion(s)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 20, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#1e293b' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 13, cursor: 'pointer' }}>
                    {s.type === 'skill' && <Hash size={12} style={{ color: '#10b981' }} />}
                    {s.type === 'refinement' && <Wand2 size={12} style={{ color: '#1E88E5' }} />}
                    {s.type === 'tip' && <Lightbulb size={12} style={{ color: '#64748b' }} />}
                    <span>{s.value}</span>
                  </button>
                ))}
              </div>
            </Card>
          )}

          {loading && (
            <Card style={{ padding: 24, marginBottom: 16 }}>
              {searchSteps.map((step, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: i === searchSteps.length - 1 ? 1 : 0.5, marginBottom: 8 }}>
                  {i === searchSteps.length - 1 ? <Loader2 size={18} className="spin" style={{ color: '#1E88E5' }} /> : <CheckCircle size={18} style={{ color: '#10b981' }} />}
                  <span style={{ fontSize: 14 }}>{step.text}</span>
                </div>
              ))}
            </Card>
          )}

          {error && <Card style={{ padding: 16, marginBottom: 16, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><AlertCircle size={20} style={{ color: '#ef4444' }} /><span style={{ color: '#ef4444' }}>{error}</span></div></Card>}

          {!loading && searched && results.length > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{ fontSize: 14, color: '#64748b' }}>Found <strong>{totalResults}</strong> candidates</span>
                <div style={{ display: 'flex', gap: 8 }}><Button variant="ghost" size="sm" icon={Download}>Export</Button><Button variant="ghost" size="sm" icon={ListPlus}>Add to List</Button></div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {results.map((c, idx) => {
                  const score = getScore(c);
                  return (
                    <Card key={c.CandidateId || c.id || idx} style={{ padding: 20, cursor: 'pointer' }} onClick={() => setSelectedCandidate(c)}>
                      <div style={{ display: 'flex', gap: 16 }}>
                        <ProfileAvatar person={{ name: getName(c) }} size="lg" />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                            <div>
                              <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700 }}>{getName(c)}</h3>
                              <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>{getTitle(c)}{getCompany(c) && ` at ${getCompany(c)}`}</p>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <div style={{ padding: '6px 12px', borderRadius: 20, background: score >= 90 ? 'rgba(16, 185, 129, 0.15)' : score >= 80 ? 'rgba(30, 136, 229, 0.15)' : 'rgba(245, 158, 11, 0.15)', color: score >= 90 ? '#10b981' : score >= 80 ? '#1E88E5' : '#f59e0b', fontWeight: 700, fontSize: 14 }}>{score}% match</div>
                              <Badge variant={getSource(c) === 'ats' ? 'ats' : 'knine'}>{getSource(c) === 'ats' ? 'ATS' : 'kNine'}</Badge>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 13, color: '#64748b' }}>
                            {getLocation(c) && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={14} />{getLocation(c)}</span>}
                            {getExp(c) && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Briefcase size={14} />{getExp(c)} years</span>}
                            {hasResume(c) && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#10b981' }}><FileText size={14} />Resume</span>}
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {getSkills(c).slice(0, 5).map(s => <span key={s} style={{ padding: '4px 10px', borderRadius: 20, background: darkMode ? '#334155' : '#f3f4f6', fontSize: 12 }}>{s}</span>)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <Button variant="primary" size="sm" icon={Eye} onClick={(e) => { e.stopPropagation(); setSelectedCandidate(c); }}>View</Button>
                          <Button variant="secondary" size="sm" icon={Mail}>Contact</Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}

          {!loading && searched && results.length === 0 && !error && <EmptyState icon={FileSearch} title="No candidates found" description="Try adjusting your search criteria" action={<Button variant="primary" icon={RotateCcw} onClick={() => { setQuery(''); setSkills([]); setSelectedState(''); }}>Clear Filters</Button>} />}
          {!loading && !searched && <EmptyState icon={Search} title="Start your search" description="Enter keywords or describe your ideal candidate" />}
        </div>
      </div>

      {selectedCandidate && <ResumePanel candidate={selectedCandidate} onClose={() => setSelectedCandidate(null)} darkMode={darkMode} />}
    </div>
  );
};

// ==================== SAMPLE ANALYTICS DATA (shown when no ATS connected) ====================
const SAMPLE_ANALYTICS = {
  stats: { totalSearches: 38, submittals: 24, interviews: 14, placements: 6, potentialRevenue: 312000, actualRevenue: 87500, activePositions: 12 },
  funnel: [
    { stage: 'Active Positions', count: 12 },
    { stage: 'Submittals', count: 24 },
    { stage: 'Interviews', count: 14 },
    { stage: 'Placements', count: 6 },
  ],
  leaderboard: [
    { userName: 'Alex M.', fullName: 'Alex Mitchell', placements: 3, revenue: 52500, submittals: 11, interviews: 7 },
    { userName: 'Jordan K.', fullName: 'Jordan Kim', placements: 2, revenue: 28000, submittals: 9, interviews: 5 },
    { userName: 'Sam P.', fullName: 'Sam Parker', placements: 1, revenue: 7000, submittals: 4, interviews: 2 },
  ],
  weeklyActivity: [
    { week: 'Wk 1', submittals: 6, interviews: 4, placements: 1 },
    { week: 'Wk 2', submittals: 7, interviews: 3, placements: 2 },
    { week: 'Wk 3', submittals: 5, interviews: 4, placements: 1 },
    { week: 'Wk 4', submittals: 6, interviews: 3, placements: 2 },
  ],
  openPositions: [
    { JobId: 's1', title: 'Senior Software Engineer', company: 'Acme Corp', salary: 145000, status: 'active', progress: 65, City: 'Austin', State: 'TX', type: 'permanent' },
    { JobId: 's2', title: 'VP of Sales', company: 'GrowthCo', salary: 220000, status: 'active', progress: 30, City: 'Chicago', State: 'IL', type: 'sales' },
    { JobId: 's3', title: 'Registered Nurse (ICU)', company: 'Metro Health', salary: 88000, status: 'active', progress: 80, City: 'Dallas', State: 'TX', type: 'permanent' },
    { JobId: 's4', title: 'Account Executive', company: 'CloudStack', salary: 95000, status: 'pending', progress: 45, City: 'Remote', State: '', type: 'sales' },
    { JobId: 's5', title: 'DevOps Engineer', company: 'Finex Technologies', salary: 130000, status: 'active', progress: 55, City: 'New York', State: 'NY', type: 'permanent' },
    { JobId: 's6', title: 'Director of Marketing', company: 'BrandForce', salary: 175000, status: 'active', progress: 20, City: 'Los Angeles', State: 'CA', type: 'sales' },
    { JobId: 's7', title: 'Data Scientist', company: 'AnalyticsPro', salary: 155000, status: 'active', progress: 70, City: 'Seattle', State: 'WA', type: 'permanent' },
    { JobId: 's8', title: 'Logistics Coordinator', company: 'FastFreight LLC', salary: 52000, status: 'pending', progress: 35, City: 'Atlanta', State: 'GA', type: 'permanent' },
    { JobId: 's9', title: 'Business Development Manager', company: 'NexaMedia', salary: 115000, status: 'active', progress: 50, City: 'Boston', State: 'MA', type: 'sales' },
    { JobId: 's10', title: 'UX Designer', company: 'PixelWorks', salary: 105000, status: 'active', progress: 60, City: 'Denver', State: 'CO', type: 'permanent' },
    { JobId: 's11', title: 'Regional Sales Director', company: 'MidWest Industrial', salary: 185000, status: 'active', progress: 15, City: 'Columbus', State: 'OH', type: 'sales' },
    { JobId: 's12', title: 'Physical Therapist', company: 'WellCare Clinics', salary: 82000, status: 'pending', progress: 85, City: 'Phoenix', State: 'AZ', type: 'permanent' },
  ],
};

// ==================== ANALYTICS PAGE - REAL PCR DATA ====================
const AnalyticsPage = () => {
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('this_month');
  const [stats, setStats] = useState({ totalSearches: 0, submittals: 0, interviews: 0, placements: 0, potentialRevenue: 0, actualRevenue: 0, activePositions: 0 });
  const [ratios, setRatios] = useState({});
  const [funnel, setFunnel] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [weeklyActivity, setWeeklyActivity] = useState([]);
  const [insights, setInsights] = useState([]);
  const [healthScore, setHealthScore] = useState(null);
  const [trajectory, setTrajectory] = useState(null);
  const [revenueForecast, setRevenueForecast] = useState(null);
  const [pcrConnected, setPcrConnected] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState('all');
  const [expandedInsights, setExpandedInsights] = useState(false);
  const [userPerformance, setUserPerformance] = useState(null);
  const [openPositions, setOpenPositions] = useState([]);
  const [selectedAnalyticsPosition, setSelectedAnalyticsPosition] = useState(null);
  const [knineStats, setKnineStats] = useState({ searches: 0, enrichments: 0, submitted: 0, interviewing: 0, placed: 0 });
  const [cooperStats, setCooperStats] = useState(null);

  const navigate = useNavigate();

  useEffect(() => { loadAnalytics(); }, [period, selectedUser]);

  // Reset expanded state when user changes
  useEffect(() => { setExpandedInsights(false); }, [selectedUser]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    setUserPerformance(null);
    try {
      // Load ATS analytics with optional user filter
      const userParam = selectedUser !== 'all' ? `&teamUsernames=${encodeURIComponent(selectedUser)}` : '';
      const pcrData = await apiCall(`/api/analytics/pcr/summary?range=${period}${userParam}`).catch(err => {
        console.log('ATS Analytics not available:', err.message);
        return null;
      });

      if (pcrData && pcrData.connected !== false) {
        setPcrConnected(true);
        setStats({
          totalSearches: pcrData.kpis?.totalSearches || 0,
          submittals: pcrData.kpis?.submittals || 0,
          interviews: pcrData.kpis?.interviews || 0,
          placements: pcrData.kpis?.placements || 0,
          potentialRevenue: pcrData.kpis?.potentialRevenue || 0,
          actualRevenue: pcrData.kpis?.actualRevenue || 0,
          activePositions: pcrData.kpis?.activePositions || 0
        });
        setRatios(pcrData.ratios || {});
        // Transform funnel object to array for display
        const funnelData = pcrData.funnel ? [
          { stage: 'Active Positions', count: pcrData.kpis?.activePositions || 0 },
          { stage: 'Submittals', count: pcrData.funnel.sendouts || 0 },
          { stage: 'Interviews', count: pcrData.funnel.interviews || 0 },
          { stage: 'Placements', count: pcrData.funnel.placements || 0 }
        ] : null;
        setFunnel(funnelData);
        setLeaderboard(pcrData.leaderboard || []);
        setWeeklyActivity(pcrData.weeklyActivity || []);
        setInsights(pcrData.insights || []);
        setHealthScore(pcrData.healthScore || null);
        setTrajectory(pcrData.trajectory || null);
        setRevenueForecast(pcrData.revenueForecast || null);
      } else {
        setPcrConnected(false);
        // Load sample data so the dashboard isn't completely empty
        const realSearchCount = pcrData?.kpis?.totalSearches || 0;
        setStats({ ...SAMPLE_ANALYTICS.stats, totalSearches: realSearchCount });
        setFunnel(SAMPLE_ANALYTICS.funnel);
        setLeaderboard(SAMPLE_ANALYTICS.leaderboard);
        setWeeklyActivity(SAMPLE_ANALYTICS.weeklyActivity);
        setOpenPositions(SAMPLE_ANALYTICS.openPositions);
      }

      // When a specific user is selected, fetch AI-powered user performance insights
      if (selectedUser !== 'all') {
        const aiPeriod = period === 'this_week' || period === 'last_week' ? '7d' : period === 'this_quarter' || period === 'last_quarter' || period === 'ytd' ? '90d' : '30d';
        const userInsights = await apiCall(`/api/analytics/insights?period=${aiPeriod}&userId=${encodeURIComponent(selectedUser)}`).catch(err => {
          console.log('User AI insights not available:', err.message);
          return null;
        });
        if (userInsights) {
          setUserPerformance(userInsights);
        }
      }

      // Load open positions — try PCR analytics first, fall back to generic multi-ATS
      let posData = await apiCall('/api/pcr/analytics/positions?limit=50').catch(() => null);
      if (!posData || posData.connected === false) {
        posData = await apiCall('/api/positions').catch(() => null);
      }
      if (posData) {
        const list = posData.positions || posData.data || posData || [];
        setOpenPositions(Array.isArray(list) ? list : []);
      }

      // Load kNine analytics
      const periodDays = period === 'this_week' ? '7' : period === 'last_week' ? '7' : period === 'this_quarter' || period === 'last_quarter' ? '90' : period === 'ytd' ? '365' : '30';
      const knineData = await apiCall(`/api/analytics/knine?days=${periodDays}${selectedUser !== 'all' ? `&userId=${encodeURIComponent(selectedUser)}` : ''}`).catch(() => null);
      if (knineData) {
        setKnineStats(knineData);
      }

      // Load Cooper BD analytics (if user has access)
      try {
        const aiAccess = JSON.parse(localStorage.getItem('kubi_ai_access') || '{}');
        if (aiAccess.cooper || aiAccess.spirko) {
          const cooperData = await apiCall(`/api/cooper-search/analytics?days=${periodDays}`).catch(() => null);
          if (cooperData) setCooperStats(cooperData);
        }
      } catch (e) { /* ignore */ }
    } catch (err) {
      console.error('Analytics error:', err);
      setError(err.message);
      setPcrConnected(false);
    }
    setLoading(false);
  };

  const handleFindCandidatesFromAnalytics = (p) => {
    const searchData = {
      title: p.title || p.JobTitle || '',
      city: p.City || '',
      state: p.State || '',
      description: p.description || p.Description || '',
      company: p.company || '',
      timestamp: Date.now()
    };
    localStorage.setItem('kubi_searchFromPosition', JSON.stringify(searchData));
    navigate('/roxi');
  };

  const getPositionColor = (p) => {
    const progress = p.progress || 0;
    if (progress >= 70) return '#10b981';
    if (progress >= 40) return '#f59e0b';
    return '#1E88E5';
  };

  if (loading) return <LoadingSpinner text="Loading analytics..." />;

  return (
    <div>
      {/* Sample Data Banner — shown when no ATS/CRM is connected */}
      {!pcrConnected && (
        <div style={{ marginBottom: 20, borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(99,102,241,0.3)', background: darkMode ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)' }}>
          <div style={{ padding: '10px 16px', background: 'linear-gradient(135deg, #1565C0, #1E88E5)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={16} style={{ color: '#fff' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>SAMPLE DATA — Preview Mode</span>
          </div>
          <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: darkMode ? '#e2e8f0' : '#1e293b', marginBottom: 4 }}>
                No ATS or CRM connected — showing example data
              </div>
              <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                This is what your analytics look like with live data. Go to{' '}
                <Link to="/settings/integrations" style={{ color: '#1565C0', fontWeight: 600, textDecoration: 'none' }}>Settings → Integrations</Link>
                {' '}to sync your system.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <Link to="/settings/integrations" style={{ textDecoration: 'none' }}>
                <Button variant="secondary" size="sm" icon={Settings}>Set Up Integration</Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: 16, marginBottom: 20, borderRadius: 12, background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <XCircle size={20} style={{ color: '#ef4444' }} />
          <div style={{ fontSize: 14, color: '#ef4444' }}>{error}</div>
        </div>
      )}

      <div className="kubi-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 16 }}>
        <div>
          <div className="kubi-page-title-row" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
            <h1 className="kubi-page-title" style={{ margin: 0, fontSize: 28, fontWeight: 800 }}>Analytics Dashboard</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, background: 'linear-gradient(135deg, rgba(30, 136, 229, 0.15), rgba(30, 136, 229, 0.15))', border: '1px solid rgba(30, 136, 229, 0.3)' }}>
              <Sparkles size={14} style={{ color: '#1E88E5' }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: '#1E88E5' }}>AI-Powered</span>
            </div>
          </div>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Live reporting with AI intelligence, forecasts & actionable insights</p>
        </div>
        <div className="kubi-filter-bar" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* User Filter */}
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#1e293b' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, cursor: 'pointer', minWidth: 140 }}
          >
            <option value="all">All Team Members</option>
            {leaderboard.map(user => (
              <option key={user.userName} value={user.userName}>{user.userName || user.fullName}</option>
            ))}
          </select>
          {/* Period Filter */}
          <select value={period} onChange={(e) => setPeriod(e.target.value)} style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#1e293b' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, cursor: 'pointer' }}>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="this_quarter">This Quarter</option>
            <option value="last_quarter">Last Quarter</option>
            <option value="ytd">Year to Date</option>
          </select>
          <Button variant="secondary" icon={RefreshCw} onClick={loadAnalytics}>Refresh</Button>
        </div>
      </div>

      {/* User-specific view indicator */}
      {selectedUser !== 'all' && (
        <div style={{ padding: 12, marginBottom: 20, borderRadius: 10, background: 'rgba(30, 136, 229, 0.1)', border: '1px solid rgba(10, 102, 194, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <User size={18} style={{ color: '#1E88E5' }} />
            <span style={{ fontSize: 14, color: '#1E88E5', fontWeight: 600 }}>Viewing activity for: {selectedUser}</span>
          </div>
          <button onClick={() => setSelectedUser('all')} style={{ background: 'none', border: 'none', color: '#1E88E5', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            Clear filter ×
          </button>
        </div>
      )}

      {/* User Performance Score (when individual user selected) */}
      {selectedUser !== 'all' && userPerformance && userPerformance.grade && (
        <Card style={{ padding: 20, marginBottom: 24, background: darkMode ? '#1e293b' : '#fff', border: `2px solid ${userPerformance.grade.letter >= 'D' ? '#ef4444' : userPerformance.grade.letter >= 'C' ? '#f59e0b' : userPerformance.grade.letter >= 'B' ? '#1E88E5' : '#10b981'}30` }}>
          <div className="kubi-score-layout" style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <ProgressRing progress={userPerformance.grade.score || 0} size={100} strokeWidth={10} color={userPerformance.grade.letter >= 'D' ? '#ef4444' : userPerformance.grade.letter >= 'C' ? '#f59e0b' : userPerformance.grade.letter >= 'B' ? '#1E88E5' : '#10b981'}>
              <div style={{ fontSize: 28, fontWeight: 800, color: userPerformance.grade.letter >= 'D' ? '#ef4444' : userPerformance.grade.letter >= 'C' ? '#f59e0b' : userPerformance.grade.letter >= 'B' ? '#1E88E5' : '#10b981' }}>{userPerformance.grade.letter}</div>
            </ProgressRing>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Performance Score: {selectedUser}</h3>
                <Badge variant={userPerformance.grade.score >= 80 ? 'success' : userPerformance.grade.score >= 60 ? 'primary' : userPerformance.grade.score >= 40 ? 'warning' : 'danger'}>
                  {userPerformance.grade.letter} ({userPerformance.grade.score}/100)
                </Badge>
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 14, color: '#64748b' }}>
                {userPerformance.aiInsights || (userPerformance.grade.score >= 80 ? 'Outstanding individual performance. Top contributor on the team!' :
                 userPerformance.grade.score >= 60 ? 'Solid performance with room for growth in specific areas.' :
                 userPerformance.grade.score >= 40 ? 'Some areas need attention. See recommendations below.' :
                 'Performance needs improvement. Focus on the recommended actions.')}
              </p>
              {userPerformance.grade.breakdown && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {Object.entries(userPerformance.grade.breakdown).map(([key, val]) => (
                    <div key={key} style={{ padding: '4px 10px', borderRadius: 6, background: val >= 80 ? 'rgba(16, 185, 129, 0.15)' : val >= 60 ? 'rgba(30, 136, 229, 0.15)' : val >= 40 ? 'rgba(245, 158, 11, 0.15)' : 'rgba(239, 68, 68, 0.15)', fontSize: 12 }}>
                      <span style={{ color: '#64748b', textTransform: 'capitalize' }}>{key}:</span>
                      <span style={{ fontWeight: 700, marginLeft: 4, color: val >= 80 ? '#10b981' : val >= 60 ? '#1E88E5' : val >= 40 ? '#f59e0b' : '#ef4444' }}>{val}/100</span>
                    </div>
                  ))}
                </div>
              )}
              {userPerformance.recommendations && userPerformance.recommendations.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {userPerformance.recommendations.slice(0, 3).map((rec, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ArrowRight size={10} style={{ color: '#1E88E5', flexShrink: 0 }} />
                      {typeof rec === 'string' ? rec : rec.text || rec.description}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Company Health Score Banner (when viewing all users) */}
      {selectedUser === 'all' && healthScore && (
        <Card style={{ padding: 20, marginBottom: 24, background: darkMode ? '#1e293b' : '#fff', border: `2px solid ${healthScore.color === 'green' ? '#10b981' : healthScore.color === 'blue' ? '#1E88E5' : healthScore.color === 'yellow' ? '#f59e0b' : healthScore.color === 'orange' ? '#f97316' : '#ef4444'}30` }}>
          <div className="kubi-score-layout" style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            <ProgressRing progress={healthScore.score} size={100} strokeWidth={10} color={healthScore.color === 'green' ? '#10b981' : healthScore.color === 'blue' ? '#1E88E5' : healthScore.color === 'yellow' ? '#f59e0b' : healthScore.color === 'orange' ? '#f97316' : '#ef4444'}>
              <div style={{ fontSize: 32, fontWeight: 800, color: healthScore.color === 'green' ? '#10b981' : healthScore.color === 'blue' ? '#1E88E5' : healthScore.color === 'yellow' ? '#f59e0b' : healthScore.color === 'orange' ? '#f97316' : '#ef4444' }}>{healthScore.score}</div>
            </ProgressRing>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Company Health Score</h3>
                <Badge variant={healthScore.score >= 60 ? 'success' : healthScore.score >= 40 ? 'warning' : 'danger'}>{healthScore.status}</Badge>
                {trajectory && (
                  <Badge variant={trajectory.direction === 'strong_growth' || trajectory.direction === 'growing' ? 'success' : trajectory.direction === 'declining' || trajectory.direction === 'slowing' ? 'warning' : 'secondary'}>
                    {trajectory.direction === 'strong_growth' ? 'Strong Growth' : trajectory.direction === 'growing' ? 'Growing' : trajectory.direction === 'declining' ? 'Declining' : trajectory.direction === 'slowing' ? 'Slowing' : 'Stable'}
                    {trajectory.direction === 'strong_growth' || trajectory.direction === 'growing' ? ' ↑' : trajectory.direction === 'declining' || trajectory.direction === 'slowing' ? ' ↓' : ' →'}
                  </Badge>
                )}
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 14, color: '#64748b' }}>
                {healthScore.score >= 80 ? 'Outstanding performance across all key metrics. Keep up the excellent work!' :
                 healthScore.score >= 60 ? 'Good overall performance with room for improvement in some areas.' :
                 healthScore.score >= 40 ? 'Some areas need attention. Review the insights below for recommendations.' :
                 'Multiple areas require immediate attention. Focus on pipeline activity and conversion rates.'}
              </p>
              {healthScore.factors && healthScore.factors.length > 0 && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {healthScore.factors.map((factor, i) => (
                    <div key={i} style={{ padding: '4px 10px', borderRadius: 6, background: factor.score === 'A' ? 'rgba(16, 185, 129, 0.15)' : factor.score === 'B' ? 'rgba(30, 136, 229, 0.15)' : factor.score === 'D' || factor.score === 'F' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(100, 116, 139, 0.15)', fontSize: 12 }}>
                      <span style={{ color: '#64748b' }}>{factor.factor}:</span>
                      <span style={{ fontWeight: 700, marginLeft: 4, color: factor.score === 'A' ? '#10b981' : factor.score === 'B' ? '#1E88E5' : factor.score === 'D' || factor.score === 'F' ? '#ef4444' : '#64748b' }}>{factor.score}</span>
                      <span style={{ color: factor.impact?.startsWith('+') ? '#10b981' : '#ef4444', marginLeft: 4, fontSize: 11 }}>{factor.impact}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {revenueForecast && revenueForecast.annualizedProjection > 0 && (
              <div style={{ textAlign: 'right', borderLeft: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, paddingLeft: 24 }}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Projected Annual Revenue</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#10b981' }}>{formatCurrency(revenueForecast.annualizedProjection)}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>YTD: {formatCurrency(revenueForecast.ytdRevenue)} ({revenueForecast.ytdPlacements} placements)</div>
              </div>
            )}
          </div>
        </Card>
      )}

      <div className="kubi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard icon={Search} label="Total Searches" value={stats.totalSearches} color="#1E88E5" darkMode={darkMode} />
        <StatCard icon={Send} label="Submittals" value={stats.submittals} color="#42A5F5" darkMode={darkMode} trend={trajectory?.details?.sendouts?.change ? parseFloat(trajectory.details.sendouts.change) : null} />
        <StatCard icon={Calendar} label="Interviews" value={stats.interviews} color="#f59e0b" darkMode={darkMode} trend={trajectory?.details?.interviews?.change ? parseFloat(trajectory.details.interviews.change) : null} />
        <StatCard icon={Trophy} label="Placements" value={stats.placements} gradient="linear-gradient(135deg, #10b981, #059669)" trend={trajectory?.details?.placements?.change ? parseFloat(trajectory.details.placements.change) : null} />
      </div>

      <div className="kubi-grid-3-wide" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: 24, marginBottom: 24 }}>
        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}><h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Potential Revenue</h3><Badge variant="primary">Pipeline</Badge></div>
          <div style={{ fontSize: 36, fontWeight: 800, color: '#1E88E5', marginBottom: 8 }}>{formatCurrency(stats.potentialRevenue)}</div>
          <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>From {stats.activePositions} active positions</p>
        </Card>

        <Card style={{ padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}><h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Actual Revenue</h3><Badge variant="success">Realized</Badge></div>
          <div style={{ fontSize: 36, fontWeight: 800, color: '#10b981', marginBottom: 8 }}>{formatCurrency(stats.actualRevenue)}</div>
          <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>From {stats.placements} placements</p>
        </Card>

        <Card style={{ padding: 24 }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>Pipeline Funnel</h3>
          {funnel && funnel.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {funnel.map((stage) => {
                const maxCount = Math.max(...funnel.map(s => s.count)) || 1;
                const width = (stage.count / maxCount) * 100;
                const colors = { 'Active Positions': '#1E88E5', 'Submittals': '#42A5F5', 'Interviews': '#f59e0b', 'Placements': '#10b981' };
                const stageColor = colors[stage.stage] || stage.color || '#64748b';
                return (
                  <div key={stage.stage}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{stage.stage}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: stageColor }}>{stage.count}</span>
                    </div>
                    <div style={{ height: 24, background: darkMode ? '#334155' : '#e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${Math.max(width, 5)}%`, background: stageColor, borderRadius: 6 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <EmptyState icon={BarChart3} title="No pipeline data" description="Connect your ATS to see your pipeline funnel" />}
        </Card>
      </div>

      <div className="kubi-grid-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24, marginBottom: 24 }}>
        <Card style={{ padding: 24 }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>Weekly Activity</h3>
          {weeklyActivity && weeklyActivity.length > 0 ? (
            <>
              <MiniChart data={weeklyActivity.map(w => (w.sendouts || 0) + (w.interviews || 0) + (w.placements || 0))} color="#1E88E5" height={80} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, gap: 8 }}>
                {weeklyActivity.slice(-5).map((w, i) => (
                  <div key={i} style={{ textAlign: 'center', flex: 1 }}>
                    <div style={{ fontSize: 10, color: '#64748b' }}>{new Date(w.week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    <div style={{ fontSize: 11, marginTop: 4 }}>
                      <span style={{ color: '#1E88E5' }}>{w.sendouts || 0}</span>/
                      <span style={{ color: '#f59e0b' }}>{w.interviews || 0}</span>/
                      <span style={{ color: '#10b981' }}>{w.placements || 0}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: '#64748b' }}>
                <span><span style={{ color: '#1E88E5' }}>●</span> Submits</span>
                <span><span style={{ color: '#f59e0b' }}>●</span> Interviews</span>
                <span><span style={{ color: '#10b981' }}>●</span> Placements</span>
              </div>
            </>
          ) : <EmptyState icon={Activity} title="No activity" description="Activity will appear as you use your connected ATS" />}
        </Card>

        <Card style={{ padding: 24 }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>Performance</h3>
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
            <div style={{ textAlign: 'center' }}>
              <ProgressRing progress={parseFloat(ratios?.fillRate) || 0} size={90} color="#10b981" />
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>Fill Rate</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>{ratios?.fillRate || '0%'}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 42, fontWeight: 800, color: '#1E88E5' }}>{ratios?.avgDaysToFill || '-'}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>Avg Days to Fill</div>
            </div>
          </div>
        </Card>

        <Card style={{ padding: 24 }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>Key Ratios</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'Submit → Interview', value: ratios?.submittalToInterview || (stats.submittals > 0 && stats.interviews > 0 ? `${(stats.submittals / stats.interviews).toFixed(1)}:1` : '-'), color: '#42A5F5' },
              { label: 'Interview → Placement', value: ratios?.interviewToPlacement || (stats.interviews > 0 && stats.placements > 0 ? `${(stats.interviews / stats.placements).toFixed(1)}:1` : '-'), color: '#f59e0b' },
              { label: 'Submit → Placement', value: stats.submittals > 0 && stats.placements > 0 ? `${(stats.submittals / stats.placements).toFixed(1)}:1` : '-', color: '#10b981' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>{r.label}</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: r.color }}>{r.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* AI Insights Section */}
      {insights && insights.length > 0 && (
        <Card style={{ padding: 24, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #1E88E5, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Brain size={22} style={{ color: '#fff' }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>AI Insights & Recommendations</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
                  {selectedUser !== 'all'
                    ? `Personalized analysis for ${selectedUser}'s performance`
                    : 'Intelligent analysis of your recruiting performance'}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[...new Set(insights.map(i => i.category))].slice(0, 5).map(cat => (
                <Badge key={cat} variant="secondary" style={{ textTransform: 'capitalize' }}>{cat}</Badge>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: insights.length > 3 ? '1fr 1fr' : '1fr', gap: 12 }}>
            {(expandedInsights ? insights : insights.slice(0, 8)).map((insight, i) => {
              const typeColors = { warning: '#f59e0b', success: '#10b981', info: '#1E88E5', alert: '#ef4444' };
              const typeIcons = { warning: AlertTriangle, success: CheckCircle, info: Lightbulb, alert: AlertCircle };
              const categoryIcons = { trend: TrendingUp, revenue: DollarSign, team: Users, pipeline: Activity, activity: Zap, velocity: Zap, forecast: BarChart3, efficiency: Clock };
              const IconComponent = categoryIcons[insight.category] || typeIcons[insight.type] || Lightbulb;
              const color = typeColors[insight.type] || '#1E88E5';
              return (
                <div key={i} style={{
                  padding: 16,
                  borderRadius: 12,
                  background: `${color}08`,
                  border: `1px solid ${color}25`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: `${color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <IconComponent size={18} style={{ color }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{insight.title}</div>
                      {insight.metric && (
                        <span style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color,
                          background: `${color}15`,
                          padding: '2px 8px',
                          borderRadius: 6
                        }}>{insight.metric}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{insight.description}</div>
                    {insight.action && (
                      <div style={{
                        marginTop: 8,
                        fontSize: 12,
                        color,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4
                      }}>
                        <ArrowRight size={12} />
                        {insight.action}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {insights.length > 8 && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <button
                onClick={() => setExpandedInsights(!expandedInsights)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1E88E5',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  margin: '0 auto'
                }}
              >
                {expandedInsights ? (
                  <>
                    <ChevronUp size={16} />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown size={16} />
                    View all {insights.length} insights
                  </>
                )}
              </button>
            </div>
          )}
        </Card>
      )}

      {/* Kubi Sourcing Analytics */}
      <Card style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #1565C0, #1E88E5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles size={22} style={{ color: '#fff' }} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Roxi Sourcing</h3>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>External candidates sourced via kNine dataset with Roxi AI</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
          <div style={{ padding: 16, borderRadius: 10, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Searches</span>
              <Search size={14} style={{ color: '#1E88E5' }} />
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1E88E5' }}>{knineStats.searches || 0}</div>
          </div>
          <div style={{ padding: 16, borderRadius: 10, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Records Sourced</span>
              <Database size={14} style={{ color: '#1E88E5' }} />
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#1E88E5' }}>{knineStats.enrichments || 0}</div>
          </div>
          <div style={{ padding: 16, borderRadius: 10, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Submitted to ATS</span>
              <Send size={14} style={{ color: '#f59e0b' }} />
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#f59e0b' }}>{knineStats.submitted || 0}</div>
          </div>
          <div style={{ padding: 16, borderRadius: 10, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Interviewing</span>
              <Calendar size={14} style={{ color: '#f97316' }} />
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#f97316' }}>{knineStats.interviewing || 0}</div>
          </div>
          <div style={{ padding: 16, borderRadius: 10, background: darkMode ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Placed</span>
              <Trophy size={14} style={{ color: '#10b981' }} />
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#10b981' }}>{knineStats.placed || 0}</div>
          </div>
        </div>
      </Card>

      {/* Cooper BD Analytics - shown when user has Cooper access */}
      {cooperStats && (
        <Card style={{ padding: 24, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #f59e0b, #f97316)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Target size={22} style={{ color: '#fff' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Cooper BD Pipeline</h3>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Business development activity powered by Cooper AI</p>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <Button variant="ghost" size="sm" icon={Target} onClick={() => navigate('/cooper')}>Open Cooper</Button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
            <div style={{ padding: 16, borderRadius: 10, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Searches</span>
                <Search size={14} style={{ color: '#f59e0b' }} />
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#f59e0b' }}>{cooperStats.searches || 0}</div>
            </div>
            <div style={{ padding: 16, borderRadius: 10, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Prospects</span>
                <Building2 size={14} style={{ color: '#f97316' }} />
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#f97316' }}>{cooperStats.prospects || 0}</div>
            </div>
            <div style={{ padding: 16, borderRadius: 10, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Contacts Found</span>
                <Users size={14} style={{ color: '#1E88E5' }} />
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#1E88E5' }}>{cooperStats.contactLookups || 0}</div>
            </div>
            <div style={{ padding: 16, borderRadius: 10, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Activities</span>
                <Activity size={14} style={{ color: '#3b82f6' }} />
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#3b82f6' }}>{cooperStats.activities || 0}</div>
            </div>
            <div style={{ padding: 16, borderRadius: 10, background: darkMode ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Won</span>
                <Trophy size={14} style={{ color: '#10b981' }} />
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#10b981' }}>{cooperStats.pipeline?.won || 0}</div>
            </div>
          </div>
        </Card>
      )}

      {/* Open Positions */}
      {openPositions.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Briefcase size={18} style={{ color: '#1E88E5' }} />
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Open Positions</h3>
              <span style={{ fontSize: 12, color: '#64748b', background: darkMode ? '#1e293b' : '#f1f5f9', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{openPositions.length}</span>
            </div>
            <Button variant="ghost" size="sm" icon={Target} onClick={() => navigate('/yard')}>View Yard</Button>
          </div>
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {openPositions.slice(0, 20).map((p, i) => {
              const pColor = getPositionColor(p);
              return (
                <div
                  key={p.id || i}
                  onClick={() => setSelectedAnalyticsPosition(p)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px',
                    borderBottom: i < Math.min(openPositions.length, 20) - 1 ? `1px solid ${darkMode ? '#1e293b' : '#f1f5f9'}` : 'none',
                    cursor: 'pointer', transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = darkMode ? 'rgba(99,102,241,0.05)' : 'rgba(99,102,241,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ width: 6, height: 36, borderRadius: 3, background: pColor, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{p.title || p.JobTitle}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {p.company}{p.location ? ` • ${p.location}` : ''}{p.Owner ? ` • ${p.Owner}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexShrink: 0 }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#10b981' }}>{formatCurrency(p.fee)}</div>
                      <div style={{ fontSize: 10, color: '#64748b' }}>Fee</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{p.daysOpen || 0}</div>
                      <div style={{ fontSize: 10, color: '#64748b' }}>Days</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{p.candidates || 0}</div>
                      <div style={{ fontSize: 10, color: '#64748b' }}>Cands</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: pColor }}>{p.progress || 0}%</div>
                      <div style={{ fontSize: 10, color: '#64748b' }}>Close</div>
                    </div>
                    <ChevronRight size={16} style={{ color: '#94a3b8' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Position Detail Modal from Analytics */}
      {selectedAnalyticsPosition && (
        <ModalOverlay onClose={() => setSelectedAnalyticsPosition(null)}>
          <Card className="kubi-modal-card" style={{ width: 560 }}>
            <div style={{ padding: 24, background: `linear-gradient(135deg, ${getPositionColor(selectedAnalyticsPosition)}20, ${getPositionColor(selectedAnalyticsPosition)}05)`, borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: `${getPositionColor(selectedAnalyticsPosition)}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Briefcase size={24} style={{ color: getPositionColor(selectedAnalyticsPosition) }} />
                  </div>
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700 }}>{selectedAnalyticsPosition.title || selectedAnalyticsPosition.JobTitle}</h2>
                    <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>{selectedAnalyticsPosition.company}</p>
                    {selectedAnalyticsPosition.location && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={12} /> {selectedAnalyticsPosition.location}</p>}
                  </div>
                </div>
                <button onClick={() => setSelectedAnalyticsPosition(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}><X size={20} style={{ color: '#64748b' }} /></button>
              </div>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Potential Fee</div><div style={{ fontSize: 20, fontWeight: 800, color: '#10b981' }}>{formatCurrency(selectedAnalyticsPosition.fee)}</div></div>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Days Open</div><div style={{ fontSize: 20, fontWeight: 800 }}>{selectedAnalyticsPosition.daysOpen || 0}</div></div>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Close Probability</div><div style={{ fontSize: 20, fontWeight: 800, color: getPositionColor(selectedAnalyticsPosition) }}>{selectedAnalyticsPosition.progress || 0}%</div></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Candidates</div><div style={{ fontSize: 20, fontWeight: 800 }}>{selectedAnalyticsPosition.candidates || 0}</div></div>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Interviews</div><div style={{ fontSize: 20, fontWeight: 800 }}>{selectedAnalyticsPosition.interviews || 0}</div></div>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Days Inactive</div><div style={{ fontSize: 20, fontWeight: 800, color: (selectedAnalyticsPosition.daysSinceLastActivity || 0) > 14 ? '#ef4444' : (selectedAnalyticsPosition.daysSinceLastActivity || 0) > 7 ? '#f59e0b' : '#64748b' }}>{selectedAnalyticsPosition.daysSinceLastActivity || 0}</div></div>
              </div>
              {selectedAnalyticsPosition.Owner && (
                <div style={{ padding: 12, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: `hsl(${(selectedAnalyticsPosition.Owner?.charCodeAt(0) || 0) * 15}, 55%, 50%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12 }}>{(selectedAnalyticsPosition.Owner || 'U').slice(0, 2).toUpperCase()}</div>
                  <div><div style={{ fontSize: 11, color: '#64748b' }}>Assigned To</div><div style={{ fontSize: 14, fontWeight: 600 }}>{selectedAnalyticsPosition.Owner}</div></div>
                </div>
              )}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span style={{ fontSize: 14, fontWeight: 600 }}>Pipeline Progress</span><span style={{ fontSize: 14, fontWeight: 700, color: getPositionColor(selectedAnalyticsPosition) }}>{selectedAnalyticsPosition.progress || 0}%</span></div>
                <div style={{ height: 8, background: darkMode ? '#334155' : '#e5e7eb', borderRadius: 4 }}><div style={{ height: '100%', width: `${selectedAnalyticsPosition.progress || 0}%`, background: getPositionColor(selectedAnalyticsPosition), borderRadius: 4, transition: 'width 0.3s' }} /></div>
              </div>
              <Button variant="primary" style={{ width: '100%' }} icon={Search} onClick={() => { setSelectedAnalyticsPosition(null); handleFindCandidatesFromAnalytics(selectedAnalyticsPosition); }}>Find Candidates</Button>
            </div>
          </Card>
        </ModalOverlay>
      )}

      {/* Team Leaderboard */}
      <Card>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Team Leaderboard</h3>
        </div>
        {leaderboard && leaderboard.length > 0 ? (
          <div>
            {leaderboard.slice(0, 10).map((m, i) => (
              <div key={m.userId || i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: i < Math.min(leaderboard.length, 10) - 1 ? `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` : 'none' }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: i === 0 ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' : i === 1 ? 'linear-gradient(135deg, #94a3b8, #64748b)' : i === 2 ? 'linear-gradient(135deg, #d97706, #b45309)' : (darkMode ? '#334155' : '#e5e7eb'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: i < 3 ? '#fff' : '#64748b' }}>{i + 1}</div>
                <ProfileAvatar person={{ name: m.userName }} size="sm" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{m.userName || 'Team Member'}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>{m.submittals || 0} submittals • {m.interviews || 0} interviews</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#10b981' }}>{m.placements || 0}</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>placements</div>
                </div>
                {m.revenue > 0 && (
                  <div style={{ textAlign: 'right', marginLeft: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1E88E5' }}>{formatCurrency(m.revenue)}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>revenue</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : <EmptyState icon={Users} title="No team data" description="Activity data will appear as your team uses their connected ATS" />}
      </Card>
    </div>
  );
};

// ==================== THE YARD - REAL POSITIONS ====================
const YardPage = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const { setSearchFromPosition } = useSearchContext();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const selectDog = useYardStore(state => state.selectDog);
  const deselectDog = useYardStore(state => state.deselectDog);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('3d'); // '2d' or '3d'
  const [isSampleData, setIsSampleData] = useState(false);

  useEffect(() => { loadPositions(); }, []);

  const loadPositions = async () => {
    setLoading(true);
    try {
      // Load positions — try PCR analytics first, fall back to generic multi-ATS
      let response = await apiCall('/api/pcr/analytics/positions?limit=100').catch(() => null);
      if (!response || response.connected === false) {
        response = await apiCall('/api/positions').catch(() => ({ positions: [] }));
      }
      const list = response.positions || response.data || response || [];
      console.log('Loaded positions:', list.length, list);

      // If no real positions, fall back to sample data
      if (!Array.isArray(list) || list.length === 0) {
        const mapped = SAMPLE_ANALYTICS.openPositions.map(p => ({
          id: p.JobId, JobId: p.JobId,
          title: p.title, company: p.company,
          fee: p.salary * 0.2, value: p.salary * 0.2,
          stage: p.progress >= 70 ? 'hot' : p.progress >= 45 ? 'active' : p.status === 'pending' ? 'cold' : 'active',
          healthState: 'active', healthScore: p.progress,
          daysOpen: Math.floor(Math.random() * 45) + 5,
          daysSinceLastActivity: Math.floor(Math.random() * 10),
          lastActivityDate: new Date().toISOString(),
          candidates: Math.floor(p.progress / 10), interviews: Math.floor(p.progress / 20),
          progress: p.progress, closeProbability: p.progress / 100,
          City: p.City, State: p.State, location: [p.City, p.State].filter(Boolean).join(', '),
          owner: 'Demo User', type: p.type,
        }));
        setPositions(mapped);
        setIsSampleData(true);
        setLoading(false);
        return;
      }

      setIsSampleData(false);
      const mapped = (Array.isArray(list) ? list : []).map(p => ({
        // Core identifiers
        id: p.JobId || p.id || p.PositionId,
        JobId: p.JobId,
        // Display fields
        title: p.title || p.JobTitle || 'Untitled Position',
        company: p.company || p.CompanyName || '',
        // Fees - use the API value or default
        fee: p.fee || p.value || p.Fee || 15000,
        value: p.value || p.fee || 15000,
        // Health state from API (hot/stale/cold)
        stage: mapHealthState(p.healthState || p.stage),
        healthState: p.healthState || 'active',
        healthScore: p.healthScore || 50,
        // Activity tracking
        daysOpen: p.daysSincePosted || calcDays(p.datePosted || p.DatePosted),
        daysSinceLastActivity: p.daysSinceLastActivity || 0,
        lastActivityDate: p.lastActivity || p.LastActivity || p.lastActivityDate || new Date().toISOString(),
        // Pipeline metrics
        candidates: p.candidateCount || p.candidates || 0,
        interviews: p.interviewCount || p.interviews || 0,
        progress: p.healthScore || calcProgress(p),
        closeProbability: p.closeProbability || (p.healthScore ? p.healthScore / 100 : 0.5),
        // Location
        City: p.City || '',
        State: p.State || '',
        location: p.location || [p.City, p.State].filter(Boolean).join(', '),
        // Ownership
        owner: p.owner || p.UserName || 'Unassigned',
        // Spread remaining fields
        ...p
      }));
      setPositions(mapped);
    } catch (err) {
      console.error('Failed to load positions:', err);
      setPositions([]);
    }
    setLoading(false);
  };

  // Map PCR health state to yard display state
  const mapHealthState = (state) => {
    if (!state) return 'active';
    const s = state.toLowerCase();
    if (s === 'hot' || s === 'active') return 'hot';
    if (s === 'stale' || s === 'aging') return 'active';
    if (s === 'cold') return 'cold';
    return 'active';
  };

  const mapStatus = (s) => {
    if (!s) return 'active';
    const l = s.toLowerCase();
    if (l.includes('hot') || l.includes('urgent')) return 'hot';
    if (l.includes('offer') || l.includes('final')) return 'offer';
    if (l.includes('cold') || l.includes('hold')) return 'cold';
    if (l.includes('sleep') || l.includes('inactive') || l.includes('closed')) return 'sleeping';
    if (l === 'available' || l === 'open') return 'active';
    return 'active';
  };

  const calcDays = (d) => d ? Math.floor((new Date() - new Date(d)) / (1000 * 60 * 60 * 24)) : 0;
  const calcProgress = (p) => {
    const s = (p.Status || p.status || '').toLowerCase();
    if (s.includes('placed') || s.includes('closed')) return 100;
    if (s.includes('offer')) return 90;
    if (s.includes('final')) return 80;
    if (s.includes('interview')) return 60;
    if (s.includes('submit')) return 40;
    return 20;
  };

  const filtered = filter === 'all' ? positions : positions.filter(p => p.stage === filter);

  const getColor = (stage) => ({ hot: '#ef4444', active: '#10b981', offer: '#1E88E5', cold: '#64748b', sleeping: '#374151' }[stage] || '#64748b');
  const getGlow = (stage) => stage === 'hot' ? '0 0 30px rgba(239, 68, 68, 0.4)' : stage === 'offer' ? '0 0 30px rgba(30, 136, 229, 0.4)' : 'none';
  const getDogSize = (fee) => {
    const f = Number(fee) || 0;
    if (f >= 100000) return { w: 100, h: 100, f: 50 };
    if (f >= 70000) return { w: 85, h: 85, f: 42 };
    if (f >= 50000) return { w: 72, h: 72, f: 36 };
    if (f >= 35000) return { w: 60, h: 60, f: 30 };
    return { w: 50, h: 50, f: 25 };
  };

  const handleFindCandidates = (p) => {
    // Store position data in localStorage for EnhancedSearch to pick up
    const searchData = {
      title: p.title || p.JobTitle || '',
      city: p.City || '',
      state: p.State || '',
      description: p.description || p.Description || '',
      company: p.company || '',
      timestamp: Date.now()
    };
    localStorage.setItem('kubi_searchFromPosition', JSON.stringify(searchData));
    navigate('/roxi');
  };

  const totalFees = positions.filter(p => p.stage !== 'sleeping').reduce((s, p) => s + (Number(p.fee) || 0), 0);
  const hotDeals = positions.filter(p => p.stage === 'hot').length;
  const nearPlacement = positions.filter(p => p.stage === 'offer').length;

  if (loading) return <LoadingSpinner text="Loading positions..." />;

  return (
    <div>
      {/* Sample data banner */}
      {isSampleData && (
        <div style={{ marginBottom: 20, borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(99,102,241,0.3)', background: darkMode ? 'rgba(99,102,241,0.08)' : 'rgba(99,102,241,0.04)' }}>
          <div style={{ padding: '10px 16px', background: 'linear-gradient(135deg, #1565C0, #1E88E5)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>🐕</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>SAMPLE DATA — Preview Mode</span>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: darkMode ? '#e2e8f0' : '#1e293b', marginBottom: 4 }}>No ATS or CRM connected — these are example deals</div>
              <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                Connect your ATS/CRM to bring the Yard to life with your active deals and pipeline.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <Link to="/settings/integrations" style={{ textDecoration: 'none' }}>
                <Button variant="secondary" size="sm">Set Up Integration</Button>
              </Link>
            </div>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 800 }}>The Yard{isSampleData ? ' (Sample)' : ''}</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Visual pipeline - each dog represents a position</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {/* View mode toggle */}
          <div style={{ display: 'flex', background: darkMode ? '#1e293b' : '#f1f5f9', borderRadius: 10, padding: 4 }}>
            <button onClick={() => setViewMode('2d')} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: viewMode === '2d' ? (darkMode ? '#334155' : '#fff') : 'transparent', color: viewMode === '2d' ? (darkMode ? '#f8fafc' : '#1e293b') : '#64748b', boxShadow: viewMode === '2d' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.2s' }}>2D</button>
            <button onClick={() => setViewMode('3d')} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: viewMode === '3d' ? 'linear-gradient(135deg, #1E88E5, #1565C0)' : 'transparent', color: viewMode === '3d' ? '#fff' : '#64748b', boxShadow: viewMode === '3d' ? '0 2px 8px rgba(30, 136, 229, 0.3)' : 'none', transition: 'all 0.2s' }}>3D</button>
          </div>
          <Button variant="secondary" icon={RefreshCw} onClick={loadPositions}>Refresh</Button>
          <Button variant="primary" icon={Plus}>Add Position</Button>
        </div>
      </div>

      <div className="kubi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <Card style={{ padding: 20 }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><span style={{ fontSize: 32 }}>🐕</span><div><div style={{ fontSize: 28, fontWeight: 800 }}>{positions.length}</div><div style={{ fontSize: 13, color: '#64748b' }}>Active Positions</div></div></div></Card>
        <Card style={{ padding: 20 }} gradient="linear-gradient(135deg, #10b981, #059669)"><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><span style={{ fontSize: 32 }}>💰</span><div><div style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>{formatCurrency(totalFees)}</div><div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)' }}>Potential Fees</div></div></div></Card>
        <Card style={{ padding: 20, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><span style={{ fontSize: 32 }}>🔥</span><div><div style={{ fontSize: 28, fontWeight: 800, color: '#ef4444' }}>{hotDeals}</div><div style={{ fontSize: 13, color: '#64748b' }}>Hot Deals</div></div></div></Card>
        <Card style={{ padding: 20, background: 'rgba(30, 136, 229, 0.1)', border: '1px solid rgba(30, 136, 229, 0.2)' }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><span style={{ fontSize: 32 }}>🎯</span><div><div style={{ fontSize: 28, fontWeight: 800, color: '#1E88E5' }}>{nearPlacement}</div><div style={{ fontSize: 13, color: '#64748b' }}>Near Placement</div></div></div></Card>
      </div>

      {/* Filter pills - only show in 2D mode */}
      {viewMode === '2d' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[{ id: 'all', label: 'All', count: positions.length }, { id: 'hot', label: '🔥 Hot', count: positions.filter(p => p.stage === 'hot').length }, { id: 'active', label: '✅ Active', count: positions.filter(p => p.stage === 'active').length }, { id: 'offer', label: '🎯 Offer', count: positions.filter(p => p.stage === 'offer').length }, { id: 'cold', label: '❄️ Cold', count: positions.filter(p => p.stage === 'cold').length }, { id: 'sleeping', label: '😴 Sleeping', count: positions.filter(p => p.stage === 'sleeping').length }].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 20, border: filter === f.id ? 'none' : `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: filter === f.id ? '#1E88E5' : 'transparent', color: filter === f.id ? '#fff' : '#64748b', fontSize: 14, cursor: 'pointer' }}>{f.label}<span style={{ padding: '2px 8px', borderRadius: 10, background: filter === f.id ? 'rgba(255,255,255,0.2)' : (darkMode ? '#334155' : '#e5e7eb'), fontSize: 12 }}>{f.count}</span></button>
          ))}
        </div>
      )}

      {/* 3D View */}
      {viewMode === '3d' ? (
        <div style={{ height: 600, borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
          <Yard3D
            deals={positions.map(p => ({
              ...p,
              value: p.fee,
              closeProbability: p.progress / 100,
              lastActivityDate: p.DateOpened || new Date().toISOString(),
              owner: p.Owner || p.assignedTo || 'Unassigned'
            }))}
            darkMode={darkMode}
            onDogSelect={(deal) => { setSelectedPosition(deal); selectDog(deal.id || deal.JobId); }}
          />
        </div>
      ) : (
      <Card style={{ padding: 32, minHeight: 400, background: darkMode ? 'linear-gradient(180deg, #0f172a, #1e293b)' : 'linear-gradient(180deg, #f8fafc, #e2e8f0)', position: 'relative' }}>
        {filtered.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, justifyContent: 'center' }}>
            {filtered.map((p) => {
              const size = getDogSize(p.fee);
              const color = getColor(p.stage);
              const glow = getGlow(p.stage);
              const isSleeping = p.stage === 'sleeping';

              return (
                <div key={p.id} onClick={() => setSelectedPosition(p)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <div style={{ width: size.w, height: size.h, borderRadius: '50%', background: `linear-gradient(135deg, ${color}30, ${color}10)`, border: `3px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: glow, position: 'relative', opacity: isSleeping ? 0.5 : 1 }}>
                    <span style={{ fontSize: size.f, filter: isSleeping ? 'grayscale(0.5)' : 'none' }}>🐕</span>
                    {isSleeping && <span style={{ position: 'absolute', top: -5, right: -5, fontSize: 16 }}>💤</span>}
                    {p.stage === 'hot' && <span style={{ position: 'absolute', top: -8, right: -8, fontSize: 18 }}>🔥</span>}
                    {p.stage === 'offer' && <span style={{ position: 'absolute', top: -8, right: -8, fontSize: 18 }}>🎯</span>}
                  </div>
                  <div style={{ marginTop: 8, textAlign: 'center', maxWidth: 120 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.title.length > 15 ? p.title.substring(0, 15) + '...' : p.title}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color }}>{formatCurrency(p.fee)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : <EmptyState icon={Target} title="No positions" description={filter === 'all' ? "Add your first position" : "No positions match this filter"} action={filter !== 'all' && <Button variant="secondary" onClick={() => setFilter('all')}>Show All</Button>} />}

        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 32, paddingTop: 24, borderTop: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
          {[{ c: '#ef4444', l: 'Hot' }, { c: '#10b981', l: 'Active' }, { c: '#1E88E5', l: 'Offer' }, { c: '#64748b', l: 'Cold' }].map(i => (<div key={i.l} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b' }}><div style={{ width: 12, height: 12, borderRadius: '50%', background: i.c }} />{i.l}</div>))}
          <span style={{ fontSize: 12, color: '#64748b' }}>🐕 Size = Deal Value</span>
        </div>
      </Card>
      )}

      {selectedPosition && (
        <ModalOverlay onClose={() => { setSelectedPosition(null); deselectDog(); }}>
          <Card className="kubi-modal-card" style={{ width: 560 }}>
            <div style={{ padding: 24, background: `linear-gradient(135deg, ${getColor(selectedPosition.stage)}20, ${getColor(selectedPosition.stage)}05)`, borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span style={{ fontSize: 48 }}>🐕</span>
                  <div>
                    <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700 }}>{selectedPosition.title}</h2>
                    <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>{selectedPosition.company}</p>
                    {selectedPosition.location && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#94a3b8' }}>📍 {selectedPosition.location}</p>}
                  </div>
                </div>
                <button onClick={() => { setSelectedPosition(null); deselectDog(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}><X size={20} style={{ color: '#64748b' }} /></button>
              </div>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Potential Fee</div><div style={{ fontSize: 20, fontWeight: 800, color: '#10b981' }}>{formatCurrency(selectedPosition.fee)}</div></div>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Days Open</div><div style={{ fontSize: 20, fontWeight: 800 }}>{selectedPosition.daysOpen || 0}</div></div>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Close Probability</div><div style={{ fontSize: 20, fontWeight: 800, color: selectedPosition.progress >= 70 ? '#10b981' : selectedPosition.progress >= 40 ? '#f59e0b' : '#ef4444' }}>{selectedPosition.progress || 0}%</div></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Candidates</div><div style={{ fontSize: 20, fontWeight: 800 }}>{selectedPosition.candidates || 0}</div></div>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Interviews</div><div style={{ fontSize: 20, fontWeight: 800 }}>{selectedPosition.interviews || 0}</div></div>
                <div style={{ padding: 14, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10 }}><div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Days Inactive</div><div style={{ fontSize: 20, fontWeight: 800, color: (selectedPosition.daysSinceLastActivity || 0) > 14 ? '#ef4444' : (selectedPosition.daysSinceLastActivity || 0) > 7 ? '#f59e0b' : '#64748b' }}>{selectedPosition.daysSinceLastActivity || 0}</div></div>
              </div>
              {selectedPosition.Owner && (
                <div style={{ padding: 12, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 10, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: `hsl(${(selectedPosition.Owner?.charCodeAt(0) || 0) * 15}, 55%, 50%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12 }}>{(selectedPosition.Owner || 'U').slice(0, 2).toUpperCase()}</div>
                  <div><div style={{ fontSize: 11, color: '#64748b' }}>Assigned To</div><div style={{ fontSize: 14, fontWeight: 600 }}>{selectedPosition.Owner}</div></div>
                </div>
              )}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}><span style={{ fontSize: 14, fontWeight: 600 }}>Pipeline Progress</span><span style={{ fontSize: 14, fontWeight: 700, color: getColor(selectedPosition.stage) }}>{selectedPosition.progress || 0}%</span></div>
                <div style={{ height: 8, background: darkMode ? '#334155' : '#e5e7eb', borderRadius: 4 }}><div style={{ height: '100%', width: `${selectedPosition.progress || 0}%`, background: getColor(selectedPosition.stage), borderRadius: 4, transition: 'width 0.3s' }} /></div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <Button variant="primary" style={{ flex: 1 }} icon={Search} onClick={() => { setSelectedPosition(null); deselectDog(); handleFindCandidates(selectedPosition); }}>Find Candidates</Button>
              </div>
            </div>
          </Card>
        </ModalOverlay>
      )}
    </div>
  );
};

// ==================== SETTINGS PAGES ====================
const SettingsTeamPage = () => {
  const { darkMode } = useTheme();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');

  // kNine branded confirm modal
  const [knModal, setKnModal] = useState(null);
  const knModalResolveRef = useRef(null);
  const knConfirm = (message, opts = {}) => new Promise(resolve => {
    knModalResolveRef.current = resolve;
    setKnModal({ type: 'confirm', message, title: opts.title || 'kNine', ...opts });
  });
  const handleKnModalClose = (result) => {
    const resolve = knModalResolveRef.current;
    knModalResolveRef.current = null;
    setKnModal(null);
    if (resolve) resolve(result);
  };
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteRoxiAccess, setInviteRoxiAccess] = useState(true);
  const [inviteCooperAccess, setInviteCooperAccess] = useState(false);
  const [inviting, setInviting] = useState(false);
  const { credits } = useBilling();

  useEffect(() => {
    loadTeam();

    // Re-fetch when tab gets focus (covers users who were merged into a new org)
    const onFocus = () => loadTeam();
    window.addEventListener('focus', onFocus);

    // Also poll every 30s while the page is visible
    const interval = setInterval(() => {
      if (!document.hidden) loadTeam();
    }, 30000);

    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, []);

  const [orgPlan, setOrgPlan] = useState('free');
  const [orgCreditsRemaining, setOrgCreditsRemaining] = useState(0);

  const loadTeam = async () => {
    setLoading(true);
    try {
      const [membersData, invitesData] = await Promise.all([
        apiCall('/api/team/members').catch(() => null),
        apiCall('/api/team/invitations').catch(() => [])
      ]);
      if (membersData) {
        // API now returns { members, orgPlan, orgCreditsRemaining }
        const list = Array.isArray(membersData) ? membersData : membersData.members || [];
        setMembers(list);
        setOrgPlan(membersData.orgPlan || 'free');
        setOrgCreditsRemaining(membersData.orgCreditsRemaining || 0);
      } else {
        const user = getUser();
        if (user) setMembers([{ id: '1', name: user.name || 'You', email: user.email, role: 'owner', creditAllocationValue: 'unlimited', creditUsageThisPeriod: 0, roxiAccess: true, cooperAccess: true }]);
      }
      setInvitations(Array.isArray(invitesData) ? invitesData : []);
    } catch {
      const user = getUser();
      if (user) setMembers([{ id: '1', name: user.name || 'You', email: user.email, role: 'owner', creditAllocationValue: 'unlimited', creditUsageThisPeriod: 0, roxiAccess: true, cooperAccess: true }]);
    }
    setLoading(false);
  };

  const inviteMember = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      const result = await apiCall('/api/team/invite', { method: 'POST', body: JSON.stringify({ email: inviteEmail, role: inviteRole, name: inviteName, roxiAccess: inviteRoxiAccess, cooperAccess: inviteCooperAccess }) });
      if (result.invitation) {
        setInvitations(prev => [...prev, result.invitation]);
      }
    } catch (err) {
      console.error('Invite error:', err);
    }
    setShowInviteModal(false); setInviteEmail(''); setInviteName(''); setInviteRole('member'); setInviteRoxiAccess(true); setInviteCooperAccess(false); setInviting(false);
  };

  const cancelInvitation = async (inviteId) => {
    try {
      await apiCall(`/api/team/invitations/${inviteId}`, { method: 'DELETE' });
      setInvitations(prev => prev.filter(inv => inv.id !== inviteId));
    } catch {}
  };

  const resendInvitation = async (inviteId) => {
    try {
      await apiCall(`/api/team/invitations/${inviteId}/resend`, { method: 'POST' });
    } catch {}
  };

  const removeMember = async (id) => {
    const ok = await knConfirm('Remove this team member?', { title: 'Remove Member' });
    if (!ok) return;
    try { await apiCall(`/api/team/members/${id}`, { method: 'DELETE' }); } catch {}
    setMembers(prev => prev.filter(m => m.id !== id));
  };

  const [resetSentId, setResetSentId] = useState(null);
  const sendPasswordReset = async (member) => {
    try {
      await apiCall(`/api/team/members/${member.id}/send-reset`, { method: 'POST' });
      setResetSentId(member.id);
      setTimeout(() => setResetSentId(null), 3000);
    } catch {
      alert(`Failed to send reset email to ${member.email}`);
    }
  };

  const [savedMemberId, setSavedMemberId] = useState(null);

  const toggleAccess = async (memberId, accessType) => {
    const member = members.find(m => m.id === memberId);
    if (!member || member.role === 'owner') return;
    const newValue = !member[accessType];
    // Optimistic update
    setMembers(prev => prev.map(m => m.id === memberId ? { ...m, [accessType]: newValue } : m));
    try {
      await apiCall(`/api/team/members/${memberId}/access`, { method: 'PATCH', body: JSON.stringify({ [accessType]: newValue }) });
      setSavedMemberId(memberId);
      setTimeout(() => setSavedMemberId(null), 2000);
    } catch (err) {
      // Revert on failure
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, [accessType]: !newValue } : m));
    }
  };

  if (loading) return <LoadingSpinner text="Loading team..." />;

  return (
    <div style={{ maxWidth: 1000 }}>
      <div className="kubi-page-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, gap: 12 }}>
        <div><h1 className="kubi-page-title" style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 700 }}>Team Management</h1><p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Manage team members and AI access permissions</p></div>
        <Button variant="primary" icon={UserPlus} onClick={() => setShowInviteModal(true)}>Add Member</Button>
      </div>

      {orgPlan === 'free' && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.3)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: darkMode ? '#fbbf24' : '#92400e' }}>
            <strong>Trial plan:</strong> 50 credits shared across all team members. Upgrade to assign per-user credit limits.
          </span>
        </div>
      )}
      <div className="kubi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <Card style={{ padding: 20 }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(30, 136, 229, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Users size={22} style={{ color: '#1E88E5' }} /></div><div><div style={{ fontSize: 24, fontWeight: 700 }}>{members.length}{invitations.length > 0 && <span style={{ fontSize: 14, color: '#f59e0b', marginLeft: 4 }}>+{invitations.length}</span>}</div><div style={{ fontSize: 12, color: '#64748b' }}>Members{invitations.length > 0 && ' (pending)'}</div></div></div></Card>
        <Card style={{ padding: 20 }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(16, 185, 129, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Zap size={22} style={{ color: '#10b981' }} /></div><div><div style={{ fontSize: 24, fontWeight: 700 }}>{credits}</div><div style={{ fontSize: 12, color: '#64748b' }}>Org Credits</div></div></div></Card>
        <Card style={{ padding: 20 }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><div style={{ width: 44, height: 44, borderRadius: 10, background: 'linear-gradient(135deg, rgba(30, 136, 229, 0.2), rgba(30, 136, 229, 0.2))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Sparkles size={22} style={{ color: '#1E88E5' }} /></div><div><div style={{ fontSize: 24, fontWeight: 700 }}>{members.filter(m => (m.roxiAccess !== false && m.kubiAccess !== false)).length}</div><div style={{ fontSize: 12, color: '#64748b' }}>Roxi Users</div></div></div></Card>
        <Card style={{ padding: 20 }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><div style={{ width: 44, height: 44, borderRadius: 10, background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(239, 68, 68, 0.2))', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Target size={22} style={{ color: '#f59e0b' }} /></div><div><div style={{ fontSize: 24, fontWeight: 700 }}>{members.filter(m => (m.cooperAccess === true || m.spirkoAccess === true)).length}</div><div style={{ fontSize: 12, color: '#64748b' }}>Cooper Users</div></div></div></Card>
      </div>

      <Card>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Team Members</h3>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>Control which AI tools each member can access</p>
        </div>
        {members.length === 0 ? <EmptyState icon={Users} title="No team members" description="Invite your first team member" /> : (
          <div className="kubi-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead><tr style={{ background: darkMode ? '#0f172a' : '#f9fafb' }}>
              <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Member</th>
              <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Role</th>
              <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Sparkles size={14} style={{ color: '#1E88E5' }} />Roxi
                </div>
              </th>
              <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Target size={14} style={{ color: '#f59e0b' }} />Cooper
                </div>
              </th>
              <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Credits</th>
              <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Actions</th>
            </tr></thead>
            <tbody>
              {members.map((m, i) => (
                <tr key={m.id} style={{ borderBottom: i < members.length - 1 ? `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` : 'none' }}>
                  <td style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <ProfileAvatar person={m} size="sm" />
                      <div><div style={{ fontWeight: 600, marginBottom: 2 }}>{m.name || m.Name}</div><div style={{ fontSize: 12, color: '#64748b' }}>{m.email || m.Email}</div></div>
                    </div>
                  </td>
                  <td style={{ padding: '16px 20px' }}><Badge variant={m.role === 'owner' ? 'success' : m.role === 'admin' ? 'primary' : 'default'}>{m.role === 'owner' ? '👑 Owner' : m.role === 'admin' ? '🛡️ Admin' : 'Member'}</Badge></td>
                  <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                    {m.role === 'owner' ? (
                      <Badge variant="success">✓ Always</Badge>
                    ) : (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Toggle checked={m.kubiAccess !== false} onChange={() => toggleAccess(m.id, 'kubiAccess')} />
                        {savedMemberId === m.id && <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>Saved ✓</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                    {m.role === 'owner' ? (
                      <Badge variant="success">✓ Always</Badge>
                    ) : (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Toggle checked={m.cooperAccess === true || m.spirkoAccess === true} onChange={() => toggleAccess(m.id, 'cooperAccess')} />
                        {savedMemberId === m.id && <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>Saved ✓</span>}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    {m.role === 'owner' && orgPlan === 'free'
                      ? <Badge variant="default" title="Trial: 50 credits shared across the entire org">🔗 Shared 50</Badge>
                      : m.creditAllocationValue === 'unlimited' || m.role === 'owner'
                        ? <Badge variant="success">♾️ Unlimited</Badge>
                        : orgPlan === 'free'
                          ? <Badge variant="default" title="Trial: credits come from the shared org pool">🔗 Shared pool</Badge>
                          : <span style={{ fontWeight: 600 }}>{m.creditAllocationValue || 50}/mo</span>}
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                    {m.role !== 'owner' && (
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                        {resetSentId === m.id ? (
                          <span style={{ fontSize: 11, color: '#10b981', fontWeight: 600 }}>Reset sent ✓</span>
                        ) : (
                          <button onClick={() => sendPasswordReset(m)} title="Send password reset email" style={{ padding: '6px 10px', borderRadius: 6, background: 'rgba(30, 136, 229, 0.1)', border: 'none', cursor: 'pointer', color: '#1E88E5', fontSize: 11, fontWeight: 500 }}>Reset PW</button>
                        )}
                        <button onClick={() => removeMember(m.id)} style={{ padding: 8, borderRadius: 6, background: 'rgba(239, 68, 68, 0.1)', border: 'none', cursor: 'pointer', color: '#ef4444' }}><Trash2 size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </Card>

      {invitations.length > 0 && (
        <Card style={{ marginTop: 16 }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Mail size={18} style={{ color: '#f59e0b' }} />
              Pending Invitations
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#64748b' }}>These members have been invited but haven't accepted yet</p>
          </div>
          <div style={{ padding: 0 }}>
            {invitations.map((inv, i) => (
              <div key={inv.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '14px 20px',
                borderBottom: i < invitations.length - 1 ? `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` : 'none',
                background: darkMode ? 'rgba(245, 158, 11, 0.05)' : 'rgba(245, 158, 11, 0.03)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: darkMode ? '#334155' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Clock size={16} style={{ color: '#f59e0b' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{inv.email}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      Invited as {inv.role} • Expires {new Date(inv.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => resendInvitation(inv.id)} style={{ padding: '6px 12px', borderRadius: 6, background: 'rgba(30, 136, 229, 0.1)', border: 'none', cursor: 'pointer', color: '#1E88E5', fontSize: 12, fontWeight: 500 }}>Resend</button>
                  <button onClick={() => cancelInvitation(inv.id)} style={{ padding: '6px 12px', borderRadius: 6, background: 'rgba(239, 68, 68, 0.1)', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 12, fontWeight: 500 }}>Cancel</button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card style={{ marginTop: 16, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #1E88E5, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><Sparkles size={24} /></div>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, #f59e0b, #ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><Target size={24} /></div>
          </div>
          <div>
            <h4 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 600 }}>AI Access Controls</h4>
            <p style={{ margin: 0, fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
              <strong style={{ color: '#1E88E5' }}>Roxi</strong> is your AI recruiting assistant for candidate sourcing, search optimization, and pipeline insights.<br/>
              <strong style={{ color: '#f59e0b' }}>Cooper</strong> is your AI sales assistant for business development, company research, and lead generation (coming soon).
            </p>
          </div>
        </div>
      </Card>

      {showInviteModal && (
        <ModalOverlay onClose={() => setShowInviteModal(false)}>
          <Card style={{ width: 480, padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}><h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Add Team Member</h3><button onClick={() => setShowInviteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button></div>
            <div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Name</label><input type="text" value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="John Doe" style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, boxSizing: 'border-box' }} /></div>
            <div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Email</label><input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="colleague@company.com" style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, boxSizing: 'border-box' }} /></div>
            <div style={{ marginBottom: 20 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Role</label><select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14 }}><option value="member">Member</option><option value="admin">Admin</option></select></div>

            <div style={{ marginBottom: 24, padding: 16, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 12 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 12, textTransform: 'uppercase' }}>AI Access</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #1E88E5, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><Sparkles size={16} /></div>
                    <div><div style={{ fontWeight: 500, fontSize: 14 }}>Roxi Access</div><div style={{ fontSize: 11, color: '#64748b' }}>AI recruiting assistant</div></div>
                  </div>
                  <Toggle checked={inviteRoxiAccess} onChange={setInviteRoxiAccess} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #f59e0b, #ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}><Target size={16} /></div>
                    <div><div style={{ fontWeight: 500, fontSize: 14 }}>Cooper Access</div><div style={{ fontSize: 11, color: '#64748b' }}>AI sales assistant (coming soon)</div></div>
                  </div>
                  <Toggle checked={inviteCooperAccess} onChange={setInviteCooperAccess} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}><Button variant="secondary" style={{ flex: 1 }} onClick={() => setShowInviteModal(false)}>Cancel</Button><Button variant="primary" style={{ flex: 1 }} icon={UserPlus} loading={inviting} onClick={inviteMember}>Add Member</Button></div>
          </Card>
        </ModalOverlay>
      )}

      {/* kNine Branded Confirm Modal */}
      {knModal && (
        <>
          <div onClick={() => handleKnModalClose(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1201, width: 380, maxWidth: '90vw' }}>
            <Card style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: 'linear-gradient(135deg, #f59e0b, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700 }}>K9</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{knModal.title}</h3>
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{knModal.message}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" size="sm" onClick={() => handleKnModalClose(false)}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={() => handleKnModalClose(true)}>Continue</Button>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

const ChangePasswordCard = () => {
  const { darkMode } = useTheme();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const inputStyle = {
    width: '100%', padding: '10px 40px 10px 12px', borderRadius: 8, fontSize: 14,
    border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`,
    background: darkMode ? '#0f172a' : '#f8fafc',
    color: darkMode ? '#f8fafc' : '#1e293b', boxSizing: 'border-box',
  };

  const handleSave = async () => {
    if (next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (next !== confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await apiCall('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      toast.success('Password updated successfully');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e) {
      toast.error(e.message || 'Failed to change password');
    }
    setSaving(false);
  };

  return (
    <Card style={{ padding: 20, marginBottom: 16 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: darkMode ? '#f8fafc' : '#1e293b' }}>Change Password</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 400 }}>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Current Password</label>
          <div style={{ position: 'relative' }}>
            <input type={showCurrent ? 'text' : 'password'} value={current} onChange={e => setCurrent(e.target.value)} style={inputStyle} autoComplete="current-password" />
            <button onClick={() => setShowCurrent(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12 }}>{showCurrent ? 'hide' : 'show'}</button>
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>New Password</label>
          <div style={{ position: 'relative' }}>
            <input type={showNext ? 'text' : 'password'} value={next} onChange={e => setNext(e.target.value)} style={inputStyle} autoComplete="new-password" />
            <button onClick={() => setShowNext(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12 }}>{showNext ? 'hide' : 'show'}</button>
          </div>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Confirm New Password</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} onKeyDown={e => e.key === 'Enter' && current && next && confirm && handleSave()} style={inputStyle} autoComplete="new-password" />
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !current || !next || !confirm}
          style={{ alignSelf: 'flex-start', padding: '10px 20px', borderRadius: 8, border: 'none', background: saving || !current || !next || !confirm ? '#334155' : '#1E88E5', color: '#fff', fontWeight: 600, fontSize: 14, cursor: saving || !current || !next || !confirm ? 'default' : 'pointer' }}
        >
          {saving ? 'Saving...' : 'Update Password'}
        </button>
      </div>
    </Card>
  );
};

const TwoFASettingsCard = () => {
  const { darkMode } = useTheme();
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState('idle');
  const [otpauthURI, setOtpauthURI] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiCall('/api/auth/2fa/status')
      .then(d => setStatus(!!d.enabled))
      .catch(() => setStatus(false));
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const data = await apiCall('/api/auth/2fa/setup', { method: 'POST' });
      setOtpauthURI(data.otpauthURI || '');
      setManualSecret(data.secret || '');
      setStep('setup');
    } catch (e) {
      toast.error(e.message || 'Failed to start 2FA setup');
    }
    setLoading(false);
  };

  const handleConfirm = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const data = await apiCall('/api/auth/2fa/confirm', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      setRecoveryCodes(data.recoveryCodes || []);
      setStatus(true);
      setStep('recoveryCodes');
      toast.success('Two-factor authentication enabled');
    } catch (e) {
      toast.error(e.message || 'Invalid code. Please try again.');
    }
    setLoading(false);
  };

  const handleDisable = async () => {
    if (!disableCode.trim()) return;
    setLoading(true);
    try {
      await apiCall('/api/auth/2fa/disable', {
        method: 'POST',
        body: JSON.stringify({ code: disableCode.trim() }),
      });
      setStatus(false);
      setStep('idle');
      setDisableCode('');
      toast.success('Two-factor authentication disabled');
    } catch (e) {
      toast.error(e.message || 'Invalid code');
    }
    setLoading(false);
  };

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const twoFaInputStyle = {
    padding: '10px 14px', borderRadius: 8,
    border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`,
    background: darkMode ? '#0f172a' : '#fff',
    color: darkMode ? '#f8fafc' : '#1f2937',
    fontSize: 16, textAlign: 'center', letterSpacing: 4, width: 180,
  };

  if (status === null) return null;

  return (
    <Card style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Shield size={20} style={{ color: '#1E88E5' }} />
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Two-Factor Authentication</h3>
          <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>
            {status ? 'Your account is protected with 2FA' : 'Add an extra layer of security to your account'}
          </p>
        </div>
      </div>

      {step === 'idle' && !status && (
        <Button variant="primary" icon={Shield} loading={loading} onClick={handleSetup}>
          Enable Two-Factor Authentication
        </Button>
      )}

      {step === 'idle' && status && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: 'rgba(16,185,129,0.1)' }}>
            <CheckCircle size={16} style={{ color: '#10b981' }} />
            <span style={{ fontSize: 13, color: '#10b981', fontWeight: 500 }}>Two-factor authentication is enabled</span>
          </div>
          <Button variant="danger" size="sm" onClick={() => setStep('disabling')}>Disable 2FA</Button>
        </div>
      )}

      {step === 'setup' && (
        <div>
          <p style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b', margin: '0 0 12px' }}>
            Scan this code with your authenticator app (Google Authenticator, Authy, etc.), or enter the secret manually.
          </p>
          {otpauthURI && (
            <div style={{ marginBottom: 12, padding: 12, borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, textAlign: 'center' }}>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthURI)}`} alt="2FA QR Code" style={{ width: 200, height: 200, borderRadius: 8 }} />
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Manual Entry Key</label>
            <code style={{ display: 'block', padding: '8px 12px', borderRadius: 6, background: darkMode ? '#0f172a' : '#f1f5f9', fontSize: 14, fontFamily: 'monospace', letterSpacing: 2, wordBreak: 'break-all', color: darkMode ? '#f8fafc' : '#1f2937' }}>{manualSecret}</code>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Enter 6-digit code to verify</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="text" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" style={twoFaInputStyle} autoFocus />
              <Button variant="primary" loading={loading} disabled={code.length !== 6} onClick={handleConfirm}>Verify & Enable</Button>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { setStep('idle'); setCode(''); }}>Cancel</Button>
        </div>
      )}

      {step === 'recoveryCodes' && (
        <div>
          <div style={{ padding: 12, borderRadius: 8, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', marginBottom: 12, display: 'flex', gap: 8 }}>
            <AlertTriangle size={16} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />
            <span style={{ fontSize: 13, color: '#f59e0b' }}>Save these recovery codes in a safe place. They can be used to access your account if you lose your authenticator. Each code can only be used once. This is the only time they will be shown.</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: 12, borderRadius: 8, background: darkMode ? '#0f172a' : '#f8fafc', border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, marginBottom: 12 }}>
            {recoveryCodes.map((c, i) => (
              <code key={i} style={{ fontSize: 14, fontFamily: 'monospace', padding: '4px 8px', color: darkMode ? '#f8fafc' : '#1f2937' }}>{c}</code>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" icon={copied ? Check : Download} onClick={copyRecoveryCodes}>{copied ? 'Copied!' : 'Copy Codes'}</Button>
            <Button variant="primary" onClick={() => { setStep('idle'); setRecoveryCodes([]); }}>Done</Button>
          </div>
        </div>
      )}

      {step === 'disabling' && (
        <div>
          <p style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b', margin: '0 0 12px' }}>
            Enter a code from your authenticator app or a recovery code to disable 2FA.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" maxLength={10} value={disableCode} onChange={(e) => setDisableCode(e.target.value)} placeholder="Code" style={{ ...twoFaInputStyle, letterSpacing: 2, width: 160 }} autoFocus />
            <Button variant="danger" loading={loading} disabled={!disableCode.trim()} onClick={handleDisable}>Disable 2FA</Button>
            <Button variant="ghost" size="sm" onClick={() => { setStep('idle'); setDisableCode(''); }}>Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
};

const SettingsGeneralPage = () => {
  const { darkMode, setDarkMode } = useTheme();

  const aiAccess = (() => {
    try { return JSON.parse(localStorage.getItem('kubi_ai_access') || '{}'); }
    catch { return {}; }
  })();

  // ---- Roxi (candidate search) settings ----
  const [aiSettings, setAiSettings] = useState(() => {
    const cached = localStorage.getItem('kubi_ai_settings');
    return cached ? JSON.parse(cached) : {
      companyType: 'staffing',
      primaryIndustries: [],
      targetRoles: [],
      preferredLocations: [],
      aiSuggestions: true,
      autoLearn: true,
    };
  });
  const [industryInput, setIndustryInput] = useState('');
  const [roleInput, setRoleInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [saving, setSaving] = useState(false);

  // ---- Cooper (BD) settings ----
  const [cooperSettings, setCooperSettings] = useState({
    productOrService: '',
    targetIndustries: [],
    companySizes: [],
    preferredRegions: [],
    preferredLocations: [],
    targetContactLevels: [],
    salesMethodology: 'consultative',
    excludeStaffingCompanies: true,
  });
  const [cooperLoaded, setCooperLoaded] = useState(false);
  const [cooperSaving, setCooperSaving] = useState(false);
  const [cooperIndustryInput, setCooperIndustryInput] = useState('');

  useEffect(() => {
    if ((aiAccess.cooper || aiAccess.spirko) && !cooperLoaded) {
      apiCall('/api/cooper-search/settings').then(data => {
        if (data?.settings) setCooperSettings(s => ({ ...s, ...data.settings }));
        setCooperLoaded(true);
      }).catch(() => setCooperLoaded(true));
    }
  }, []);

  const companyTypes = [
    { value: 'staffing', label: 'Staffing Agency' },
    { value: 'executive_search', label: 'Executive Search Firm' },
    { value: 'corporate', label: 'Corporate HR/TA' },
    { value: 'rpo', label: 'RPO Provider' },
    { value: 'boutique', label: 'Boutique Recruiting' },
  ];

  const methodologyInfo = {
    consultative: { label: 'Consultative Selling', desc: 'Ask questions to understand needs, then recommend the best solution.' },
    solution: { label: 'Solution Selling', desc: 'Focus on pain points and show how your product/service solves them.' },
    challenger: { label: 'Challenger Sale', desc: 'Teach prospects something new, then show why your approach is better.' },
    relationship: { label: 'Relationship Selling', desc: 'Build trust and long-term connections. Ideal for repeat business.' },
    value: { label: 'Value-Based Selling', desc: 'Lead with ROI and business impact.' },
  };

  const usRegions = ['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West', 'Pacific Northwest', 'Tri-State', 'New England'];
  const companySizeOptions = ['Startup', 'Small', 'SMB', 'Mid-Market', 'Enterprise', 'Large'];
  const contactLevelOptions = ['CXO', 'Owner', 'Partner', 'VP', 'Director', 'Manager'];
  const industryOptions = ['Software', 'SaaS', 'Technology', 'Financial Services', 'Healthcare', 'Manufacturing', 'Consulting', 'Telecommunications', 'Real Estate', 'Retail', 'Education', 'Media', 'Insurance', 'Energy', 'Construction', 'Automotive', 'Logistics', 'Pharmaceuticals', 'Fintech'];

  const saveAiSettings = async () => {
    setSaving(true);
    localStorage.setItem('kubi_ai_settings', JSON.stringify(aiSettings));
    try {
      await apiCall('/api/ai/settings/organization', { method: 'PUT', body: JSON.stringify({
        primaryIndustries: aiSettings.primaryIndustries,
        targetRoles: aiSettings.targetRoles,
        primaryMarkets: aiSettings.preferredLocations,
        companyType: aiSettings.companyType,
      }) });
    } catch {}
    setSaving(false);
  };

  const saveCooperSettings = async () => {
    setCooperSaving(true);
    try {
      await apiCall('/api/cooper-search/settings', { method: 'PUT', body: JSON.stringify({ settings: cooperSettings }) });
    } catch {}
    setCooperSaving(false);
  };

  const addItem = (field, input, setInput) => {
    if (!input.trim()) return;
    const items = [...(aiSettings[field] || [])];
    if (!items.includes(input.trim())) {
      items.push(input.trim());
      setAiSettings({ ...aiSettings, [field]: items });
    }
    setInput('');
  };

  const removeItem = (field, item) => {
    setAiSettings({ ...aiSettings, [field]: aiSettings[field].filter(i => i !== item) });
  };

  const addCooperTag = (field, value) => {
    if (!value.trim()) return;
    const items = cooperSettings[field] || [];
    if (!items.map(i => i.toLowerCase()).includes(value.trim().toLowerCase())) {
      setCooperSettings(s => ({ ...s, [field]: [...items, value.trim()] }));
    }
  };

  const removeCooperTag = (field, item) => {
    setCooperSettings(s => ({ ...s, [field]: s[field].filter(i => i !== item) }));
  };

  const toggleChip = (field, value) => {
    const items = cooperSettings[field] || [];
    setCooperSettings(s => ({
      ...s, [field]: items.includes(value) ? items.filter(i => i !== value) : [...items, value]
    }));
  };

  const inputStyle = { flex: 1, padding: '10px 14px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14 };

  const TagPill = ({ item, color, onRemove }) => (
    <span style={{ padding: '4px 10px', borderRadius: 6, background: `${color}26`, color, fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
      {item}
      <button onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color }}><X size={12} /></button>
    </span>
  );

  const ChipBtn = ({ label, active, onClick }) => (
    <button onClick={onClick} style={{
      padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
      border: `1px solid ${active ? '#f59e0b' : (darkMode ? '#334155' : '#e5e7eb')}`,
      background: active ? 'rgba(245,158,11,0.15)' : 'transparent',
      color: active ? '#f59e0b' : (darkMode ? '#94a3b8' : '#64748b'),
    }}>{label}</button>
  );

  return (
    <div style={{ maxWidth: 700 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 700 }}>Settings</h1>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: '#64748b' }}>Manage preferences and AI behavior</p>

      <Card style={{ padding: 20, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Appearance</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>{darkMode ? <Moon size={20} style={{ color: '#1E88E5' }} /> : <Sun size={20} style={{ color: '#f59e0b' }} />}<div><div style={{ fontWeight: 500 }}>Dark Mode</div><div style={{ fontSize: 13, color: '#64748b' }}>Use dark theme</div></div></div>
          <Toggle checked={darkMode} onChange={setDarkMode} />
        </div>
      </Card>

      <ChangePasswordCard />

      <TwoFASettingsCard />

      {aiAccess.kubi !== false && (
        <Card style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #1E88E5, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <Sparkles size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Roxi Settings</h3>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Help Roxi understand your recruiting focus</p>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Company Type</label>
            <select value={aiSettings.companyType} onChange={(e) => setAiSettings({ ...aiSettings, companyType: e.target.value })} style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14 }}>
              {companyTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Industries You Recruit For</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input type="text" value={industryInput} onChange={(e) => setIndustryInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addItem('primaryIndustries', industryInput, setIndustryInput)} placeholder="e.g., Healthcare, Technology, Finance" style={inputStyle} />
              <Button variant="secondary" size="sm" icon={Plus} onClick={() => addItem('primaryIndustries', industryInput, setIndustryInput)}>Add</Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(aiSettings.primaryIndustries || []).map(ind => (
                <TagPill key={ind} item={ind} color="#1E88E5" onRemove={() => removeItem('primaryIndustries', ind)} />
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Common Roles You Search For</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input type="text" value={roleInput} onChange={(e) => setRoleInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addItem('targetRoles', roleInput, setRoleInput)} placeholder="e.g., Software Engineer, Account Executive" style={inputStyle} />
              <Button variant="secondary" size="sm" icon={Plus} onClick={() => addItem('targetRoles', roleInput, setRoleInput)}>Add</Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(aiSettings.targetRoles || []).map(role => (
                <TagPill key={role} item={role} color="#1E88E5" onRemove={() => removeItem('targetRoles', role)} />
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Preferred Locations</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input type="text" value={locationInput} onChange={(e) => setLocationInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addItem('preferredLocations', locationInput, setLocationInput)} placeholder="e.g., New York, Remote, California" style={inputStyle} />
              <Button variant="secondary" size="sm" icon={Plus} onClick={() => addItem('preferredLocations', locationInput, setLocationInput)}>Add</Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(aiSettings.preferredLocations || []).map(loc => (
                <TagPill key={loc} item={loc} color="#10b981" onRemove={() => removeItem('preferredLocations', loc)} />
              ))}
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, paddingTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div><div style={{ fontWeight: 500 }}>AI Suggestions</div><div style={{ fontSize: 13, color: '#64748b' }}>Get smart recommendations from Roxi</div></div>
              <Toggle checked={aiSettings.aiSuggestions} onChange={(v) => setAiSettings({ ...aiSettings, aiSuggestions: v })} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><div style={{ fontWeight: 500 }}>Auto-Learn from Searches</div><div style={{ fontSize: 13, color: '#64748b' }}>Roxi learns your preferences over time</div></div>
              <Toggle checked={aiSettings.autoLearn} onChange={(v) => setAiSettings({ ...aiSettings, autoLearn: v })} />
            </div>
          </div>

          <div style={{ marginTop: 20 }}>
            <Button variant="primary" icon={Check} loading={saving} onClick={saveAiSettings}>Save Roxi Settings</Button>
          </div>
        </Card>
      )}

      {(aiAccess.cooper === true || aiAccess.spirko === true) && (
        <Card style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #f59e0b, #f97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
              <Target size={20} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Cooper Settings</h3>
              <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>Help Cooper focus your business development</p>
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>What Do You Sell?</label>
            <textarea
              value={cooperSettings.productOrService || ''}
              onChange={e => setCooperSettings(s => ({ ...s, productOrService: e.target.value }))}
              placeholder="Describe your product or service and the value it provides..."
              rows={3}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Target Industries</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input type="text" value={cooperIndustryInput} onChange={e => setCooperIndustryInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { addCooperTag('targetIndustries', cooperIndustryInput); setCooperIndustryInput(''); } }}
                placeholder="Add industry..." style={inputStyle} />
              <Button variant="secondary" size="sm" icon={Plus} onClick={() => { addCooperTag('targetIndustries', cooperIndustryInput); setCooperIndustryInput(''); }}>Add</Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {industryOptions.filter(o => !(cooperSettings.targetIndustries || []).map(i => i.toLowerCase()).includes(o.toLowerCase())).slice(0, 10).map(o => (
                <span key={o} onClick={() => addCooperTag('targetIndustries', o)} style={{ padding: '3px 8px', borderRadius: 4, background: darkMode ? '#334155' : '#f1f5f9', color: darkMode ? '#94a3b8' : '#64748b', fontSize: 11, cursor: 'pointer' }}>{o}</span>
              ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(cooperSettings.targetIndustries || []).map(item => (
                <TagPill key={item} item={item} color="#f59e0b" onRemove={() => removeCooperTag('targetIndustries', item)} />
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Target Company Sizes</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {companySizeOptions.map(size => (
                <ChipBtn key={size} label={size} active={(cooperSettings.companySizes || []).includes(size)} onClick={() => toggleChip('companySizes', size)} />
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Preferred US Regions</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {usRegions.map(region => (
                <ChipBtn key={region} label={region} active={(cooperSettings.preferredRegions || []).includes(region)} onClick={() => toggleChip('preferredRegions', region)} />
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Target Contact Levels</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {contactLevelOptions.map(level => (
                <ChipBtn key={level} label={level} active={(cooperSettings.targetContactLevels || []).includes(level)} onClick={() => toggleChip('targetContactLevels', level)} />
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Sales Methodology</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Object.entries(methodologyInfo).map(([key, info]) => {
                const active = (cooperSettings.salesMethodology || 'consultative') === key;
                return (
                  <div key={key} onClick={() => setCooperSettings(s => ({ ...s, salesMethodology: key }))}
                    style={{ padding: '12px 14px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${active ? '#f59e0b' : (darkMode ? '#334155' : '#e5e7eb')}`, background: active ? 'rgba(245,158,11,0.08)' : 'transparent' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${active ? '#f59e0b' : (darkMode ? '#475569' : '#cbd5e1')}`, background: active ? '#f59e0b' : 'transparent', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: active ? '#f59e0b' : (darkMode ? '#f8fafc' : '#1e293b') }}>{info.label}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginLeft: 22 }}>{info.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: 20, padding: '16px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#f8fafc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Exclude Recruiting & Staffing Companies</div>
                <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                  When on, Cooper will never suggest recruiting, staffing, or employment agencies as BD targets.
                  Recommended for staffing and recruiting firms — you don't want to pitch other recruiters.
                </div>
              </div>
              <Toggle
                checked={cooperSettings.excludeStaffingCompanies !== false}
                onChange={() => setCooperSettings(s => ({ ...s, excludeStaffingCompanies: s.excludeStaffingCompanies === false }))}
              />
            </div>
          </div>

          <Button variant="primary" icon={Check} loading={cooperSaving} onClick={saveCooperSettings} style={{ background: 'linear-gradient(135deg, #f59e0b, #f97316)', border: 'none' }}>Save Cooper Settings</Button>
        </Card>
      )}

      <Card style={{ padding: 20 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>Notifications</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}><div><div style={{ fontWeight: 500 }}>Email Notifications</div><div style={{ fontSize: 13, color: '#64748b' }}>Receive email updates</div></div><Toggle checked={true} onChange={() => {}} /></div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><div><div style={{ fontWeight: 500 }}>Job Change Alerts</div><div style={{ fontSize: 13, color: '#64748b' }}>Get notified when candidates change jobs</div></div><Toggle checked={true} onChange={() => {}} /></div>
      </Card>
    </div>
  );
};

const SettingsIntegrationsPage = () => {
  const { darkMode } = useTheme();
  const { plan } = useBilling();
  const isTrialOrFree = !plan || plan === 'free' || plan === 'trial' || plan === 'trialing';
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(null);

  // kNine branded confirm modal
  const [knModal, setKnModal] = useState(null);
  const knModalResolveRef = useRef(null);
  const knConfirm = (message, opts = {}) => new Promise(resolve => {
    knModalResolveRef.current = resolve;
    setKnModal({ type: 'confirm', message, title: opts.title || 'kNine', ...opts });
  });
  const handleKnModalClose = (result) => {
    const resolve = knModalResolveRef.current;
    knModalResolveRef.current = null;
    setKnModal(null);
    if (resolve) resolve(result);
  };
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const hasActiveIntegration = integrations.some(i => i.isActive);

  useEffect(() => { loadIntegrations(); }, []);

  const loadIntegrations = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall('/api/integrations');
      const intList = Array.isArray(data) ? data : data.integrations || [];
      setIntegrations(intList);
      // Cache successful response
      localStorage.setItem('kubi_integrations_cache', JSON.stringify(intList));
    } catch (err) {
      // On network error, show cached data but indicate it might be stale
      const cached = localStorage.getItem('kubi_integrations_cache');
      if (cached) {
        try {
          setIntegrations(JSON.parse(cached));
          setError('Unable to reach server. Showing cached data.');
        } catch {
          setIntegrations([]);
          setError('Failed to load integrations. Please try again.');
        }
      } else {
        setIntegrations([]);
        setError('Failed to load integrations. Please try again.');
      }
    }
    setLoading(false);
  };

  const handleTest = async (int) => {
    setTestingId(int.id); setTestResult(null);
    try {
      const result = await apiCall(`/api/integrations/${int.id}/test`, { method: 'POST' });
      setTestResult({ id: int.id, success: result.success !== false, message: result.message || 'Connection successful!' });
    } catch (err) {
      setTestResult({ id: int.id, success: false, message: err.message || 'Connection test failed. Please check your credentials.' });
    }
    setTestingId(null); setTimeout(() => setTestResult(null), 5000);
  };

  const handleDelete = async (id) => {
    const ok = await knConfirm('Remove this integration?', { title: 'Remove Integration' });
    if (!ok) return;
    try {
      await apiCall(`/api/integrations/${id}`, { method: 'DELETE' });
      const updated = integrations.filter(i => i.id !== id);
      setIntegrations(updated);
      localStorage.setItem('kubi_integrations_cache', JSON.stringify(updated));
    } catch (err) {
      setSaveError('Failed to remove integration. Please try again.');
      setTimeout(() => setSaveError(null), 5000);
    }
  };

  const handleAdd = async (type, credentials) => {
    const meta = AVAILABLE_INTEGRATIONS.find(i => i.type === type);
    setSaveError(null);
    try {
      const result = await apiCall('/api/integrations', { method: 'POST', body: JSON.stringify({ type, name: meta?.name || type, credentials }) });
      // Use the server-returned integration object
      const newInt = {
        id: result.id,
        type: result.type || type,
        name: result.name || meta?.name || type,
        isActive: result.isActive !== false,
        hasCredentials: result.hasCredentials !== false,
        lastSync: result.lastSync || result.createdAt || new Date().toISOString(),
        apiUrl: result.apiUrl,
        settings: result.settings || {}
      };
      const updated = [...integrations, newInt];
      setIntegrations(updated);
      localStorage.setItem('kubi_integrations_cache', JSON.stringify(updated));
      setShowAddModal(false);
    } catch (err) {
      setSaveError(err.message || 'Failed to save integration. Please check your credentials and try again.');
      // Don't close modal so user can retry
    }
  };

  const handleUpdate = async (id, credentials) => {
    setSaveError(null);
    try {
      await apiCall(`/api/integrations/${id}`, { method: 'PUT', body: JSON.stringify({ credentials }) });
      const updated = integrations.map(i => i.id === id ? { ...i, lastSync: new Date().toISOString() } : i);
      setIntegrations(updated);
      localStorage.setItem('kubi_integrations_cache', JSON.stringify(updated));
      setShowConfigModal(null);
    } catch (err) {
      setSaveError(err.message || 'Failed to update integration. Please try again.');
    }
  };

  const getMeta = (type) => AVAILABLE_INTEGRATIONS.find(i => i.type === type) || { icon: '🔗', color: '#64748b', fields: [] };

  if (loading) return <LoadingSpinner text="Loading integrations..." />;

  return (
    <div style={{ maxWidth: 900 }}>
      <div className="kubi-page-header" style={{ marginBottom: 24 }}>
        <h1 className="kubi-page-title" style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 700 }}>Integrations</h1>
        <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Connect your ATS and tools</p>
      </div>

      {isTrialOrFree && !hasActiveIntegration && (
        <div style={{ marginBottom: 28, borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(139,92,246,0.3)' }}>
          <div style={{ height: 4, background: 'linear-gradient(135deg, #1E88E5, #1565C0)' }} />
          <div style={{ padding: '20px 24px', background: darkMode ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.04)', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #1E88E5, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Crown size={20} color="#fff" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: darkMode ? '#f8fafc' : '#1e293b', marginBottom: 6 }}>
                Upgrade to connect your database
              </div>
              <p style={{ margin: '0 0 14px', fontSize: 14, color: darkMode ? '#94a3b8' : '#64748b', lineHeight: 1.6 }}>
                Upgrade your account to connect your ATS and source over 100M kNine records alongside your own candidates — all weighted and ranked together by AI with simple, natural language searching.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {['PCRecruiter', 'Bullhorn', 'TE Recruit', 'Greenhouse', 'Lever', 'Stardex'].map(ats => (
                  <span key={ats} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: darkMode ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)', color: darkMode ? '#94a3b8' : '#4b5563' }}>{ats}</span>
                ))}
              </div>
            </div>
            <a href="/settings/usage" style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #1E88E5, #1565C0)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <Crown size={15} /> Upgrade Plan
            </a>
          </div>
        </div>
      )}

      {(error || saveError) && (
        <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle size={18} style={{ color: '#ef4444' }} />
          <span style={{ fontSize: 14, color: '#ef4444' }}>{error || saveError}</span>
          <button onClick={() => { setError(null); setSaveError(null); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={16} style={{ color: '#ef4444' }} /></button>
        </div>
      )}

      {integrations.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Connected ({integrations.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {integrations.map(int => {
              const meta = getMeta(int.type);
              const testing = testingId === int.id;
              const result = testResult?.id === int.id ? testResult : null;
              return (
                <Card key={int.id} style={{ padding: 20 }}>
                  <div className="kubi-integration-row" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ width: 56, height: 56, borderRadius: 12, background: `${meta.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, flexShrink: 0 }}>{meta.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 style={{ margin: '0 0 2px', fontSize: 16, fontWeight: 600 }}>{int.name}</h4>
                      <div style={{ fontSize: 13, color: '#64748b' }}>Last synced: {int.lastSync ? new Date(int.lastSync).toLocaleString() : 'Never'}</div>
                      {result && <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: result.success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>{result.success ? <CheckCircle size={16} style={{ color: '#10b981' }} /> : <XCircle size={16} style={{ color: '#ef4444' }} />}<span style={{ fontSize: 13, color: result.success ? '#10b981' : '#ef4444' }}>{result.message}</span></div>}
                    </div>
                    <div className="kubi-btn-group" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Badge variant="success">✓ Connected</Badge>
                      <Button variant="secondary" size="sm" icon={Settings} onClick={() => setShowConfigModal(int)}>Configure</Button>
                      <Button variant="secondary" size="sm" icon={testing ? Loader2 : RefreshCw} loading={testing} onClick={() => handleTest(int)}>Test</Button>
                      <Button variant="danger" size="sm" icon={Trash2} onClick={() => handleDelete(int.id)}>Remove</Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>Available</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {AVAILABLE_INTEGRATIONS.filter(a => !integrations.some(i => i.type === a.type)).map(a => (
            <Card key={a.type} style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: `${a.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>{a.icon}</div>
                <div style={{ flex: 1 }}><h4 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 600 }}>{a.name}</h4><p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b' }}>{a.description}</p><Button variant="primary" size="sm" icon={Link2} onClick={() => setShowAddModal(a.type)}>Connect</Button></div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {showAddModal && <IntegrationModal mode="add" darkMode={darkMode} selectedType={typeof showAddModal === 'string' ? showAddModal : null} onClose={() => { setShowAddModal(false); setSaveError(null); }} onSave={handleAdd} error={saveError} />}
      {showConfigModal && <IntegrationModal mode="edit" darkMode={darkMode} integration={showConfigModal} onClose={() => { setShowConfigModal(null); setSaveError(null); }} onSave={(_, creds) => handleUpdate(showConfigModal.id, creds)} error={saveError} />}

      {/* kNine Branded Confirm Modal */}
      {knModal && (
        <>
          <div onClick={() => handleKnModalClose(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1201, width: 380, maxWidth: '90vw' }}>
            <Card style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, background: 'linear-gradient(135deg, #f59e0b, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700 }}>K9</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{knModal.title}</h3>
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>{knModal.message}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost" size="sm" onClick={() => handleKnModalClose(false)}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={() => handleKnModalClose(true)}>Continue</Button>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

const IntegrationModal = ({ mode, darkMode, selectedType, integration, onClose, onSave, error }) => {
  const [type, setType] = useState(selectedType || integration?.type || '');
  const [credentials, setCredentials] = useState(() => {
    const raw = integration?.credentials || integration?.credentialSummary || {};
    // Strip masked password values (e.g. "****word") so they show as empty rather than the mask
    const cleaned = { ...raw };
    Object.keys(cleaned).forEach(k => {
      if (typeof cleaned[k] === 'string' && cleaned[k].startsWith('****')) {
        delete cleaned[k];
      }
    });
    return cleaned;
  });
  const [showPasswords, setShowPasswords] = useState({});
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState(null);
  const [useManualEntry, setUseManualEntry] = useState(false);

  const meta = AVAILABLE_INTEGRATIONS.find(i => i.type === type);
  const fields = meta?.fields || [];
  const isOAuth = meta?.authType === 'oauth2';
  const hasManualFields = isOAuth && fields.length > 0;

  // Check if all required fields are filled (password fields optional when editing existing integration)
  const requiredFields = fields.filter(f => f.required);
  const missingRequired = requiredFields.filter(f => {
    if (mode === 'edit' && f.type === 'password' && integration?.hasCredentials) return false;
    return !credentials[f.key]?.trim();
  });
  const hasManualCreds = fields.some(f => f.required && credentials[f.key]?.trim());
  const isValid = type && (
    (isOAuth && !useManualEntry) ||
    missingRequired.length === 0
  );

  const handleOAuthConnect = async () => {
    if (!meta?.oauthStartUrl) return;
    setOauthLoading(true);
    setOauthError(null);
    try {
      const data = await apiCall(meta.oauthStartUrl);
      if (data.authorizeUrl) {
        window.location.href = data.authorizeUrl;
      } else {
        setOauthError('Failed to get authorization URL');
      }
    } catch (err) {
      setOauthError(err.message || 'Failed to start OAuth flow');
    } finally {
      setOauthLoading(false);
    }
  };

  const handleSave = async () => {
    if (isOAuth && !useManualEntry) {
      handleOAuthConnect();
      return;
    }
    // Validate required fields
    const errors = {};
    requiredFields.forEach(f => {
      if (!credentials[f.key]?.trim()) {
        errors[f.key] = `${f.label} is required`;
      }
    });
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    setSaving(true);
    await onSave(type, credentials);
    setSaving(false);
  };

  return (
    <ModalOverlay onClose={onClose}>
      <Card className="kubi-modal-card" style={{ width: 520, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: 24, borderBottom: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{mode === 'add' ? 'Add Integration' : `Configure ${integration?.name}`}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button></div>
        </div>
        <div style={{ padding: 24 }}>
          {mode === 'add' && !selectedType && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Select Integration</label>
              <select value={type} onChange={(e) => { setType(e.target.value); setCredentials({}); setOauthError(null); setUseManualEntry(false); }} style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14 }}>
                <option value="">Choose...</option>
                {AVAILABLE_INTEGRATIONS.map(i => <option key={i.type} value={i.type}>{i.icon} {i.name}</option>)}
              </select>
            </div>
          )}

          {meta && <div style={{ padding: 16, background: `${meta.color}10`, borderRadius: 12, marginBottom: 20, border: `1px solid ${meta.color}30` }}><div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><span style={{ fontSize: 32 }}>{meta.icon}</span><div><div style={{ fontWeight: 600, fontSize: 16 }}>{meta.name}</div><div style={{ fontSize: 13, color: '#64748b' }}>{meta.description}</div></div></div></div>}

          {isOAuth && meta && !useManualEntry && (
            <div style={{ marginBottom: hasManualFields ? 12 : 20 }}>
              <div style={{ padding: 16, background: darkMode ? '#1e293b' : '#f0f9ff', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#bae6fd'}`, marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: darkMode ? '#e2e8f0' : '#0c4a6e', marginBottom: 8 }}>OAuth2 Connection</div>
                <div style={{ fontSize: 13, color: darkMode ? '#94a3b8' : '#64748b', lineHeight: 1.5 }}>
                  Click the button below to securely connect your {meta.name} account. You'll be redirected to {meta.name} to authorize access, then returned here automatically.
                </div>
              </div>
              {oauthError && (
                <div style={{ marginBottom: 12, padding: '12px 16px', borderRadius: 8, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <AlertCircle size={16} style={{ color: '#ef4444', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#ef4444' }}>{oauthError}</span>
                </div>
              )}
            </div>
          )}

          {hasManualFields && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1, height: 1, background: darkMode ? '#334155' : '#e5e7eb' }} />
              <button onClick={() => setUseManualEntry(!useManualEntry)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#3b82f6', fontWeight: 500, whiteSpace: 'nowrap', padding: '4px 8px' }}>
                {useManualEntry ? 'Use OAuth2 instead' : 'Enter credentials manually'}
              </button>
              <div style={{ flex: 1, height: 1, background: darkMode ? '#334155' : '#e5e7eb' }} />
            </div>
          )}

          {(useManualEntry || !isOAuth) && fields.map(f => (
            <div key={f.key} style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>
                {f.label}{f.required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
              </label>
              {f.type === 'select' ? (
                <select value={credentials[f.key] || ''} onChange={(e) => { setCredentials({ ...credentials, [f.key]: e.target.value }); setValidationErrors({ ...validationErrors, [f.key]: null }); }} style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${validationErrors[f.key] ? '#ef4444' : (darkMode ? '#334155' : '#e5e7eb')}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14 }}>
                  <option value="">Select...</option>
                  {f.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <div style={{ position: 'relative' }}>
                  <input type={f.type === 'password' && !showPasswords[f.key] ? 'password' : 'text'} value={credentials[f.key] || ''} onChange={(e) => { setCredentials({ ...credentials, [f.key]: e.target.value }); setValidationErrors({ ...validationErrors, [f.key]: null }); }} placeholder={mode === 'edit' && f.type === 'password' && integration?.hasCredentials ? 'Leave blank to keep current password' : f.placeholder} style={{ width: '100%', padding: '12px 14px', paddingRight: f.type === 'password' ? 44 : 14, borderRadius: 10, border: `1px solid ${validationErrors[f.key] ? '#ef4444' : (darkMode ? '#334155' : '#e5e7eb')}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, boxSizing: 'border-box' }} />
                  {f.type === 'password' && <button type="button" onClick={() => setShowPasswords({ ...showPasswords, [f.key]: !showPasswords[f.key] })} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>{showPasswords[f.key] ? <EyeOff size={18} /> : <Eye size={18} />}</button>}
                </div>
              )}
              {validationErrors[f.key] && (
                <div style={{ marginTop: 6, padding: '8px 12px', borderRadius: 6, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <AlertCircle size={14} style={{ color: '#ef4444' }} />
                  <span style={{ fontSize: 12, color: '#ef4444' }}>{validationErrors[f.key]}</span>
                </div>
              )}
            </div>
          ))}

          {error && (
            <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 8, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <AlertCircle size={16} style={{ color: '#ef4444', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#ef4444' }}>{error}</span>
            </div>
          )}

          <div style={{ padding: 12, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}><Shield size={18} style={{ color: '#10b981' }} /><span style={{ fontSize: 12, color: '#64748b' }}>Credentials encrypted and stored securely</span></div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, display: 'flex', gap: 12 }}>
          <Button variant="secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</Button>
          <Button variant="primary" icon={(isOAuth && !useManualEntry) ? ExternalLink : Check} loading={saving || oauthLoading} onClick={handleSave} disabled={!isValid} style={{ flex: 1 }}>{(isOAuth && !useManualEntry) ? `Connect ${meta?.name || ''}` : (mode === 'add' ? 'Add Integration' : 'Save Changes')}</Button>
        </div>
      </Card>
    </ModalOverlay>
  );
};

// ==================== BILLING PAGE (DEMO ONLY) ====================
// Calculate credit price based on plan - maps to backend services/billing/config.js
const getPlanCreditRate = (planId) => {
  const rates = { free: 1.50, starter: 1.50, professional: 1.20, business: 1.00, enterprise: 1.00 };
  return rates[planId] || 1.50;
};

// Get upgrade recommendation based on credit purchase amount
const getUpgradeRecommendation = (currentPlanId, creditsToPurchase, currentPlanCredits, currentPlanPrice) => {
  const planOrder = ['free', 'starter', 'professional', 'business'];
  const planData = {
    starter: { credits: 2000, price: 3000, name: 'Starter' },
    professional: { credits: 5000, price: 6000, name: 'Professional' },
    business: { credits: 10000, price: 10000, name: 'Business' }
  };

  const currentIndex = planOrder.indexOf(currentPlanId);
  const pricePerCredit = getPlanCreditRate(currentPlanId);
  const purchaseCost = creditsToPurchase * pricePerCredit;

  for (let i = currentIndex + 1; i < planOrder.length; i++) {
    const upgradePlan = planData[planOrder[i]];
    if (!upgradePlan) continue;

    const additionalCredits = upgradePlan.credits - (currentPlanCredits || 0);
    const additionalCost = upgradePlan.price - (currentPlanPrice || 0);

    if (creditsToPurchase >= additionalCredits * 0.5 || purchaseCost >= additionalCost * 0.5) {
      return {
        recommend: true,
        plan: planOrder[i],
        planName: upgradePlan.name,
        additionalCredits,
        upgradeCost: additionalCost,
        savings: purchaseCost > additionalCost ? purchaseCost - additionalCost : 0
      };
    }
  }
  return null;
};

const SettingsUsagePage = () => {
  const { darkMode } = useTheme();
  const { credits, setCredits, plan, setPlan } = useBilling();
  const [activeTab, setActiveTab] = useState('overview');
  const [paymentMethod, setPaymentMethod] = useState(() => localStorage.getItem('kubi_payment_method') ? JSON.parse(localStorage.getItem('kubi_payment_method')) : null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [creditBlocks, setCreditBlocks] = useState(1);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(0);
  const [purchasing, setPurchasing] = useState(false);

  const currentPlan = SUBSCRIPTION_PLANS.find(p => p.id === plan) || SUBSCRIPTION_PLANS[0];
  const currentPlanIndex = SUBSCRIPTION_PLANS.findIndex(p => p.id === plan);

  // Credit purchase calculations
  const BLOCK_SIZE = 500;
  const maxBlocks = 20;
  const pricePerCredit = getPlanCreditRate(plan);
  const totalCredits = creditBlocks * BLOCK_SIZE;
  const totalPrice = totalCredits * pricePerCredit;
  const upgradeRec = getUpgradeRecommendation(plan, totalCredits, currentPlan.credits, currentPlan.annualPrice);

  const handleUpgrade = async (newPlan) => {
    setPlan(newPlan.id);
    setCredits(prev => prev + (typeof newPlan.credits === 'number' ? newPlan.credits : 0));
    setShowUpgradeModal(null);
  };

  const handlePurchaseCredits = async () => {
    if (!paymentMethod) return;
    setPurchasing(true);
    try {
      // API call to purchase credits
      const res = await fetch('/api/billing/purchase-credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ blocks: creditBlocks })
      });
      if (res.ok) {
        const data = await res.json();
        setCredits(prev => prev + (data.creditsAdded || totalCredits));
      } else {
        // Demo fallback
        setCredits(prev => prev + totalCredits);
      }
    } catch (err) {
      // Demo fallback
      setCredits(prev => prev + totalCredits);
    }
    setPurchasing(false);
    setCreditBlocks(1);
  };

  const handleSavePayment = (card) => {
    setPaymentMethod(card);
    localStorage.setItem('kubi_payment_method', JSON.stringify(card));
    setShowPaymentModal(false);
  };

  // Plans comparison for slider
  const plansForComparison = SUBSCRIPTION_PLANS.filter(p => p.id !== 'enterprise');

  return (
    <div style={{ maxWidth: 1000 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, fontWeight: 700 }}>Usage & Billing</h1>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: '#64748b' }}>Manage subscription and credits</p>

      <div style={{ padding: '12px 16px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: 10, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <AlertTriangle size={18} style={{ color: '#f59e0b' }} />
        <span style={{ fontSize: 14, color: '#f59e0b' }}>Demo Mode: Billing features are simulated. Use Stripe test cards.</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'plans', label: 'Plans' },
          { id: 'validation', label: '✦ How We Validate' },
          { id: 'credits', label: 'Credits' },
          { id: 'payment', label: 'Payment' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ padding: '10px 20px', borderRadius: 10, border: tab.id === 'validation' ? '1px solid rgba(16,185,129,0.35)' : 'none', background: activeTab === tab.id ? (tab.id === 'validation' ? 'linear-gradient(135deg,#10b981,#6366f1)' : '#1E88E5') : (tab.id === 'validation' ? (darkMode ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.04)') : 'transparent'), color: activeTab === tab.id ? '#fff' : (tab.id === 'validation' ? '#10b981' : '#64748b'), fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div>
          <div className="kubi-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
            <Card style={{ padding: 20 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Current Plan</div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{currentPlan.name}</div>
              <div style={{ fontSize: 14, color: '#64748b' }}>{currentPlan.annualPrice ? `$${currentPlan.annualPrice.toLocaleString()}/year` : 'Free'}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>${pricePerCredit.toFixed(2)}/credit</div>
            </Card>
            <Card style={{ padding: 20 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Available Credits</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#10b981' }}>{credits.toLocaleString()}</div>
              <div style={{ fontSize: 14, color: '#64748b' }}>searches remaining</div>
            </Card>
            <Card style={{ padding: 20 }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>Payment Method</div>
              <div style={{ fontSize: 18, fontWeight: 600 }}>{paymentMethod ? `•••• ${paymentMethod.last4}` : 'None'}</div>
              <button onClick={() => setShowPaymentModal(true)} style={{ marginTop: 8, padding: '6px 12px', borderRadius: 6, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: 'transparent', color: '#1E88E5', fontSize: 13, cursor: 'pointer' }}>{paymentMethod ? 'Update' : 'Add Card'}</button>
            </Card>
          </div>
          <Card style={{ padding: 24 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>Quick Actions</h3>
            <div style={{ display: 'flex', gap: 12 }}>
              <Button variant="primary" icon={Crown} onClick={() => setActiveTab('plans')}>Upgrade Plan</Button>
              <Button variant="secondary" icon={Zap} onClick={() => setActiveTab('credits')}>Buy Credits</Button>
            </div>
          </Card>

          {/* Trial/Starter: validation upsell callout */}
          {!currentPlan.validation && (
            <div style={{ marginTop: 16, borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(16,185,129,0.25)', background: darkMode ? 'rgba(16,185,129,0.05)' : 'rgba(16,185,129,0.03)' }}>
              <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg, #10b981, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ShieldCheck size={18} style={{ color: '#fff' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: darkMode ? '#f8fafc' : '#111827', marginBottom: 2 }}>Unlock Contact Validation on Professional+</div>
                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>
                    Every enrichment automatically checks email deliverability (bounce detection) and phone line-type (mobile / landline / VoIP). The verified address is pushed as the primary contact into your ATS. Our competitors don't do this.
                  </div>
                </div>
                <Button variant="primary" size="sm" icon={Crown} onClick={() => setActiveTab('plans')}>See Plans</Button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderTop: '1px solid rgba(16,185,129,0.12)' }}>
                {[
                  { label: 'Email bounce detection', sub: 'via ZeroBounce' },
                  { label: 'Phone line-type ID', sub: 'via Twilio Lookup' },
                  { label: 'Verified contact to ATS', sub: 'automatic on enrichment' },
                ].map(({ label, sub }, i) => (
                  <div key={i} style={{ padding: '10px 18px', borderRight: i < 2 ? '1px solid rgba(16,185,129,0.1)' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                      <Check size={11} style={{ color: '#10b981' }} />
                      <span style={{ fontSize: 12, fontWeight: 600, color: darkMode ? '#e2e8f0' : '#374151' }}>{label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', paddingLeft: 16 }}>{sub}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'plans' && (
        <div>
          {/* kNine Contact Validation differentiator banner */}
          <div style={{ marginBottom: 24, borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(16,185,129,0.3)', background: darkMode ? 'linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(99,102,241,0.08) 100%)' : 'linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(99,102,241,0.06) 100%)' }}>
            <div style={{ padding: '20px 24px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #10b981, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ShieldCheck size={18} style={{ color: '#fff' }} />
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: darkMode ? '#f8fafc' : '#111827' }}>kNine Contact Validation — built in. No extra tools.</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>Our competitors don't do this. Every enrichment automatically verifies email deliverability and phone line-type before you reach out.</div>
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 0, borderTop: '1px solid rgba(16,185,129,0.15)', marginTop: 16 }}>
              {[
                { Icon: Mail, color: '#10b981', title: 'Email Deliverability', desc: 'ZeroBounce validates every address — catches bad domains, spam traps, disposables, and catch-all inboxes before you send.' },
                { Icon: Smartphone, color: '#6366f1', title: 'Phone Line-Type', desc: 'Twilio Lookup identifies mobile vs. landline vs. VoIP — so you know whether to text, call, or skip.' },
                { Icon: ShieldCheck, color: '#f59e0b', title: 'Verified First', desc: 'The best address is automatically prioritized in your ATS record. No more guessing which email or number is real.' },
              ].map(({ Icon, color, title, desc }, i) => (
                <div key={i} style={{ padding: '16px 24px', borderRight: i < 2 ? '1px solid rgba(16,185,129,0.1)' : 'none', background: 'transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Icon size={14} style={{ color }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: darkMode ? '#e2e8f0' : '#111827' }}>{title}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Trial/Starter upgrade nudge */}
          {!currentPlan.validation && (
            <div style={{ marginBottom: 20, padding: '14px 18px', borderRadius: 10, background: darkMode ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)', display: 'flex', alignItems: 'center', gap: 14 }}>
              <Shield size={20} style={{ color: '#f59e0b', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#f59e0b', marginBottom: 2 }}>Contact Validation is a paid feature</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>Upgrade to Professional or higher to automatically validate email deliverability and phone line-type on every enrichment — included at no extra cost. Your competitors are flying blind; you don't have to.</div>
              </div>
              <Button variant="primary" size="sm" onClick={() => {}}>See Plans</Button>
            </div>
          )}

          {/* Plan cards grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', gap: 14, marginBottom: 28 }}>
            {SUBSCRIPTION_PLANS.map(p => {
              const isCurrent = plan === p.id;
              const borderColor = p.popular ? '#10b981' : isCurrent ? '#3b82f6' : (darkMode ? '#1e293b' : '#e5e7eb');
              return (
                <Card key={p.id} style={{ padding: 20, border: `2px solid ${borderColor}`, position: 'relative', opacity: isCurrent ? 1 : 0.9, transition: 'opacity 0.2s, box-shadow 0.2s' }}>
                  {p.popular && !isCurrent && <div style={{ position: 'absolute', top: -11, left: '50%', transform: 'translateX(-50%)', padding: '3px 10px', borderRadius: 20, background: '#10b981', color: '#fff', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>MOST POPULAR</div>}
                  {isCurrent && <div style={{ position: 'absolute', top: -11, right: 12, padding: '3px 10px', borderRadius: 20, background: '#3b82f6', color: '#fff', fontSize: 10, fontWeight: 700 }}>YOUR PLAN</div>}
                  <div style={{ fontSize: 13, fontWeight: 700, color: p.color, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{p.name}</div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: darkMode ? '#f8fafc' : '#111827', marginBottom: 2, lineHeight: 1 }}>
                    {p.annualPrice !== null ? `$${(p.annualPrice / 12 / 100 * 100).toFixed(0) === '0' ? '0' : p.annualPrice !== 0 ? Math.round(p.annualPrice / 12).toLocaleString() : '0'}` : 'Custom'}
                    {p.annualPrice !== null && <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b' }}>/mo</span>}
                  </div>
                  {p.annualPrice !== null && p.annualPrice > 0 && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>${p.annualPrice.toLocaleString()} billed annually</div>}
                  {(p.annualPrice === 0 || p.annualPrice === null) && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>{p.annualPrice === 0 ? 'Free forever' : 'Talk to sales'}</div>}
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#10b981', marginBottom: 14 }}>{typeof p.credits === 'number' ? `${p.credits.toLocaleString()} credits/yr` : p.credits}</div>

                  {/* Validation badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 10px', borderRadius: 7, marginBottom: 14, background: p.validation ? 'rgba(16,185,129,0.1)' : (darkMode ? 'rgba(100,116,139,0.1)' : 'rgba(100,116,139,0.06)'), border: `1px solid ${p.validation ? 'rgba(16,185,129,0.25)' : 'rgba(100,116,139,0.2)'}` }}>
                    <ShieldCheck size={12} style={{ color: p.validation ? '#10b981' : '#94a3b8', flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: p.validation ? '#10b981' : '#94a3b8' }}>
                      {p.validation ? 'Contact Validation included' : 'No contact validation'}
                    </span>
                  </div>

                  <ul style={{ margin: '0 0 16px', padding: '0 0 0 16px', fontSize: 12, color: darkMode ? '#cbd5e1' : '#374151', lineHeight: 1.8 }}>
                    {p.features.map((f, i) => <li key={i}>{f}</li>)}
                  </ul>

                  {isCurrent
                    ? <button disabled style={{ width: '100%', padding: '8px 0', borderRadius: 8, border: '1px solid #334155', background: 'transparent', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'default' }}>Current Plan</button>
                    : p.annualPrice === null
                    ? <button style={{ width: '100%', padding: '8px 0', borderRadius: 8, border: '1px solid #f59e0b', background: 'transparent', color: '#f59e0b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Contact Sales</button>
                    : <button onClick={() => setShowUpgradeModal(p)} style={{ width: '100%', padding: '8px 0', borderRadius: 8, border: 'none', background: p.popular ? '#10b981' : '#1E88E5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Upgrade</button>
                  }
                </Card>
              );
            })}
          </div>

          {/* Feature comparison table */}
          <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${darkMode ? '#1e293b' : '#f1f5f9'}` }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: darkMode ? '#f8fafc' : '#111827' }}>Full Feature Comparison</div>
            </div>
            {(() => {
              const rows = [
                { label: 'Enrichment Credits / Year', values: ['50', '2,000', '5,000', '10,000', 'Unlimited'] },
                { label: 'Team Members', values: ['1', '2', '5', '10', 'Unlimited'] },
                { label: 'ATS / CRM Integration', values: [false, true, true, true, true] },
                { label: 'kNine Network Access', values: [true, true, true, true, true] },
                { label: 'Searches (Roxi + Cooper)', values: ['Free', 'Free', 'Free', 'Free', 'Free'] },
                { label: 'kNine Contact Validation', values: [false, false, true, true, true], section: true },
                { label: 'Email Deliverability (ZeroBounce)', values: [false, false, true, true, true], highlight: true, indent: true },
                { label: '  ↳ Valid / Invalid / Catch-all / Risky', values: [false, false, true, true, true], highlight: true, indent: true, sub: true },
                { label: '  ↳ Disposable & spam-trap detection', values: [false, false, true, true, true], highlight: true, indent: true, sub: true },
                { label: '  ↳ MX record & SMTP provider check', values: [false, false, true, true, true], highlight: true, indent: true, sub: true },
                { label: 'Phone Line-Type (Twilio Lookup)', values: [false, false, true, true, true], highlight: true, indent: true },
                { label: '  ↳ Mobile / Landline / VoIP / Unknown', values: [false, false, true, true, true], highlight: true, indent: true, sub: true },
                { label: '  ↳ Carrier name identification', values: [false, false, true, true, true], highlight: true, indent: true, sub: true },
                { label: 'Verified email pushed to ATS as primary', values: [false, false, true, true, true], highlight: true, indent: true },
                { label: 'Verified phone pushed to ATS as primary', values: [false, false, true, true, true], highlight: true, indent: true },
                { label: 'Personal-email-first priority (Roxi)', values: [false, false, true, true, true], highlight: true, indent: true },
                { label: 'Work-email-first priority (Cooper)', values: [false, false, true, true, true], highlight: true, indent: true },
                { label: 'Validation badge in search drawer', values: [false, false, true, true, true], highlight: true, indent: true },
                { label: 'Priority Support', values: [false, false, true, true, true] },
                { label: 'Dedicated CSM', values: [false, false, false, false, true] },
                { label: 'Custom Integrations', values: [false, false, false, false, true] },
              ];
              return rows.map((row, ri) => (
              <div key={ri} style={{ display: 'grid', gridTemplateColumns: '2fr repeat(5, 1fr)', borderBottom: ri < rows.length - 1 ? `1px solid ${darkMode ? '#0f172a' : '#f8fafc'}` : 'none', background: row.section ? (darkMode ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.06)') : row.highlight ? (darkMode ? 'rgba(16,185,129,0.03)' : 'rgba(16,185,129,0.02)') : 'transparent' }}>
                <div style={{ padding: row.sub ? '6px 20px 6px 36px' : row.indent ? '8px 20px 8px 28px' : '11px 20px', fontSize: row.sub ? 11 : 13, color: row.sub ? (darkMode ? '#64748b' : '#94a3b8') : (darkMode ? '#cbd5e1' : '#374151'), display: 'flex', alignItems: 'center', gap: 6, fontWeight: row.section ? 700 : row.highlight && !row.sub ? 500 : 400 }}>
                  {row.section && <ShieldCheck size={12} style={{ color: '#10b981', flexShrink: 0 }} />}
                  {!row.section && row.highlight && !row.sub && <div style={{ width: 3, height: 3, borderRadius: '50%', background: '#10b981', flexShrink: 0 }} />}
                  {row.label}
                </div>
                {row.values.map((val, ci) => (
                  <div key={ci} style={{ padding: '11px 8px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: val === true ? '#10b981' : val === false ? '#94a3b8' : (darkMode ? '#e2e8f0' : '#374151'), background: plan === SUBSCRIPTION_PLANS[ci]?.id ? (darkMode ? 'rgba(59,130,246,0.06)' : 'rgba(59,130,246,0.03)') : 'transparent' }}>
                    {val === true ? <Check size={14} style={{ color: '#10b981', margin: '0 auto' }} /> : val === false ? <span style={{ color: '#334155' }}>—</span> : val}
                  </div>
                ))}
              </div>
            ));
          })()}
            {/* Column headers */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr repeat(5, 1fr)', background: darkMode ? '#0f172a' : '#f8fafc', borderTop: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}` }}>
              <div style={{ padding: '10px 20px', fontSize: 11, color: '#64748b', fontWeight: 600 }}>FEATURE</div>
              {SUBSCRIPTION_PLANS.map(p => (
                <div key={p.id} style={{ padding: '10px 8px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: plan === p.id ? '#3b82f6' : '#64748b' }}>{p.name.replace(' Trial', '')}</div>
              ))}
            </div>
          </Card>

          {/* Upgrade recommendation */}
          {currentPlanIndex < SUBSCRIPTION_PLANS.length - 1 && (
            <Card style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Ready to upgrade?</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    {!currentPlan.validation
                      ? 'Unlock automatic email + phone validation on every enrichment — starting with Professional.'
                      : 'Increase your credit limit or add more team members.'}
                  </div>
                </div>
                <Button variant="success" icon={Crown} onClick={() => setShowUpgradeModal(SUBSCRIPTION_PLANS[Math.min(currentPlanIndex + 1, SUBSCRIPTION_PLANS.length - 1)])}>
                  {!currentPlan.validation ? 'Upgrade to Professional' : 'View Next Plan'}
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'validation' && (
        <div>
          {/* Hero banner */}
          <div style={{ borderRadius: 16, overflow: 'hidden', border: '1px solid rgba(16,185,129,0.3)', background: darkMode ? 'linear-gradient(135deg, rgba(16,185,129,0.09) 0%, rgba(99,102,241,0.09) 100%)' : 'linear-gradient(135deg, rgba(16,185,129,0.07) 0%, rgba(99,102,241,0.06) 100%)', marginBottom: 24 }}>
            <div style={{ padding: '28px 28px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg, #10b981, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <ShieldCheck size={22} style={{ color: '#fff' }} />
                </div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: darkMode ? '#f8fafc' : '#111827', letterSpacing: '-0.4px' }}>kNine Contact Validation</div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>Powered by ZeroBounce + Twilio — every enrichment, automatically</div>
                </div>
                {!currentPlan.validation && (
                  <Button variant="primary" size="sm" icon={Crown} onClick={() => setActiveTab('plans')} style={{ marginLeft: 'auto' }}>Upgrade to unlock</Button>
                )}
                {currentPlan.validation && (
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 100, background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)' }}>
                    <Check size={12} style={{ color: '#10b981' }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#10b981' }}>Active on your plan</span>
                  </div>
                )}
              </div>
              <p style={{ margin: '0 0 0 54px', fontSize: 14, color: '#64748b', lineHeight: 1.6, maxWidth: 680 }}>
                Most enrichment tools hand you a list of emails and phone numbers and wish you luck. kNine verifies them on the spot — before you reach out — and pushes only the best address into your ATS as the primary contact. No extra tools, no extra steps.
              </p>
            </div>
          </div>

          {/* Email validation explainer */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#64748b', marginBottom: 12 }}>Email Verification — ZeroBounce</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {[
                { badge: 'Valid', color: '#10b981', bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.25)', icon: '✓', desc: 'Deliverable inbox — SMTP confirmed. This address is pushed to ATS as primary.' },
                { badge: 'Catch-all', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', icon: '~', desc: 'Domain accepts all mail — inbox existence cannot be confirmed. Flagged as risky.' },
                { badge: 'Risky', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', icon: '!', desc: 'Abuse or spam-trap history detected. Shown in drawer but not used as ATS primary.' },
                { badge: 'Invalid', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.25)', icon: '✕', desc: 'SMTP rejected — mailbox does not exist. Surfaced in drawer so you can skip or investigate.' },
                { badge: 'Disposable', color: '#ef4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.25)', icon: '✕', desc: 'Temporary inbox service (Mailinator, Guerrilla Mail, etc.). Automatically excluded from ATS push.' },
                { badge: 'Unknown', color: '#64748b', bg: darkMode ? 'rgba(100,116,139,0.1)' : 'rgba(100,116,139,0.06)', border: 'rgba(100,116,139,0.2)', icon: '?', desc: 'Server timed out or was unreachable. Shown as unchecked — validation skipped for this address.' },
              ].map(({ badge, color, bg, border, icon, desc }) => (
                <Card key={badge} style={{ padding: 16, border: `1px solid ${border}`, background: bg }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ width: 24, height: 24, borderRadius: 6, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff', flexShrink: 0 }}>{icon}</div>
                    <span style={{ fontSize: 13, fontWeight: 700, color }}>{badge}</span>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b', lineHeight: 1.5 }}>{desc}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* MX / SMTP details */}
          <Card style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
              {[
                { label: 'MX Record Check', desc: 'Confirms the domain has valid mail-exchange DNS records before SMTP probing.' },
                { label: 'SMTP Provider', desc: 'Identifies the sending platform (Google Workspace, Microsoft 365, etc.) for context.' },
                { label: 'Did You Mean?', desc: 'ZeroBounce suggests corrections for common typos — e.g. "gnail.com → gmail.com".' },
                { label: 'Free Email Flag', desc: 'Flags Gmail / Yahoo / Hotmail so you can distinguish personal vs. work inboxes.' },
              ].map(({ label, desc }) => (
                <div key={label}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: darkMode ? '#e2e8f0' : '#111827', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Check size={11} style={{ color: '#10b981' }} />{label}
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{desc}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Phone validation explainer */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#64748b', marginBottom: 12 }}>Phone Verification — Twilio Lookup v2</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {[
                { badge: 'Mobile', color: '#10b981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)', desc: 'SIM-based cell number. Best for SMS outreach. Shown with carrier name (Verizon, AT&T, etc.).' },
                { badge: 'Landline', color: '#6366f1', bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.2)', desc: 'Traditional fixed-line number. Call only — not reachable by text.' },
                { badge: 'VoIP', color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)', desc: 'Internet-based number (Google Voice, Twilio, etc.). May be a forwarded or virtual line.' },
                { badge: 'Unknown', color: '#64748b', bg: darkMode ? 'rgba(100,116,139,0.1)' : 'rgba(100,116,139,0.06)', border: 'rgba(100,116,139,0.2)', desc: 'Line type could not be determined. Number may be valid but carrier data is unavailable.' },
              ].map(({ badge, color, bg, border, desc }) => (
                <Card key={badge} style={{ padding: 16, border: `1px solid ${border}`, background: bg }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 6 }}>{badge}</div>
                  <p style={{ margin: 0, fontSize: 12, color: darkMode ? '#94a3b8' : '#64748b', lineHeight: 1.5 }}>{desc}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Priority ordering */}
          <Card style={{ padding: 24, marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Smart Priority Ordering</div>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: '#64748b' }}>kNine validates addresses in priority order and stops as soon as one valid email and one valid phone are found — saving API calls and ensuring the right contact lands in your ATS.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {[
                { label: 'Roxi (Candidate Search)', color: '#10b981', order: ['Personal emails first', 'Generic email field', 'Work email last resort'], phoneOrder: ['Mobile phone', 'Generic phone', 'Work phone', 'Additional numbers'] },
                { label: 'Cooper (BD Search)', color: '#6366f1', order: ['Work email first', 'Generic email field', 'Personal emails last'], phoneOrder: ['Work phone', 'Generic phone', 'Mobile phone', 'Additional numbers'] },
              ].map(({ label, color, order, phoneOrder }) => (
                <div key={label} style={{ padding: 16, borderRadius: 10, border: `1px solid ${color}30`, background: `${color}08` }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 12 }}>{label}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>Email Priority</div>
                  {order.map((o, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{i + 1}</div>
                      <span style={{ fontSize: 12, color: darkMode ? '#cbd5e1' : '#374151' }}>{o}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#64748b', marginBottom: 6, marginTop: 12 }}>Phone Priority</div>
                  {phoneOrder.map((o, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ width: 18, height: 18, borderRadius: '50%', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>{i + 1}</div>
                      <span style={{ fontSize: 12, color: darkMode ? '#cbd5e1' : '#374151' }}>{o}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </Card>

          {/* Powered by */}
          <Card style={{ padding: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>Powered by</div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderRadius: 10, border: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#f8fafc' }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: '#0D47A1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#fff' }}>Z</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>ZeroBounce</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Email deliverability & validation API</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderRadius: 10, border: `1px solid ${darkMode ? '#1e293b' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#f8fafc' }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: '#F22F46', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#fff' }}>T</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>Twilio Lookup v2</div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>Phone number intelligence & line-type</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'credits' && (
        <div>
          {/* Interactive Credit Slider */}
          <Card style={{ padding: 24, marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700 }}>Purchase Additional Credits</h3>
            <p style={{ margin: '0 0 20px', fontSize: 14, color: '#64748b' }}>
              Based on your <strong>{currentPlan.name}</strong> plan rate of ${pricePerCredit.toFixed(2)}/credit
            </p>

            {/* Credit Slider */}
            <div style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 14, color: '#64748b' }}>Select amount</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{creditBlocks} block{creditBlocks > 1 ? 's' : ''} ({totalCredits.toLocaleString()} credits)</span>
              </div>

              <input
                type="range"
                min="1"
                max={maxBlocks}
                value={creditBlocks}
                onChange={(e) => setCreditBlocks(parseInt(e.target.value))}
                style={{ width: '100%', height: 12, borderRadius: 6, appearance: 'none', background: `linear-gradient(to right, #10b981 ${((creditBlocks - 1) / (maxBlocks - 1)) * 100}%, ${darkMode ? '#334155' : '#e5e7eb'} 0%)`, cursor: 'pointer' }}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: '#64748b' }}>
                <span>500 credits</span>
                <span>5,000 credits</span>
                <span>10,000 credits</span>
              </div>
            </div>

            {/* Purchase Summary */}
            <div style={{ padding: 20, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 12, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 36, fontWeight: 800, color: '#10b981' }}>{totalCredits.toLocaleString()}</div>
                  <div style={{ fontSize: 14, color: '#64748b' }}>credits</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 36, fontWeight: 800 }}>${totalPrice.toLocaleString()}</div>
                  <div style={{ fontSize: 14, color: '#64748b' }}>${pricePerCredit.toFixed(2)}/credit</div>
                </div>
              </div>

              {/* Quick select buttons */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {[1, 2, 4, 10, 20].map(blocks => (
                  <button
                    key={blocks}
                    onClick={() => setCreditBlocks(blocks)}
                    style={{
                      flex: 1, padding: '8px 4px', borderRadius: 8,
                      border: creditBlocks === blocks ? '2px solid #10b981' : `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`,
                      background: creditBlocks === blocks ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                      color: creditBlocks === blocks ? '#10b981' : darkMode ? '#e2e8f0' : '#374151',
                      fontSize: 12, fontWeight: 500, cursor: 'pointer'
                    }}
                  >
                    {(blocks * BLOCK_SIZE).toLocaleString()}
                  </button>
                ))}
              </div>

              <Button
                variant="success"
                style={{ width: '100%' }}
                loading={purchasing}
                disabled={!paymentMethod}
                onClick={handlePurchaseCredits}
              >
                Purchase {totalCredits.toLocaleString()} Credits for ${totalPrice.toLocaleString()}
              </Button>

              {!paymentMethod && (
                <div style={{ marginTop: 12, textAlign: 'center' }}>
                  <button onClick={() => setShowPaymentModal(true)} style={{ background: 'none', border: 'none', color: '#1E88E5', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                    Add payment method first
                  </button>
                </div>
              )}
            </div>

            {/* Upgrade Recommendation */}
            {upgradeRec && (
              <div style={{ padding: 16, background: 'linear-gradient(135deg, rgba(30, 136, 229, 0.1), rgba(16, 185, 129, 0.1))', borderRadius: 12, border: '1px solid rgba(30, 136, 229, 0.3)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <Crown size={24} style={{ color: '#1E88E5', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: '#1E88E5', marginBottom: 4 }}>
                      Consider upgrading to {upgradeRec.planName}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                      Get {upgradeRec.additionalCredits.toLocaleString()} annual credits for ${upgradeRec.upgradeCost.toLocaleString()}/year
                      {upgradeRec.savings > 0 && <span style={{ color: '#10b981', fontWeight: 600 }}> — Save ${upgradeRec.savings.toFixed(0)}!</span>}
                    </div>
                    <Button variant="primary" size="sm" onClick={() => { setActiveTab('plans'); setSelectedPlanIndex(SUBSCRIPTION_PLANS.findIndex(p => p.id === upgradeRec.plan)); }}>
                      View {upgradeRec.planName} Plan
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Credit Usage Info */}
          <Card style={{ padding: 20 }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Credit Usage</h4>
            <div className="kubi-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, fontSize: 13 }}>
              <div style={{ padding: 12, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 8 }}>
                <div style={{ color: '#64748b' }}>Roxi Search</div>
                <div style={{ fontWeight: 600 }}>1 credit per search</div>
              </div>
              <div style={{ padding: 12, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 8 }}>
                <div style={{ color: '#64748b' }}>Enrich Contact</div>
                <div style={{ fontWeight: 600 }}>1 credit per record</div>
              </div>
              <div style={{ padding: 12, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 8 }}>
                <div style={{ color: '#64748b' }}>Add to Tracker</div>
                <div style={{ fontWeight: 600 }}>1 credit/person/month</div>
              </div>
              <div style={{ padding: 12, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 8 }}>
                <div style={{ color: '#64748b' }}>Fee Recovery</div>
                <div style={{ fontWeight: 600 }}>1 credit/person/month</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'payment' && (
        <Card style={{ padding: 24 }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>Payment Method</h3>
          {paymentMethod ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 20, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 12 }}>
              <div style={{ width: 48, height: 32, background: 'linear-gradient(135deg, #1a1a2e, #16213e)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b', fontWeight: 700, fontSize: 12 }}>{paymentMethod.brand || 'VISA'}</div>
              <div style={{ flex: 1 }}><div style={{ fontWeight: 600 }}>•••• •••• •••• {paymentMethod.last4}</div><div style={{ fontSize: 13, color: '#64748b' }}>Expires {paymentMethod.exp}</div></div>
              <Button variant="secondary" size="sm" onClick={() => setShowPaymentModal(true)}>Update</Button>
              <Button variant="danger" size="sm" onClick={() => { setPaymentMethod(null); localStorage.removeItem('kubi_payment_method'); }}>Remove</Button>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}><CreditCard size={48} style={{ color: '#64748b', marginBottom: 16 }} /><p style={{ color: '#64748b', marginBottom: 16 }}>No payment method on file</p><Button variant="primary" icon={Plus} onClick={() => setShowPaymentModal(true)}>Add Payment Method</Button></div>
          )}
          <div style={{ marginTop: 20, padding: 16, background: 'rgba(10, 102, 194, 0.05)', borderRadius: 10 }}><h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>Test Cards (Demo)</h4><div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{STRIPE_TEST_CARDS.map((card, i) => (<div key={i} style={{ padding: '8px 12px', background: darkMode ? '#1e293b' : '#fff', borderRadius: 8, fontSize: 12 }}><div style={{ fontWeight: 600 }}>{card.name}</div><div style={{ color: '#64748b', fontFamily: 'monospace' }}>{card.number}</div></div>))}</div></div>
        </Card>
      )}

      {showUpgradeModal && (
        <ModalOverlay onClose={() => setShowUpgradeModal(null)}>
          <Card className="kubi-modal-card" style={{ width: 480, padding: 24 }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600 }}>Upgrade to {showUpgradeModal.name}</h3>

            {/* Comparison */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 16, marginBottom: 20 }}>
              <div style={{ padding: 16, background: darkMode ? '#0f172a' : '#f1f5f9', borderRadius: 10, textAlign: 'center' }}>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Current</div>
                <div style={{ fontWeight: 700 }}>{currentPlan.name}</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>{typeof currentPlan.credits === 'number' ? `${currentPlan.credits.toLocaleString()} credits` : currentPlan.credits}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', color: '#10b981' }}>→</div>
              <div style={{ padding: 16, background: 'rgba(16, 185, 129, 0.1)', borderRadius: 10, textAlign: 'center', border: '2px solid #10b981' }}>
                <div style={{ fontSize: 12, color: '#10b981', marginBottom: 4 }}>New</div>
                <div style={{ fontWeight: 700, color: '#10b981' }}>{showUpgradeModal.name}</div>
                <div style={{ fontSize: 13, color: '#10b981' }}>{typeof showUpgradeModal.credits === 'number' ? `${showUpgradeModal.credits.toLocaleString()} credits` : showUpgradeModal.credits}</div>
              </div>
            </div>

            <div style={{ padding: 16, background: darkMode ? '#0f172a' : '#f8fafc', borderRadius: 12, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Plan Price</span>
                <span style={{ fontWeight: 600 }}>${showUpgradeModal.annualPrice?.toLocaleString()}/year</span>
              </div>
              {currentPlan.annualPrice > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, color: '#64748b', fontSize: 13 }}>
                  <span>Current Plan Credit</span>
                  <span>-${currentPlan.annualPrice.toLocaleString()}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}` }}>
                <span style={{ fontWeight: 600 }}>Amount Due Today</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: '#10b981' }}>
                  ${((showUpgradeModal.annualPrice || 0) - (currentPlan.annualPrice || 0)).toLocaleString()}
                </span>
              </div>
            </div>

            {!paymentMethod && (
              <div style={{ padding: 12, background: 'rgba(245, 158, 11, 0.1)', borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={16} style={{ color: '#f59e0b' }} />
                <span style={{ fontSize: 13, color: '#f59e0b' }}>Add a payment method first</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <Button variant="secondary" style={{ flex: 1 }} onClick={() => setShowUpgradeModal(null)}>Cancel</Button>
              <Button variant="success" style={{ flex: 1 }} disabled={!paymentMethod} onClick={() => handleUpgrade(showUpgradeModal)}>Upgrade Now</Button>
            </div>
          </Card>
        </ModalOverlay>
      )}

      {showPaymentModal && <PaymentModal darkMode={darkMode} onClose={() => setShowPaymentModal(false)} onSave={handleSavePayment} />}
    </div>
  );
};

const PaymentModal = ({ darkMode, onClose, onSave }) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 1000));
    const last4 = cardNumber.replace(/\s/g, '').slice(-4);
    onSave({ last4, exp: expiry, brand: cardNumber.startsWith('5') ? 'MC' : 'VISA' });
    setSaving(false);
  };

  const formatCard = (v) => v.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim().slice(0, 19);
  const formatExp = (v) => v.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2').slice(0, 5);

  return (
    <ModalOverlay onClose={onClose}>
      <Card className="kubi-modal-card" style={{ width: 440, padding: 24 }}>
        <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 600 }}>Add Payment Method</h3>
        <div style={{ marginBottom: 16 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Card Number</label><input value={cardNumber} onChange={(e) => setCardNumber(formatCard(e.target.value))} placeholder="4242 4242 4242 4242" style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, boxSizing: 'border-box' }} /></div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          <div style={{ flex: 1 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Expiry</label><input value={expiry} onChange={(e) => setExpiry(formatExp(e.target.value))} placeholder="MM/YY" style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, boxSizing: 'border-box' }} /></div>
          <div style={{ flex: 1 }}><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>CVC</label><input value={cvc} onChange={(e) => setCvc(e.target.value.slice(0, 4))} placeholder="123" style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: darkMode ? '#0f172a' : '#fff', color: darkMode ? '#f8fafc' : '#1f2937', fontSize: 14, boxSizing: 'border-box' }} /></div>
        </div>
        <div style={{ padding: 12, background: 'rgba(10, 102, 194, 0.05)', borderRadius: 8, marginBottom: 20, display: 'flex', gap: 8, flexWrap: 'wrap' }}>{STRIPE_TEST_CARDS.slice(0, 2).map((card, i) => (<button key={i} onClick={() => { setCardNumber(card.number); setExpiry(card.exp); setCvc(card.cvc); }} style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${darkMode ? '#334155' : '#e5e7eb'}`, background: 'transparent', fontSize: 12, cursor: 'pointer' }}>{card.name}</button>))}</div>
        <div style={{ display: 'flex', gap: 12 }}><Button variant="secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</Button><Button variant="primary" style={{ flex: 1 }} loading={saving} onClick={handleSave}>Save Card</Button></div>
      </Card>
    </ModalOverlay>
  );
};

// Placeholder Pages
const ListsPage = () => <div><h1 style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 800 }}>Lists</h1><p style={{ margin: '0 0 24px', fontSize: 14, color: '#64748b' }}>Organize candidates</p><EmptyState icon={FolderOpen} title="No lists yet" description="Create your first list" action={<Button variant="primary" icon={Plus}>Create List</Button>} /></div>;

// Global Animation Styles
const GLOBAL_ANIMATION_STYLES = `
@keyframes kubi-fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes kubi-slideInLeft {
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes kubi-slideInRight {
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes kubi-scaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes kubi-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}
@keyframes kubi-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes kubi-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
@keyframes kubi-glow {
  0%, 100% { box-shadow: 0 0 5px rgba(30, 136, 229, 0.35); }
  50% { box-shadow: 0 0 22px rgba(30, 136, 229, 0.65); }
}
@keyframes kubi-toast-in {
  from { opacity: 0; transform: translateX(20px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes kubi-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes kubi-ripple {
  0% { transform: scale(0); opacity: 0.5; }
  100% { transform: scale(4); opacity: 0; }
}
.kubi-fade-in { animation: kubi-fadeIn 0.4s ease-out forwards; }
.kubi-slide-left { animation: kubi-slideInLeft 0.3s ease-out forwards; }
.kubi-slide-right { animation: kubi-slideInRight 0.3s ease-out forwards; }
.kubi-scale-in { animation: kubi-scaleIn 0.3s ease-out forwards; }
.kubi-pulse { animation: kubi-pulse 2s ease-in-out infinite; }
.kubi-spin { animation: kubi-spin 1s linear infinite; }
.kubi-bounce { animation: kubi-bounce 1s ease-in-out infinite; }
.kubi-glow { animation: kubi-glow 2s ease-in-out infinite; }
.kubi-hover-lift { transition: transform 0.2s ease, box-shadow 0.2s ease; }
.kubi-hover-lift:hover { transform: translateY(-3px); box-shadow: 0 10px 40px rgba(0,0,0,0.12); }
.kubi-hover-scale { transition: transform 0.2s ease; }
.kubi-hover-scale:hover { transform: scale(1.02); }
.kubi-transition-all { transition: all 0.2s ease; }
.kubi-skeleton {
  background: linear-gradient(90deg, rgba(100,100,100,0.1) 25%, rgba(100,100,100,0.2) 50%, rgba(100,100,100,0.1) 75%);
  background-size: 200% 100%;
  animation: kubi-shimmer 1.5s linear infinite;
}
/* Smooth scrollbar styling */
.kubi-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
.kubi-scrollbar::-webkit-scrollbar-track { background: transparent; }
.kubi-scrollbar::-webkit-scrollbar-thumb { background: rgba(100,100,100,0.3); border-radius: 4px; }
.kubi-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(100,100,100,0.5); }
/* Page transitions */
main { animation: kubi-fadeIn 0.3s ease-out; }
/* Button click effect */
button:active:not(:disabled) { transform: scale(0.98); }
/* Focus states */
input:focus, select:focus, textarea:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(30, 136, 229, 0.22);
}
/* Mobile responsive helpers */
@media (max-width: 1024px) {
  .responsive-grid-4 { grid-template-columns: repeat(2, 1fr) !important; }
  .responsive-grid-3 { grid-template-columns: repeat(2, 1fr) !important; }
}
@media (max-width: 640px) {
  .responsive-grid-4,
  .responsive-grid-3,
  .responsive-grid-2 { grid-template-columns: 1fr !important; }
  .hide-mobile { display: none !important; }
  .mobile-stack { flex-direction: column !important; }
}
/* Card hover states */
[class*="card"]:hover, .card:hover {
  transition: box-shadow 0.2s ease;
}
`;

// =============================================================================
// IMPERSONATION BANNER
// =============================================================================

const ImpersonationBanner = ({ onEnd }) => {
  const [meta] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('knine_impersonation')); } catch { return null; }
  });
  const [ending, setEnding] = useState(false);

  if (!meta) return null;

  const handleEnd = async () => {
    setEnding(true);
    try {
      if (meta.impersonationId) {
        await fetch(`${API_URL}/admin/impersonate/${meta.impersonationId}/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` }
        });
      }
    } catch (_) {}
    sessionStorage.removeItem('knine_impersonation');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onEnd) onEnd();
    window.close(); // Close the impersonation tab
  };

  return (
    <div style={{
      background: '#7c3aed',
      borderBottom: '2px solid #6d28d9',
      padding: '8px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 16,
      flexShrink: 0,
      zIndex: 100
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 16 }}>🔐</span>
        <span style={{ color: '#ede9fe', fontSize: 13, fontWeight: 600 }}>
          Admin Session — You are viewing as <strong style={{ color: '#fff' }}>{meta.email}</strong>. All actions are logged.
        </span>
      </div>
      <button
        onClick={handleEnd}
        disabled={ending}
        style={{
          flexShrink: 0,
          padding: '5px 14px',
          borderRadius: 6,
          background: 'rgba(255,255,255,0.2)',
          border: '1px solid rgba(255,255,255,0.4)',
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          cursor: ending ? 'default' : 'pointer',
          whiteSpace: 'nowrap'
        }}
      >
        {ending ? 'Ending...' : 'End Session'}
      </button>
    </div>
  );
};

// ==================== MAIN LAYOUT & APP ====================
const MainLayout = ({ children }) => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('kubi_darkMode') !== 'false');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const isImpersonating = !!sessionStorage.getItem('knine_impersonation');

  // Auto-close mobile sidebar when viewport grows to desktop
  useEffect(() => { if (!isMobile) setMobileSidebarOpen(false); }, [isMobile]);
  const [credits, setCredits] = useState(() => parseInt(localStorage.getItem('kubi_credits') || '150'));
  const [plan, setPlan] = useState(() => localStorage.getItem('kubi_plan') || 'free');
  const [user] = useState(getUser);
  const [searchFromPosition, setSearchFromPosition] = useState(null);
  const [showKubiChat, setShowKubiChat] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // Inject global animation styles
  useEffect(() => {
    if (!document.getElementById('kubi-global-styles')) {
      const styleEl = document.createElement('style');
      styleEl.id = 'kubi-global-styles';
      styleEl.textContent = GLOBAL_ANIMATION_STYLES;
      document.head.appendChild(styleEl);
    }
  }, []);

  useEffect(() => { localStorage.setItem('kubi_darkMode', darkMode); }, [darkMode]);
  useEffect(() => { localStorage.setItem('kubi_credits', credits); }, [credits]);
  useEffect(() => { localStorage.setItem('kubi_plan', plan); }, [plan]);

  // Fetch credits from server on app load to sync with actual balance
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const data = await apiCall('/api/billing/credits');
        if (data && typeof data.creditsRemaining === 'number') {
          setCredits(data.creditsRemaining);
          if (data.subscriptionTier) {
            setPlan(data.subscriptionTier);
          }
        }
      } catch (err) {
        console.error('Failed to fetch credits:', err);
      }
    };
    fetchCredits();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch platform features (controls which nav items / features are visible)
  useEffect(() => {
    const fetchPlatformFeatures = async () => {
      try {
        const data = await apiCall('/api/platform/features');
        if (data) {
          // Store full features + platforms for gating
          localStorage.setItem('kubi_platform_features', JSON.stringify(data.features || {}));
          localStorage.setItem('kubi_platform_access', JSON.stringify(data.platforms || {}));
          // Update AI access for Sidebar (cooper visibility)
          const cooperAccessible = (data.platforms?.cooper?.accessible !== false) || (data.platforms?.spirko?.accessible !== false);
          const roxiAccessible = (data.platforms?.roxi?.accessible !== false) || (data.platforms?.kubi?.accessible !== false);
          localStorage.setItem('kubi_ai_access', JSON.stringify({ roxi: roxiAccessible, cooper: cooperAccessible }));
        }
      } catch (err) {
        console.error('Failed to fetch platform features:', err);
      }
    };
    fetchPlatformFeatures();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch billing warning for past-due HubSpot accounts (admin users only)
  const [billingWarning, setBillingWarning] = useState(null);
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const fetchBillingStatus = async () => {
      try {
        const data = await apiCall('/api/auth/subscription');
        if (data?.billingWarning) setBillingWarning(data.billingWarning);
      } catch (_) {}
    };
    fetchBillingStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch integration status so banners can hide when an ATS/CRM is connected
  useEffect(() => {
    const fetchIntegrationStatus = async () => {
      try {
        const data = await apiCall('/api/integrations/status');
        if (data) {
          localStorage.setItem('kubi_has_integration', data.hasActiveIntegration ? 'true' : 'false');
          localStorage.setItem('kubi_active_systems', JSON.stringify(data.activeSystems || []));
        }
      } catch (err) {
        console.error('Failed to fetch integration status:', err);
      }
    };
    fetchIntegrationStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-user tour key — each account gets its own first-login tour regardless of shared browser
  const tourKey = user?.id ? `knine_tour_seen_${user.id}` : 'knine_tour_seen';

  // Show onboarding tour on first login for trial/free accounts and new org members
  useEffect(() => {
    const alreadySeen = localStorage.getItem(tourKey);
    if (alreadySeen) return;
    // plan defaults to 'free' before the server responds — always show for new/trial accounts
    const currentPlan = localStorage.getItem('kubi_plan') || plan || 'free';
    const isTrialOrFree = !currentPlan || currentPlan === 'free' || currentPlan === 'trial' || currentPlan === 'trialing';
    const createdAt = user?.createdAt;
    const isNewOrgMember = createdAt && (Date.now() - new Date(createdAt).getTime()) < 48 * 60 * 60 * 1000;
    if (isTrialOrFree || isNewOrgMember) {
      const t = setTimeout(() => setShowTour(true), 900);
      return () => clearTimeout(t);
    }
  }, [tourKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Instant re-trigger via CustomEvent dispatched by kNine AI chat (no polling lag)
  useEffect(() => {
    const handler = () => setShowTour(true);
    window.addEventListener('knine-show-tour', handler);
    return () => window.removeEventListener('knine-show-tour', handler);
  }, []);

  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); };

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <BillingContext.Provider value={{ credits, setCredits, plan, setPlan }}>
        <SearchContext.Provider value={{ searchFromPosition, setSearchFromPosition }}>
          <ToastProvider>
          <div style={{ display: 'flex', minHeight: '100vh', background: darkMode ? '#0f172a' : '#f4f6f8', color: darkMode ? '#f8fafc' : '#18181b' }}>
            <Sidebar
              collapsed={sidebarCollapsed}
              setCollapsed={setSidebarCollapsed}
              mobileOpen={mobileSidebarOpen}
              onMobileClose={() => setMobileSidebarOpen(false)}
            />
            <div style={{ flex: 1, marginLeft: isMobile ? 0 : (sidebarCollapsed ? 72 : 260), transition: 'margin-left 0.2s', display: 'flex', flexDirection: 'column', minHeight: '100vh', minWidth: 0 }}>
              <Header user={user} onLogout={handleLogout} onMenuToggle={() => setMobileSidebarOpen(o => !o)} />
              {isImpersonating && <ImpersonationBanner onEnd={() => navigate('/login')} />}
              {billingWarning && (
                <div style={{
                  background: billingWarning.businessDaysRemaining === 0 ? '#7f1d1d' : '#78350f',
                  borderBottom: `1px solid ${billingWarning.businessDaysRemaining === 0 ? '#dc2626' : '#d97706'}`,
                  padding: '10px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  flexShrink: 0,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>{billingWarning.businessDaysRemaining === 0 ? '🚨' : '⚠️'}</span>
                    <span style={{ color: '#fef3c7', fontSize: 14, fontWeight: 500 }}>
                      {billingWarning.message}
                    </span>
                  </div>
                  <a
                    href="mailto:support@knine.ai"
                    style={{ flexShrink: 0, padding: '5px 14px', borderRadius: 6, background: 'rgba(255,255,255,0.15)', color: '#fef3c7', fontSize: 13, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
                  >
                    Contact Support
                  </a>
                </div>
              )}
              <main style={{ padding: isMobile ? '16px 12px' : 28, flex: 1, background: darkMode ? '#0f172a' : '#f4f6f8' }}>{children}</main>
            </div>
            <KubiChatButton onClick={() => setShowKubiChat(true)} isDark={darkMode} />
            <KubiChatbot isOpen={showKubiChat} onClose={() => setShowKubiChat(false)} isDark={darkMode} />
            {showTour && <OnboardingTour onDismiss={() => setShowTour(false)} />}
          </div>
          </ToastProvider>
        </SearchContext.Provider>
      </BillingContext.Provider>
    </ThemeContext.Provider>
  );
};

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/knine-admin" element={<AdminDashboard />} />
      <Route path="/roxi" element={<ProtectedRoute><MainLayout><EnhancedSearch /></MainLayout></ProtectedRoute>} />
      <Route path="/my-contacts" element={<ProtectedRoute><MainLayout><MyEnrichedContacts /></MainLayout></ProtectedRoute>} />
      <Route path="/dashboard" element={<Navigate to="/roxi" replace />} />
      <Route path="/cooper" element={<ProtectedRoute><MainLayout><CooperSearch /></MainLayout></ProtectedRoute>} />
      <Route path="/spirko" element={<ProtectedRoute><MainLayout><CooperSearch /></MainLayout></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><MainLayout><AnalyticsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/yard" element={<ProtectedRoute><MainLayout><YardPage /></MainLayout></ProtectedRoute>} />
      <Route path="/tracker" element={<ProtectedRoute><MainLayout><TrackerPage /></MainLayout></ProtectedRoute>} />
      <Route path="/fee-recovery" element={<ProtectedRoute><MainLayout><FeeRecoveryPage /></MainLayout></ProtectedRoute>} />
      <Route path="/lists" element={<ProtectedRoute><MainLayout><ListsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><MainLayout><AIAssistantDashboard /></MainLayout></ProtectedRoute>} />
      <Route path="/ai-assistant/settings" element={<ProtectedRoute><MainLayout><AISettingsPanel /></MainLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><MainLayout><SettingsGeneralPage /></MainLayout></ProtectedRoute>} />
      <Route path="/settings/team" element={<ProtectedRoute><MainLayout><SettingsTeamPage /></MainLayout></ProtectedRoute>} />
      <Route path="/settings/integrations" element={<ProtectedRoute><MainLayout><SettingsIntegrationsPage /></MainLayout></ProtectedRoute>} />
      <Route path="/settings/usage" element={<ProtectedRoute><MainLayout><SettingsUsagePage /></MainLayout></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/roxi" replace />} />
      <Route path="*" element={<Navigate to="/roxi" replace />} />
    </Routes>
  );
}

export default App;
export { useBilling };
